import axios, { type AxiosInstance } from 'axios';
import type {
  CashierDiscountMode,
  ArchiveRequestStatus,
  CashoutLimits,
  CashoutSchedule,
  EarningsBreakdownRow,
  EcomPaymentMethodsConfig,
  PosCustomFeesConfig,
  PosPaymentMethodsConfig,
  KycRequirementDef,
  RegistrationField,
  MemberEditableFields,
  MerchantEditableFields,
  BinaryGenealogyNodeFields,
  OrderDocType,
  OrderDocumentPayload,
  PayoutDeductionsConfig,
  PayoutExportConfig,
  ReceiptData,
  ReceiptFieldFlags,
  ReceiptHeader,
  RpLayout,
  ShippingConfig,
  StorefrontConfig,
  TaxCode,
  ThemeTokens,
  WalletTopupBracket,
} from '@mlm/shared';
import { orderToReceipt, REPLICATED_IMAGE_PATH } from '@mlm/shared';
import { useAuthStore, type AuthUser } from './auth-store';
import { queryClient } from './query-client';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Axios client with:
 *  - Authorization header from the in-memory token (Zustand store).
 *  - `withCredentials: true` so the refresh cookie travels on every request.
 *  - One-shot refresh-and-retry on 401. The original request is replayed
 *    once with the new token. If refresh itself fails (no cookie, expired,
 *    revoked) we clear the session and let the caller redirect to /login.
 */
export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshInFlight: Promise<string | null> | null = null;

async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        // Reset after a tick so concurrent 401s share this attempt.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !String(original.url ?? '').includes('/auth/refresh') &&
      !String(original.url ?? '').includes('/auth/login') &&
      // See the member SPA: a wrong current password 401s, and replaying it
      // after a token refresh spends a second attempt from the hourly budget.
      !String(original.url ?? '').includes('/me/password')
    ) {
      original._retried = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(newToken, user);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }
    return Promise.reject(error);
  },
);

/**
 * Turn a thrown request error into a human message. Axios swallows the server
 * body behind a generic "Request failed with status code 4xx"; this digs out
 * the API's `{ error, message, issues }` so dialogs show the real reason — e.g.
 * a Zod `validation_error` becomes "rtuLevelShares.2: must be a decimal …".
 */
/**
 * The server's JSON error body, when there is one. Routes return a stable
 * `{ error: '<code>' }` plus any code-specific extras (e.g. `usage` on
 * `sku_in_use`), which callers branch on to render a tailored recovery UI.
 */
export function apiErrorPayload(
  e: unknown,
): ({ error?: string } & Record<string, unknown>) | undefined {
  if (!axios.isAxiosError(e)) return undefined;
  const data = e.response?.data;
  return data && typeof data === 'object' ? (data as { error?: string }) : undefined;
}

/**
 * Human copy for the stable `{ error: '<code>' }` slugs the API returns.
 *
 * This lives in the admin SPA, NOT in `@mlm/shared`, on purpose: the API owns
 * the codes, each SPA owns its own prose. The cashier's equivalent map is
 * till-specific ("Refresh the product list.") and would read wrong in an admin
 * dialog. Add a code here the moment an admin can trigger it.
 */
const ERROR_COPY: Record<string, string> = {
  sku_in_use: 'This product already has orders or stock history.',
  sku_taken: 'Another product already uses that SKU.',
  sku_required: 'Enter a SKU.',
  sku_too_long: 'That SKU is too long (64 characters max).',
  sku_reserved_suffix: '“-Archived” is reserved — the archive action adds it automatically.',
  sku_change_with_archive_toggle:
    'Change the SKU and archive in separate saves — doing both at once would discard the rename.',
  kit_membership_required: 'Pick a membership tier for the kit.',
  bundle_cannot_be_kit: 'A bundle can’t also be a membership kit.',
  redeem_cannot_be_kit: 'A redeem-only product can’t also be a membership kit.',
  validation_error: 'Some fields are invalid — check the highlighted values.',
  // Every rule `createProduct`/`updateProduct` can throw. `skuErrorReply` turns
  // an unmapped throw into `400 {error: <slug>}`, and the SLUG_RE guard below
  // then hides it behind the caller's fallback — so a missing entry here is not
  // a cosmetic gap, it is the difference between "Save failed" and a sentence
  // the operator can act on. A free (FS) kit aimed at a tier nobody had ticked
  // FS-eligible is what made that concrete.
  free_kit_membership_not_fs_eligible:
    'That tier isn’t marked FS-eligible, so a free (FS) kit can’t mint into it. Turn on “FS-eligible (free-slot tier)” for the tier under Memberships, or pick a tier that already has it.',
  signup_free_slot_membership_invalid:
    'Pick the membership of the free slot. It has to be marked FS-eligible under Memberships.',
  topup_bracket_program_invalid:
    'A top-up bracket names a gift check program that no longer exists or is not earned by a top-up.',
  cd_kit_activation_only: 'A CD / Half-CD kit can only be used for activation, not upgrades.',
  cd_kit_debt_amount_required: 'A CD / Half-CD kit needs a debt amount greater than 0.',
  gc_price_must_be_positive: 'The GC price must be greater than 0.',
  coin_price_must_be_positive: 'The Coin price must be greater than 0.',
  gc_price_required_when_purchasable: '“Sell in GC” is on — set a GC price.',
  coin_price_required_when_purchasable: '“Sell in Coin” is on — set a Coin price.',
  redeem_requires_gc_price: 'A redeem-only product needs a GC price.',
  bundle_cannot_be_redeem: 'A bundle can’t also be a redeem-only product.',
  bundle_must_be_house: 'Bundles are house products — a merchant can’t own one.',
  kit_cannot_have_variants: 'A membership kit can’t have variations.',
  bundle_cannot_have_variants: 'A bundle can’t have variations — it assembles from its components.',
  not_house_product: 'That action only applies to house products.',
  qty_must_be_non_negative_integer: 'Quantity must be a whole number, 0 or more.',
  // Materials and recipes. A material is stock that is used up, never sold, so
  // each of these names the thing it cannot also be.
  material_cannot_be_kit: 'A material can’t also be a membership kit.',
  material_cannot_be_bundle: 'A material can’t also be a bundle.',
  material_cannot_be_virtual:
    'A material can’t be a virtual product. Virtual means not stock-tracked, and a material exists to be counted.',
  material_cannot_be_redeem: 'A material can’t also be a redeem-only product.',
  material_must_be_house: 'Materials are house stock. A merchant can’t own one.',
  material_cannot_have_variants:
    'A material can’t have variations. Add each size as its own material instead.',
  material_cannot_have_recipe: 'A material can’t have a recipe of its own.',
  material_in_use:
    'This material is still named in a product’s recipe. Remove it from those recipes first.',
  material_cannot_be_self: 'A product can’t use itself as a material.',
  material_not_found: 'One of those materials no longer exists. Reload and pick again.',
  not_a_material:
    'One of those items isn’t a material. Only items from the Materials page can go in a recipe.',
  duplicate_material: 'The same material is listed twice for the same size. Combine the two rows.',
  variant_not_in_product: 'That size belongs to a different product. Reload and pick again.',
  // Course enrollments. `already_enrolled` is the one an operator hits by
  // accident: the grant route refuses to overwrite a LIVE enrollment rather
  // than silently rewrite a paid one, so the copy has to name the way out.
  already_enrolled:
    'That member already has access to this course. Revoke the existing enrollment first if you mean to replace it.',
  already_revoked: 'That enrollment is already revoked.',
  // Course topic nesting is exactly ONE level deep. The picker only offers
  // root topics, so these fire on a stale list (someone else re-parented a
  // topic while this page was open) — the copy has to name the way out.
  category_parent_not_root:
    'That topic is already a sub-topic, so nothing can sit under it. Topics nest one level only — pick a top-level topic as the parent.',
  category_has_children:
    'This topic has sub-topics of its own, so it can’t become one. Move its sub-topics somewhere else first.',
  category_parent_not_found: 'That parent topic no longer exists — reload and pick another.',
  category_parent_self: 'A topic can’t be its own parent.',
  // Phase 8 — content review, moderation and archival. Each of these is a
  // state the operator can act on, so none may fall through to "Request
  // failed": the slug is the only thing that says what to do next.
  lesson_video_not_ready:
    'That lesson’s video hasn’t finished processing yet — there is nothing to play. Try again once the upload is ready.',
  lesson_has_no_source: 'That lesson has no video, embed or article body to preview.',
  not_content_pending:
    'This course has no content review waiting — someone else already ruled on it. Reload the queue.',
  not_a_creator_course:
    'Content review only applies to third-party creator courses. A house course is authored and reviewed by the same tenant.',
  target_not_hideable:
    'A course can’t be hidden from here — unpublishing is a listing decision. Open it under Creators → Courses.',
  archive_blocked_open_payout:
    'This member has an open cash-out. Pay or reject it first — archiving would strand the money.',
  archive_request_not_open:
    'That archive request is no longer open — someone else already decided it. Reload the queue.',
  user_archived:
    'This account has been archived. Its details are erased and can no longer be edited.',
  // Coaching uploads (frequency tracks).
  unsupported_file_type: 'That file type isn’t accepted here.',
  missing_file: 'Choose a file to upload.',
  file_too_large: 'That file is too large.',
  file_infected: 'The virus scan flagged that file, so it was not saved.',
  storage_unavailable: 'File storage is unavailable right now. Try again in a minute.',
  // Distribution runs.
  run_window_not_closed:
    'This run’s dates haven’t ended yet. It can be distributed once its end date has passed.',
  older_run_not_distributed:
    'An older run for this plan hasn’t been distributed yet. Distribute or discard it first.',
  preview_changed:
    'The figures changed since they were reviewed. Check the new figures, then distribute again.',
  run_already_executed: 'This run already paid out, so it can’t be discarded. Reload the page.',
};

/** `some_error_code` — the shape of a server slug that escaped `ERROR_COPY`. */
const SLUG_RE = /^[a-z0-9]+(_[a-z0-9]+)+$/;

export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as
      | {
          error?: string;
          message?: string;
          issues?: Array<{ path?: (string | number)[]; message?: string }>;
        }
      | undefined;
    if (data?.issues?.length) {
      return data.issues
        .map((i) => `${(i.path ?? []).join('.') || 'value'}: ${i.message ?? 'invalid'}`)
        .join('; ');
    }
    if (data?.error && ERROR_COPY[data.error]) return ERROR_COPY[data.error]!;
    if (data?.message) return data.message;
    // Never render an unmapped slug. Printing `sku_in_use` in red under a form
    // is what this whole path is being fixed for, and the map will always lag
    // the API — so the FALLBACK is the guard, not anyone's diligence.
    if (data?.error) return SLUG_RE.test(data.error) ? fallback : data.error;
    if (e.message) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export async function login(args: {
  email: string;
  password: string;
  totp?: string;
}): Promise<{ accessToken: string; user: AuthUser }> {
  const res = await api.post<{ accessToken: string; user: AuthUser }>('/auth/login', args);
  return res.data;
}

/**
 * QoL Slice 5b — set / clear a POS manager PIN. The set route validates the
 * target holds `pos.discount.override` before stamping; both routes audit.
 * Plaintext PIN is sent ONCE and never returned.
 */
export async function setManagerPin(userId: string, pin: string): Promise<void> {
  await api.post(`/admin/users/${userId}/manager-pin`, { pin });
}
export async function clearManagerPin(userId: string): Promise<void> {
  await api.delete(`/admin/users/${userId}/manager-pin`);
}

/** SPA a grant can land in. A creator account is confined to `creator`, and
 *  only a creator account may go there — the server enforces both directions. */
export type ImpersonationApp = 'member' | 'cashier' | 'creator';

/**
 * QoL Slice 4 — mint a one-time impersonation grant for `userId`. The server
 * returns a URL that opens the target SPA in a new tab and bootstraps a real
 * session there (admin's own session, on the admin origin, stays untouched).
 */
export async function startImpersonation(
  userId: string,
  app: ImpersonationApp = 'member',
): Promise<{ url: string }> {
  const res = await api.post<{ token: string; url: string; expiresAt: string }>(
    `/admin/users/${userId}/impersonate`,
    { app },
  );
  return { url: res.data.url };
}

/**
 * QoL Slice 2 — admin generates a one-time temp password for a user. The
 * response carries the plaintext; the modal must show it ONCE and not persist
 * it anywhere. The user is forced into the change-password screen on next
 * login (driven by `must_change_password=true` set server-side).
 */
export async function adminResetUserPassword(userId: string): Promise<{ tempPassword: string }> {
  const res = await api.post<{ tempPassword: string }>(`/users/${userId}/password/reset`);
  return res.data;
}

export type ReferralCodeRow = {
  code: string;
  status: string;
  useCount: number;
  createdAt: string;
  retiredAt: string | null;
};

export async function adminGetUserReferralCodes(
  userId: string,
): Promise<{ codes: ReferralCodeRow[]; referralCodeChanges: number }> {
  const res = await api.get<{ codes: ReferralCodeRow[]; referralCodeChanges: number }>(
    `/users/${userId}/referral-codes`,
  );
  return res.data;
}

export async function adminReleaseReferralCode(code: string): Promise<void> {
  await api.post(`/admin/referral-codes/${encodeURIComponent(code)}/release`);
}

export async function adminResetReferralChanges(userId: string): Promise<void> {
  await api.post(`/users/${userId}/referral-changes/reset`);
}

/**
 * QoL Slice 2 — caller changes their own password. Clears the must-change
 * gate server-side on success. Used by the forced-change screen and the
 * voluntary Settings password form.
 */
export async function changeOwnPassword(args: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/me/password', args);
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore — clear local state regardless */
  }
  useAuthStore.getState().clear();
  // Drop every cached query so the next signed-in user can't see the
  // previous account's data flash before refetches complete.
  queryClient.clear();
}

/**
 * Restore an in-memory session from the refresh-cookie on the device. Called
 * once at app boot — without this, a page refresh would always boot the user
 * back to the login screen even when their refresh cookie is still valid.
 *
 * Returns `true` if the session was restored, `false` if the refresh cookie
 * was missing/expired and the user must log in.
 */
export async function restoreSession(): Promise<boolean> {
  try {
    const refreshRes = await axios.post<{ accessToken: string }>(
      `${BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const accessToken = refreshRes.data.accessToken;
    // Fetch the user shape so the store has roles/perms — `/auth/refresh`
    // only returns the token.
    const meRes = await axios.get<AuthUser>(`${BASE_URL}/me`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    useAuthStore.getState().setSession(accessToken, meRes.data);
    return true;
  } catch {
    useAuthStore.getState().clear();
    queryClient.clear();
    return false;
  }
}

export type DailyCapGroup = {
  id: string;
  label: string;
  limit: number;
  plans: string[];
};

export type TenantFeatures = {
  kit_codes_transferable: boolean;
  kit_codes_expire_days: number | null;
  kit_codes_downline_transfer: boolean;
  member_2fa_required: boolean;
  account_archive_mode: 'deactivate' | 'erase';
  inactivity_block_enabled: boolean;
  inactivity_block_days: number;
  /** Server-stamped when the rule is switched on; no clock starts before it. */
  inactivity_block_since: string | null;
  ecom_event_on: 'payment_approved' | 'order_completed';
  /** When kit Code+PIN unlocks in the member's Vault for an ecom order. */
  kit_code_reveal_on: 'payment_approved' | 'shipped' | 'delivered' | 'completed';
  /** Item 8: days before an order awaiting receipt auto-completes (null/0 = off). */
  order_auto_complete_days: number | null;
  order_auto_cancel_unpaid_days: number | null;
  disabled_plans: string[];
  /** @deprecated Ordered source_plan codes for the Earnings Breakdown (read fallback; superseded by earnings_breakdown_rows). */
  earnings_breakdown_order: string[];
  /** Member Earnings Breakdown rows: array order = display order, per-row visibility mode. */
  earnings_breakdown_rows: EarningsBreakdownRow[];
  /** Member wallet top-ups (member submits proof, admin approves, wallet credited). */
  wallet_topup_enabled: boolean;
  /** Top-up bonus brackets (first matching bracket wins). */
  wallet_topup_brackets: WalletTopupBracket[];
  /** Plans paid only via ad-hoc distribution runs, skipped at period close. */
  distribution_run_only_plans: string[];
  /** Plans for which Company slots skip the personal-PV maintenance gate. */
  company_auto_maintained_plans: string[];
  /** Global per-plan daily cash-income cap (plan_code → amount; 0/absent = no limit). */
  daily_caps: Record<string, number>;
  auto_place_slots: boolean;
  /** Member redeem form shows the manual L/R placement checkbox (default off). */
  manual_placement_on_redeem: boolean;
  /** Genealogy tree shows the "Activate New Slot" action card (default on). */
  genealogy_activate_slot: boolean;
  /** Free slots earn (receive commissions) from their downlines (default on). */
  free_slots_earn: boolean;
  /** Master show/hide for replicated-link packages (Invite card + per-membership kit scoping; default on). */
  package_replicated_link_enabled: boolean;
  total_saved_counter_enabled: boolean;
  /** CD/Half-CD: an in-debt slot's own purchases pay upline comp (default on). */
  cd_orders_pay_upline: boolean;
  /** In-debt CD slots count toward Stairstep leg requirements (default on). */
  cd_stairstep_count_immediately: boolean;
  /** In-debt Half-CD slots count toward Stairstep leg requirements (default on). */
  half_cd_stairstep_count_immediately: boolean;
  /** In-debt CD/Half-CD directs count for the Binary Mentor's Bonus requirement (default on). */
  cd_binary_mentor_count_directs: boolean;
  /** In-debt CD/Half-CD directs count for the Extreme Binary placement (side) requirement (default on). */
  cd_binary_placement_count_directs: boolean;
  /** Half-CD fires entry comp at redemption like a paid slot (default off). */
  half_cd_triggers_like_ps: boolean;
  /** Plan codes whose cash earnings pay a Half-CD slot's debt (default none). */
  half_cd_debt_plans: string[];
  /** Block cash-outs from in-debt CD slots (Half-CD always allowed; default on). */
  cd_block_payout_in_debt: boolean;
  /** Approved top-ups to an in-debt CD/Half-CD slot pay the debt first (default on). */
  cd_topup_settles_debt: boolean;
  /** How a top-up bonus is treated when the top-up pays a CD debt. */
  cd_topup_bonus_treatment: 'principal_only' | 'include_cash_bonus' | 'no_bonus';
  /** Max slots a member may own (null = unlimited). Enforced at slot creation. */
  max_slots_per_member: number | null;
  /** Append a per-member ordinal suffix ("-01", "-02") to new slot codes. */
  slot_code_series: { enabled: boolean };
  /** BF2: paid->paid slot upgrade re-fires activation bonuses for the new tier. */
  paid_upgrade_refires_bonuses: boolean;
  /** Stairstep rank recomputed live on activation/repurchase (default on). */
  stairstep_live_rankup: boolean;
  /** Pay a one-time per-rank bonus when a slot reaches a new rank (default off). */
  stairstep_rankup_bonus: boolean;
  /** Send the rank-up bell for the first (entry) rank too (default on). */
  rank_up_notify_entry_rank: boolean;
  /** Exclude the earner's own PPV from the Stairstep commissionable base (default off). */
  stairstep_exclude_personal_pv: boolean;
  /** Ranks showcase on the storefront landing + member dashboard (default off). */
  rank_showcase_enabled: boolean;
  /** Per-month PV card shown on the member dashboard (default ppv_and_gpv). */
  member_pv_display: 'off' | 'ppv_only' | 'ppv_and_gpv';
  /** Phase 14: per-method payout deductions (WTAX brackets + service charge + admin fee). */
  payout_deductions: PayoutDeductionsConfig;
  /** LMS creators: their own payout method registry (rates); order comes from payout_deductions. */
  creator_payout_deductions: PayoutDeductionsConfig;
  merchant_payout_deductions: PayoutDeductionsConfig;
  /** Cash-out availability window (day-of-week + open/close hour, tenant tz). */
  cashout_schedule: CashoutSchedule;
  /** When true, cash-out requires verified KYC (else the gate is skipped). */
  cashout_require_kyc: boolean;
  /** When true, the cash-out account-holder name is forced to the owner's full name (default off). */
  lock_cashout_name_to_owner: boolean;
  /** Autofill mode for cash-out fields tagged email/contact_number: off (member
   *  types), prefill (seeded, editable), locked (seeded, read-only + server
   *  overwrite; empty profile value stays editable). Default 'off'. */
  cashout_contact_autofill: 'off' | 'prefill' | 'locked';
  /** When true, a member must have a saved payout QR for the method before cashing out (default off). */
  payout_qr_required: boolean;
  /** When true, a cash-out account number can't be reused by another member (default off). */
  unique_cashout_account_per_user: boolean;
  /** When true, only KYC-verified members may use Cash-on-Delivery (default off). */
  cod_requires_kyc: boolean;
  /** Allow "Approve anyway" on a proof whose reference was never verified. */
  manual_payment_force_approve: boolean;
  /** Admin-defined KYC document requirements (upload slots in the member tab). */
  kyc_requirements: KycRequirementDef[];
  /** Min/max bounds on cash-out (per-slot single-request + per-member aggregate). */
  cashout_limits: CashoutLimits;
  /** When false, a member with a pending request can't create another (see scope). */
  multiple_cashout_requests_allowed: boolean;
  /** Scope of "already pending" when multiple requests are disallowed. */
  cashout_multiple_scope: 'member' | 'slot';
  /** When false, members can't cancel their own not-yet-batched cash-out request. */
  cashout_cancellation_allowed: boolean;
  /** Tenant constants stamped on every row of the payout batch disbursement file. */
  payout_export: PayoutExportConfig;
  /** Admin-managed ecom payment-method registry (currency × wallet/manual settlement). */
  ecom_payment_methods: EcomPaymentMethodsConfig;
  /** Admin-managed POS (Cashier) payment-method registry (tenderKind × wallet currency). */
  pos_payment_methods: PosPaymentMethodsConfig;
  /** Admin-managed POS custom fee TYPES; each is typed by the cashier, a
   *  percentage of the running total, or a fixed amount (applied in list order). */
  pos_custom_fees: PosCustomFeesConfig;
  /** F5: auto-create the slot on Membership Kit purchase, per sales channel. */
  kit_auto_create_slot: { ecom: boolean; cashier: boolean; virtual: boolean };
  /** F6: defer PV to a redeemable product code in the buyer's Code Vault. */
  product_code_generation: boolean;
  /** Global kill-switch for online payment gateways (Dragonpay / iPay88). */
  gateway_payments_enabled: boolean;
  /** BF1: Terms & Privacy. Versions auto-bump server-side when the text changes. */
  terms_text: string;
  terms_version: number;
  privacy_policy_text: string;
  privacy_policy_version: number;
  require_terms_on_signup: boolean;
  /** Username on registration + login-by-username; activation agreement. */
  require_username_on_signup: boolean;
  /** Admin-defined extra signup fields (text or file upload). */
  registration_fields: RegistrationField[];
  /** Which Profile-tab fields a member may edit (name / phone / photo). */
  member_profile_editable: MemberEditableFields;
  /** Which Profile fields a merchant may edit (business/owner/contact/pickup). */
  merchant_profile_editable: MerchantEditableFields;
  allow_login_by_username: boolean;
  activation_terms_text: string;
  activation_terms_version: number;
  /** Item 11: admin-editable message shown to the member after a slot activates. */
  activation_success_message: string;
  require_activation_terms: boolean;
  /** Storefront: congratulatory message shown in the order-placed dialog. */
  order_success_message: string;
  /** Member portal: dialog shown on login when the member has unpaid orders. */
  unpaid_orders_dialog_message: string;
  /**
   * Admin-configurable default sponsor user_id for self-signups without a
   * `?ref=` code. Null = super_admin fallback (legacy).
   */
  default_sponsor_user_id: string | null;
  /** Walk-in default sponsor SLOT (slot_code). Preferred over the user field. */
  default_sponsor_slot_code: string | null;
  /** When set, ALL link signups register under this slot (slot_code). */
  link_sponsor_override_slot_code: string | null;
  /** 2nd+ slots suggest the member's own first slot as sponsor (self-stacking). */
  extra_slots_self_sponsor: boolean;
  /** 2nd+ slots default to the slot the member registered under (default on). */
  extra_slots_use_registration_slot: boolean;
  /** Sponsor editable at redeem for a link-registered member's first slot (default on). */
  sponsor_editable_from_replicated_link: boolean;
  redeem_for_downline_direct_only: boolean;
  /** Sponsor editable at redeem for 2nd+ slots when self-stacking is OFF (default on). */
  sponsor_editable_extra_slots: boolean;
  /** Sponsor editable at redeem for 2nd+ slots when self-stacking is ON (default on). */
  sponsor_editable_self_stack: boolean;
  /** Show the member's sponsor on the dashboard "Your standing" card (default off). */
  show_sponsor_on_dashboard: boolean;
  /** Which detail fields show on each box of the binary genealogy tree. */
  binary_genealogy_node_fields: BinaryGenealogyNodeFields;
  /** Platform/domain license expiration (ISO date, super-admin only). Drives the renewal popup. */
  domain_expires_at: string | null;
  /** Show the sponsor field on redeem; off hides it for overridden members (default on). */
  show_sponsor_on_redeem: boolean;
  signup_mode: 'on' | 'links_only' | 'by_sponsor_only';
  public_site_info_only: boolean;
  /** Storefront home: leave the curated Featured Packages out of the product
   *  grid below them, so they aren't listed twice (default off). */
  product_feed_hide_featured_packages: boolean;
  /** Storefront home: leave `featured` products out of the product grid
   *  (default off — `featured` is otherwise only a sort boost). */
  product_feed_hide_featured_products: boolean;
  /** An enabled campaign page counts as an invite under the restrictive signup
   *  modes; those visitors register under the walk-in default sponsor. */
  campaign_page_signup_allowed: boolean;
  /** Members may set/change their own referral code from Settings (opt-in). */
  member_referral_code_self_set: boolean;
  /** Max referral-code changes per member (0 = none); admins can reset a member. */
  referral_code_max_changes: number;
  /** When on, the referral link shows the admin-built landing page before signup. */
  replicated_page_enabled: boolean;
  virtual_orders_auto_complete: boolean;
  /** Split a mixed virtual+physical cart into two orders under one payment group. */
  virtual_orders_split_checkout: boolean;
  virtual_products_open_replicated_page: boolean;
  /** Master admin-UI switch for virtual products (page, product flag, policies). */
  virtual_products_enabled: boolean;
  /** Master admin-UI switch for materials and recipes (Materials page, Recipe tab). */
  materials_enabled: boolean;
  /** Stock lots: per-receipt cost + expiry, FIFO/FEFO stock-out, actual cost of sales. */
  inventory_lots_enabled: boolean;
  lot_pick_order: 'fifo' | 'fefo';
  /** Item locator: aisle / rack per product per branch (admin + cashier UI). */
  item_locator_enabled: boolean;
  /** Gift checks (internally vouchers): programs, member page, till buttons. */
  vouchers_enabled: boolean;
  /** What the tenant calls a gift check ("Gift Check"). */
  voucher_label: string;
  /** Birthday gift checks: days after the birthday a claim stays open (0 = the day). */
  birthday_claim_window_days: number;
  /** Birthday gift checks only for members whose KYC is verified. */
  birthday_requires_kyc: boolean;
  /** Where a member's birth date is asked for (signup form and profile). */
  birth_date_field: 'hidden' | 'optional' | 'required';
  /** A free slot for every member who signs up, at an FS-eligible membership. */
  signup_free_slot: { enabled: boolean; membership_id: string | null };
  /** Cashiers can top up a member's cash wallet at the till. */
  pos_topup_enabled: boolean;
  /** Till top-ups may also be paid by card / e-wallet (reference required). */
  pos_topup_exact_methods_enabled: boolean;
  /** A till top-up of this amount or more needs a manager PIN; null = never. */
  pos_topup_pin_above: string | null;
  /** Member Resources library (documents + links) — member page and admin CMS. */
  member_resources_enabled: boolean;
  member_resources_office_viewer: boolean;
  /** LMS: R2 lesson-video size cap in MB (presigned upload). */
  course_video_max_mb: number;
  /** Phase 9: storage lifecycle sweep + how long an orphan waits before deletion. */
  storage_sweep_enabled: boolean;
  /** Hosts a lesson embed URL may point at. */
  course_embed_hosts: string[];
  storage_delete_grace_days: number;
  /** LMS: lapsed-learner nudge window in days; null/0 = off. */
  lms_nudge_after_days: number | null;
  pos_course_sale_mode: 'allow' | 'warn' | 'block';
  lms_content_rereview: 'flag' | 'delist' | 'hold';
  lms_content_rereview_scope: 'content' | 'all';
  lms_abuse_reporting_enabled: boolean;
  lms_seek_restriction: boolean;
  /** LMS: show learners the "Mark complete" button (default true). */
  lms_manual_completion: boolean;
  /** LMS: visitors who are not signed in may browse the courses (default true). */
  lms_public_catalog: boolean;
  /** LMS: the coach phone app links to the website for a course (default false, Play risk). */
  lms_app_web_link: boolean;
  lms_subscription_model: 'tenant' | 'creator';
  lms_subscription_eligibility: 'activated_slot' | 'any_member';
  /** LMS creator model: members may opt in to wallet auto-renewal (default true). */
  lms_subscription_auto_renew_enabled: boolean;
  /** LMS creator model: a house plan paid by Stripe card becomes a Stripe subscription (default false). */
  lms_subscription_card_recurring: boolean;
  /** LMS master switch (default off): hides every learning surface + 404s the LMS API. */
  lms_enabled: boolean;
  /** LMS: member dashboard style — 'mlm' passbook (default) or 'learning' classroom. */
  member_dashboard_mode: 'mlm' | 'learning';
  /** LMS learning mode: show the Invite-a-friend card on the classroom dashboard (default true). */
  show_team_earnings: boolean;
  /** Does a slot's own PPV count toward its own GPV? Default true (own PPV + downline). */
  gpv_includes_ppv: boolean;
  /** Dropshipping Income: per-product replicated-link retail-margin earning (opt-in). */
  dropshipping_income: boolean;
  /** Store-wide Share & Earn link (/s/{ref}) — whole-storefront dropship attribution (needs the above). */
  store_wide_dropship_link: boolean;
  /** Attribute a `/buy` funnel sale to the inviter as a dropship sale (needs the above). */
  virtual_funnel_dropship_attribution: boolean;
  /** Replicated-link PV to owner: non-activated buyer's SRP PV credited to the link owner (opt-in). */
  replicated_link_pv_to_owner: boolean;
  /** When on, share links carry an encrypted opaque ref token instead of the plaintext code. */
  replicated_link_ref_encrypted: boolean;
  /** BF4: non-cash (gc/coin) purchases fire comp like cash when true (default false). */
  commissions_on_non_cash: boolean;
  /** Wallet (GC/coin) ecom orders with an address charge shipping + walk the ladder. */
  wallet_orders_charge_shipping: boolean;
  wallet_orders_auto_complete: boolean;
  /** All-virtual carts check out on a stripped screen and go straight to the
   *  locked payment page. Default off. */
  virtual_instant_pay: boolean;
  /** Campaign-page signups with an unpaid order are locked to the payment page.
   *  Default off. */
  unpaid_order_lock_enabled: boolean;
  member_order_cancel_enabled: boolean;
  /** A fresh login resumes an interrupted virtual checkout (unpaid virtual
   *  order → its pay page, else all-virtual cart → /checkout). One-shot at
   *  login, not a lock. Default off. */
  virtual_checkout_resume_on_login: boolean;
  /** Shows the Accounting section in the admin sidebar (/accounting) — preview + live v1 pages. */
  accounting_enabled: boolean;
  /** Accounting v1 — consignment at the till (cashier surfaces + /pos/consignment*). */
  consignment_enabled: boolean;
  /** Track 2A — stockist consignment (SCN-/SAR-/SRTN-/SCPAY- branch-keyed module). */
  stockist_consignment_enabled: boolean;
  /** Cash/Bank register — require a funds account on payments/expenses/receipts. */
  funds_account_required: boolean;
  /** Accounting Phase 2 — snapshot house-line VAT classification at checkout (never reprices). */
  house_vat_enabled: boolean;
  /** Accounting Phase 2 — VAT rate (money-string percent) for 'vatable' house lines. */
  house_vat_default_pct: string;
  /** Phase 4 lean GL — the poster only books documents dated on/after this date (YYYY-MM-DD); null = no bound. */
  accounting_posting_start_date: string | null;
  /** Phase 4 lean GL — fiscal-year start month (1–12); Balance Sheet's Retained Earnings / Net Income boundary. 1 = calendar year. */
  accounting_fiscal_year_start_month: number;
  /** R2 — general invoicing: gates creation of general billings + the Receivables surfaces. */
  ar_invoicing_enabled: boolean;
  /** R2 — fallback payment_terms.code when neither the document nor the customer names one. */
  ar_default_terms_code: string;
  /** R2 — credit-limit behaviour at POST: off (silent) / warn / block (409). */
  ar_credit_limit_mode: 'off' | 'warn' | 'block';
  /** R2 — which product this deployment presents as; drives the admin sidebar only. */
  platform_edition: 'mlm' | 'accounting';
  /**
   * Membership tier whose per-product prices apply to a buyer with NO active
   * slot. null (default) = slotless buyers pay SRP. Never applies when the
   * cashier merely deselected an activated member's slot.
   */
  non_activated_pricing_membership_id: string | null;
  /**
   * Which slot's per-product price a member holding SEVERAL slots pays:
   * 'active_slot' (default) = the tier of the slot they're logged in on;
   * 'best_across_slots' = the lowest price any of their active slots earns.
   * Pricing only — attribution (buyer_slot_id / PV / commissions) always
   * follows the selected slot.
   */
  pricing_slot_source: 'active_slot' | 'best_across_slots';
  /** BF4: display name for the coin currency (default "Coins"). */
  coin_label: string;
  /** BF4: per-plan flat coin reward paid to each earner alongside cash (plan_code → amount). */
  coin_rewards: Record<string, number>;
  /** Flat AI-Credit rider per plan (mirrors coin_rewards). */
  ai_credit_rewards: Record<string, number>;
  /** F3: admin display-name overrides for ledger kinds (raw kind → label; sparse). */
  ledger_labels: Record<string, string>;
  /** F3: admin display-name overrides for ledger source_plan values (sparse). */
  ledger_source_plan_labels: Record<string, string>;
  /** JM-EX9: per-member notification templates (title/body with placeholders). */
  notification_templates: {
    earnings: { enabled: boolean; title: string; body: string };
    /** Per-event member notifications keyed by event code (order_placed, …). */
    events?: Record<string, { enabled: boolean; title: string; body: string }>;
  };
  /** Email admins when a manual-payment proof is submitted (proof-submission
   *  only). Empty recipients = every super-admin's account email. Needs the
   *  deployment's SMTP env to actually send. Default disabled. */
  manual_payment_admin_email: { enabled: boolean; recipients: string[] };
  /** Merchant feature: default platform fee % (money string) charged on merchant sales. */
  platform_fee_pct: string;
  /** Inclusive [min,max] band a merchant-declared platform fee % must fall in. */
  platform_fee_min_pct: string;
  platform_fee_max_pct: string;
  /** Merchant pricing: default PV-commission % pre-filled at product review. */
  merchant_pv_commission_pct: string;
  /** Realtime Unilevel defaults pre-filling the merchant-product approval dialog.
   *  Pool = ceiling% × commission; each default-share is that level's % of the
   *  pool (level count = array length). */
  merchant_rtu_ceiling_pct: string;
  merchant_rtu_default_shares: string[];
  /** Merchant feature: when on, merchant sales also fire MLM comp (default off). */
  merchant_sales_earn_comp: boolean;
  /** Order-by-Area: auto-route an ecom ship order to the branch tagged for the buyer's zone (default off). */
  order_by_area: boolean;
  /** Order-by-Area: a stockist that fulfills a house order accrues a receivable from Main (default off). */
  stockist_receivables: boolean;
  /** Merchant feature: admin-defined registration requirements. */
  merchant_requirements: Array<{
    key: string;
    label: string;
    description?: string;
    required: boolean;
    acceptsFile: boolean;
    fileTypes?: string[];
  }>;
  /** Merchant feature: cadence for batching merchant payouts. */
  merchant_payout_schedule: 'weekly' | 'biweekly' | 'monthly';
  /** LMS creators: default platform fee % on creator course sales + the [min,max] band. */
  creator_platform_fee_pct: string;
  creator_platform_fee_min_pct: string;
  creator_platform_fee_max_pct: string;
  /** LMS creators: when on, creator course sales also fire MLM comp (default off). */
  creator_sales_earn_comp: boolean;
  /** LMS creators: admin-defined registration requirements (merchant shape). */
  creator_requirements: Array<{
    key: string;
    label: string;
    description?: string;
    required: boolean;
    acceptsFile: boolean;
    fileTypes?: string[];
  }>;
  /** Storefront flash-sale strip: featured product ids + optional countdown end. */
  flash_sale: {
    enabled: boolean;
    productIds: string[];
    endsAt: string | null;
  };
  /** Landing "packages": admin-curated, ordered product ids for a tenant landing's packages section. */
  landing_packages: {
    productIds: string[];
  };
  /** Featured "packages": admin-curated, ordered product ids for the storefront Featured Packages section. */
  featured_packages: {
    enabled: boolean;
    productIds: string[];
    heading: string | null;
  };
  /** Storefront CMS content (hero, banners, vouchers, footer, etc.). */
  storefront: StorefrontConfig;
  /** Chat (M-chat): master on/off for buyer↔seller messaging. */
  messaging_enabled: boolean;
  /** Auto chat-block on spam: flag + trailing window (s) + message threshold. */
  chat_spam_autoblock_enabled: boolean;
  chat_spam_window_seconds: number;
  chat_spam_threshold: number;
  /** Auto-Ship: master on/off for member auto-ship subscriptions. */
  autoship_enabled: boolean;
  /** Guided tutorials for new members, plus the Tutorial button in the member app. */
  member_tutorial_enabled: boolean;
  /** Member monthly subscription: master switch for the paid special-links unlock. */
  member_subscription_enabled: boolean;
  /** Member subscription plan: monthly price + wallet currency charged. */
  member_subscription: { price: string; currency: 'cash' | 'gc' };
  /** AI Assistant: master switch for the member /assistant page (default off). */
  assistant_enabled: boolean;
  /** AI Assistant config: model, daily cap, monthly USD budget, web search, personas, knowledge. */
  assistant: {
    model: 'claude-haiku-4-5' | 'claude-sonnet-5' | 'claude-opus-5';
    /** Messages per member per tenant-day; <= 0 = unlimited. */
    daily_cap: number;
    /** Tenant-wide monthly spend ceiling in USD (money string); ''/'0' = unlimited. */
    monthly_budget_usd: string;
    web_search_enabled: boolean;
    /** Cross-chat recall: digests of idle threads + past-chat search tools. */
    recall_enabled: boolean;
    /** Minutes a thread must sit idle before it's digested. */
    recall_idle_minutes: number;
    /** Only digest threads newer than this (anti-stampede backfill floor). */
    recall_digest_max_age_days: number;
    /**
     * Persona overrides, edited by AssistantPersonaEditor. Keep in sync with
     * `assistantPersonaOverrideSchema` (packages/shared/src/zod/kits.ts) — a
     * field missing from EITHER is silently dropped on save.
     */
    personas: Array<{
      id: string;
      name?: string;
      icon?: string;
      /** Uploaded avatar image (R2 object key); `icon` is the fallback. */
      avatarKey?: string;
      tagline?: string;
      instructions?: string;
      allowAccountTools?: boolean;
      allowWebSearch?: boolean;
      voiceHint?: string;
      enabled?: boolean;
      intake?: Array<{ key: string; question: string; placeholder?: string }>;
      suggestedQuestions?: string[];
    }>;
    /** Meter usage with AI Credits (free monthly allowance, then wallet). */
    credits_enabled: boolean;
    credits_monthly_free: number;
    /** Peso price per credit; '' = not for sale. */
    credits_price: string;
    /** Land members on /assistant after login instead of the dashboard. */
    home_promotion: boolean;
    /** Tenant-authored knowledge text folded into the system prompt. */
    knowledge: string;
  };
  /** Miracles Protocol coaching: master switch for the coach app + /coaching API (default off). */
  coaching_enabled: boolean;
  /**
   * Coaching config. Keep in sync with `CoachingConfig` (packages/shared) and the
   * `coaching` block of `tenantFeaturesPatchSchema`: a key missing from either is
   * silently dropped on save.
   */
  coaching: {
    astro_provider: 'local' | 'prokerala';
    /** Hours a Prokerala chart is kept; null = keep (written storage permission on file). */
    astro_natal_cache_hours: number | null;
    astro_credit_alert_pct: number;
    free_daily_messages: number;
    pro_daily_messages: number;
    free_active_goals: number;
    streak_freeze_pro: boolean;
    /** Gate the chart + daily guidance behind Pro (default off during the beta). */
    chart_guidance_pro_only: boolean;
    /** Gate breathwork, frequencies, biorhythm and the written weekly summary behind Pro (default off). */
    tools_pro_only: boolean;
    /** The provider's monthly credit pack, i.e. what the alert percent is OF. */
    astro_monthly_credits: number;
    model: 'claude-haiku-4-5' | 'claude-sonnet-5' | 'claude-opus-5';
    daily_engine_enabled: boolean;
    /** Coaching's own monthly Claude budget in USD (Master Kala + generated text); '' = unlimited. */
    monthly_budget_usd: string;
    /** The member community link shown on the coach app's Home and Profile; '' = no card. https only. */
    community_url: string;
    /** That card's title; '' = "Join the community". */
    community_label: string;
    /**
     * Which coaching feature belongs to which plan, edited on Coaching ->
     * Features. Supersedes the switches above key by key; anything unset falls
     * back to them, so null means "nothing saved, behave as before".
     */
    plan_features: {
      access?: Record<string, 'free' | 'pro' | 'elite' | 'off'>;
      limits?: Record<string, Record<string, number>>;
    } | null;
    /** Subscription required to use the app (no free tier); default false. */
    paid_only: boolean;
    /** AI Credits per coaching action, by action key; missing = default 1, 0 = free. */
    credit_costs: Record<string, number>;
    /** Pesos per USD for the admin cost figures. */
    usd_php_rate: number;
    /** What the two paid tiers are called; the keys pro / elite never change. */
    tier_labels: { pro: string; elite: string };
    /** The frequency categories in order; [] = the seven built-ins. */
    frequency_categories: Array<{ key: string; label: string; hue: number | null }>;
    /** The content library's categories in order; [] = none. */
    library_categories: Array<{ key: string; label: string }>;
  };
};

export type AssistantUsageSummary = {
  monthUsd: string;
  exchangeCount: number;
  byModel: Array<{
    model: string;
    exchanges: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: string;
  }>;
  /** Spend split by persona (null personaId = pre-attribution rows). */
  byPersona: Array<{
    personaId: string | null;
    name: string;
    icon: string;
    exchanges: number;
    costUsd: string;
  }>;
  webSearchRequests: number;
  /** Answer-quality counters over the same month window. */
  quality: {
    answers: number;
    refusals: number;
    truncated: number;
    stopped: number;
    feedbackUp: number;
    feedbackDown: number;
  };
  /** The configured ceiling, echoed so the card can draw a usage bar. */
  budget: { monthlyUsd: string | null };
};
/** Month-to-date AI Assistant spend (Settings card read-only line). */
export async function getAssistantUsage(): Promise<AssistantUsageSummary> {
  const res = await api.get<AssistantUsageSummary>('/admin/assistant/usage');
  return res.data;
}

export async function getTenantFeatures(): Promise<TenantFeatures> {
  const res = await api.get<TenantFeatures>('/admin/tenant/features');
  return res.data;
}

export type FreeSlotBackfillResult = {
  execute: boolean;
  eligible: number;
  sample: Array<{ id: string; email: string; name: string }>;
  activated: number;
  failed: Array<{ userId: string; email: string; error: string }>;
};

/** Count (execute false) or give (execute true) a free slot to every member who has none. */
export async function backfillFreeSlots(body: {
  execute: boolean;
}): Promise<FreeSlotBackfillResult> {
  const res = await api.post<FreeSlotBackfillResult>(
    '/admin/tenant/signup-free-slot/backfill',
    body,
  );
  return res.data;
}

export async function patchTenantFeatures(patch: Partial<TenantFeatures>): Promise<TenantFeatures> {
  const res = await api.patch<TenantFeatures>('/admin/tenant/features', patch);
  return res.data;
}

export type SlotSponsorPreview = {
  slotCode: string;
  ownerFullName: string | null;
  ownerEmail: string;
  membershipCode: string;
  slotType: 'paid' | 'free';
};

/**
 * Resolve a slot_code (or referral alias) to its sponsor-slot details so the
 * settings pickers can confirm the owner before saving. Returns null on 404
 * (no active slot resolves) so callers can show "not found".
 */
export async function previewSlotByCode(code: string): Promise<SlotSponsorPreview | null> {
  try {
    const res = await api.post<SlotSponsorPreview>('/admin/slots/preview-by-code', { code });
    return res.data;
  } catch (err) {
    if ((err as { response?: { status?: number } }).response?.status === 404) return null;
    throw err;
  }
}

export type KitTermsOption = {
  kitProductId: string;
  name: string;
  slotType: 'paid' | 'cd' | 'half_cd';
  debtAmount: string | null;
};

export type SlotTermsPreview = {
  kits: KitTermsOption[];
  derived: {
    slotType: 'paid' | 'cd' | 'half_cd';
    debtAmount: string | null;
    kitProductId: string | null;
  } | null;
  requiresKitChoice: boolean;
};

/**
 * The slot type + opening debt an admin-created slot would carry for a
 * membership, derived server-side from its kit product(s). The Create Slot
 * dialog shows this (and a kit picker when `requiresKitChoice`) instead of
 * free-typing the slot type.
 */
export async function previewSlotTerms(membershipCode: string): Promise<SlotTermsPreview> {
  const res = await api.post<SlotTermsPreview>('/admin/slots/preview-terms', { membershipCode });
  return res.data;
}

// Theme lives in the separate `tenant_config.theme` column (super_admin only).
export async function getTenantTheme(): Promise<ThemeTokens> {
  const res = await api.get<ThemeTokens>('/admin/tenant/theme');
  return res.data;
}

export async function patchTenantTheme(patch: Partial<ThemeTokens>): Promise<ThemeTokens> {
  const res = await api.patch<ThemeTokens>('/admin/tenant/theme', patch);
  return res.data;
}

// ── Feedback inbox ─────────────────────────────────────────────────────
export type FeedbackRow = {
  id: string;
  userId: string | null;
  name: string | null;
  email: string | null;
  category: string;
  rating: number | null;
  subject: string | null;
  message: string;
  status: 'new' | 'read' | 'archived';
  pageUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function getFeedback(params?: {
  status?: 'new' | 'read' | 'archived';
  limit?: number;
  offset?: number;
}): Promise<{ items: FeedbackRow[]; total: number }> {
  const res = await api.get<{ items: FeedbackRow[]; total: number }>('/admin/feedback', { params });
  return res.data;
}

export async function updateFeedbackStatus(
  id: string,
  status: 'new' | 'read' | 'archived',
): Promise<FeedbackRow> {
  const res = await api.patch<FeedbackRow>(`/admin/feedback/${id}`, { status });
  return res.data;
}

/** Fetch a single user (for resolving a configured default sponsor's display info). */
export async function getAdminUser(id: string): Promise<{
  id: string;
  fullName: string | null;
  email: string;
  referralCode: string | null;
  status: string;
}> {
  const res = await api.get<{
    id: string;
    fullName: string | null;
    email: string;
    status: string;
  }>(`/users/${id}`);
  // The full /users/:id payload doesn't return referralCode today; the picker
  // surfaces enough without it (name + email). If a future feature needs the
  // referral code on detail, extend the route response — not the picker.
  return {
    id: res.data.id,
    fullName: res.data.fullName,
    email: res.data.email,
    referralCode: null,
    status: res.data.status,
  };
}

export type AuditRow = {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  /**
   * The human behind an impersonated session, when there was one. The actor
   * columns name the account the write LOOKED like it came from; without these
   * two, anything done through a shared login (the house creator account above
   * all) is unattributable to a person.
   */
  impersonatorUserId: string | null;
  impersonatorEmail: string | null;
  action: string;
  targetTable: string | null;
  targetId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
};

export type AuditListResponse = {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
};

export async function listAudit(params: {
  page?: number;
  pageSize?: number;
  action?: string;
  actorUserId?: string;
  /** Narrow to everything one operator did while impersonating anyone. */
  impersonatorUserId?: string;
  targetTable?: string;
  targetId?: string;
  since?: string;
  until?: string;
}): Promise<AuditListResponse> {
  const res = await api.get<AuditListResponse>('/admin/audit', { params });
  return res.data;
}

export type Product = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  /** Rich long-form copy (sanitized HTML) from the WYSIWYG Details tab. */
  detailsHtml: string | null;
  videoUrl: string | null;
  price: string;
  pv: string;
  /** Accounting Phase 1: static unit cost (COGS basis). null = unknown. Admin
   *  catalog only — public/member product payloads strip it server-side. */
  cost: string | null;
  /** Accounting Phase 2: 3-code tax registry (vatable | exempt | zero_rated). */
  taxCode: TaxCode;
  /** BF4: per-currency sticker prices (null = not sold in that currency) + coin grant per unit. */
  priceGc: string | null;
  priceCoin: string | null;
  /**
   * Explicit "sold in this currency" intent, separate from the price being set.
   * A stored `0` used to slip past the null-only checkout gate and price the
   * line at zero, so "not sold" is now a flag rather than a sentinel value.
   * PRODUCT-level only — variants have no such columns and inherit these.
   */
  gcPurchasable: boolean;
  coinPurchasable: boolean;
  coinGrant: string;
  /** Assistant answers granted per unit; null = none. */
  aiCreditReward?: number | null;
  /** Binary points contributed per unit on repurchase (Teslab variant). */
  binaryPoints: string;
  /** Vortex tokens granted per unit on purchase. */
  vortexToken: string;
  /** Physical dimensions + weight for the shipping packer. */
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  categoryId: string | null;
  images: string[];
  active: boolean;
  featured: boolean;
  /** Free-shipping tag: zeroes a store group's fee when all its items are tagged. */
  freeShipping: boolean;
  /** Virtual product / service: no shipping, no dims required, not stock-tracked. */
  isVirtual: boolean;
  /** Campaign page a virtual product opens instead of its product page
   *  (`virtual_products_open_replicated_page`). null = normal product page. */
  replicatedPageId: string | null;
  isKit: boolean;
  /** Promo bundle: no own stock, deducts inclusive components on sale, grants no membership. */
  isBundle: boolean;
  /** Bundle only: may a stockist's till open it to sell its inclusions? */
  stockistOpenable: boolean;
  kitMembershipId: string | null;
  /** BF2/CD: the slot type a redeemed kit creates, and whether it activates/upgrades. */
  kitSlotType: 'paid' | 'free' | 'cd' | 'half_cd';
  kitUsageMode: 'activation' | 'upgrade' | 'both';
  /** CD/Half-CD kits: the debt the minted slot starts with (required > 0). */
  kitDebtAmount: string | null;
  /** Sale-channel + buyer-eligibility policy (default 'both' on both). */
  platformSold: 'cashier' | 'ecom' | 'both';
  soldTo: 'activated' | 'non_activated' | 'both';
  /** Redeem channel: GC-only, no PV, hidden from the normal storefront. */
  redeemOnly: boolean;
  /** House products are born approved; a merchant listing waits for review. */
  approvalStatus: 'pending' | 'approved' | 'rejected';
  /** Material / supply: stocked and costed, never sold; recipes use it up. */
  isMaterial: boolean;
  /** Display unit of a stock quantity ("pcs", "g", "ml"). null = pieces. */
  unitLabel: string | null;
  /** Units per purchase pack, a receiving aid (1 bag = 1000 g). null = none. */
  packSize: number | null;
  /** Days a received unit keeps; a stock lot with no expiry typed gets received + this. */
  shelfLifeDays: number | null;
};

export type Membership = {
  id: string;
  code: string;
  name: string;
  discountPct: string;
  active: number;
  /** Per-tier comp-plan config blob (Phase 12B). May be empty. */
  config: Record<string, unknown>;
};

export async function listProducts(params: {
  page?: number;
  pageSize?: number;
  search?: string;
  activeOnly?: boolean;
  /** Splits the catalog between the Products page and the Virtual products page. */
  virtual?: 'only' | 'exclude' | 'all';
  /** Only rows that may be a kit/bundle INCLUSION (never a kit; a bundle only when `componentAllowBundles`). */
  componentEligible?: boolean;
  /** With `componentEligible`: also offer bundles (the picker is editing a bundle). */
  componentAllowBundles?: boolean;
  /** Splits the catalog between the Products page and the Materials page. */
  material?: 'only' | 'exclude' | 'all';
}): Promise<Product[]> {
  // Admin endpoint: the full HOUSE catalog regardless of sale channel / buyer
  // eligibility / archive state. The public `/products` is storefront-filtered
  // (ecom + non-activated only) and would hide cashier-only / activated-only
  // products from the admin — and silently turn an edit-save into a CREATE.
  //
  // `componentEligible` is dropped when falsy on purpose: the route parses it
  // with z.coerce.boolean(), which reads the serialized string "false" as TRUE.
  const { componentEligible, componentAllowBundles, ...rest } = params;
  const res = await api.get<Product[]>('/admin/products', {
    params: componentEligible
      ? {
          ...rest,
          componentEligible: true,
          ...(componentAllowBundles ? { componentAllowBundles: true } : {}),
        }
      : rest,
  });
  return res.data;
}

/**
 * Resolve a stored product-id list to full admin rows, order preserved and
 * ARCHIVED INCLUDED — so a picker can always display (and let you remove) a
 * selection its search page doesn't happen to contain.
 */
export async function getAdminProductsByIds(ids: string[]): Promise<Product[]> {
  if (ids.length === 0) return [];
  const res = await api.get<Product[]>('/admin/products/by-ids', {
    params: { ids: ids.join(',') },
  });
  return res.data;
}

// ── Categories CMS ────────────────────────────────────────────────────────
export type Category = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  imageKey?: string | null;
  createdAt?: string;
};

export async function listCategories(): Promise<Category[]> {
  const res = await api.get<Category[]>('/categories');
  return res.data;
}

export async function createCategory(input: {
  code: string;
  name: string;
  parentId?: string | null;
  sortOrder?: number;
  imageKey?: string | null;
}): Promise<Category> {
  const res = await api.post<Category>('/categories', input);
  return res.data;
}

export async function updateCategory(
  id: string,
  patch: Partial<{
    code: string;
    name: string;
    parentId: string | null;
    sortOrder: number;
    imageKey: string | null;
  }>,
): Promise<Category> {
  const res = await api.patch<Category>(`/categories/${id}`, patch);
  return res.data;
}

export async function deleteCategory(id: string): Promise<void> {
  await api.delete(`/categories/${id}`);
}

// ── Official Store Featured Feedback: admin pins house-product reviews to the
// Official Store page's "Featured Feedback" section. ──
export type OfficialStoreReview = {
  id: string;
  rating: number;
  comment: string | null;
  hasPhoto: boolean;
  photoCount: number;
  productId: string;
  productName: string;
  reviewerName: string;
  featured: boolean;
  createdAt: string;
};

export async function listOfficialStoreReviews(opts?: {
  rating?: number;
  featuredOnly?: boolean;
}): Promise<OfficialStoreReview[]> {
  const params = new URLSearchParams();
  if (opts?.rating) params.set('rating', String(opts.rating));
  if (opts?.featuredOnly) params.set('featuredOnly', 'true');
  const q = params.toString();
  const res = await api.get<{ reviews: OfficialStoreReview[] }>(
    `/admin/official-store/reviews${q ? `?${q}` : ''}`,
  );
  return res.data.reviews;
}

export async function setOfficialStoreReviewFeatured(id: string, featured: boolean): Promise<void> {
  await api.post(`/admin/official-store/reviews/${id}/feature`, { featured });
}

export function productReviewPhotoUrl(productId: string, reviewId: string, index = 0): string {
  const q = index ? `?i=${index}` : '';
  return `${api.defaults.baseURL ?? ''}/products/${productId}/reviews/${reviewId}/photo${q}`;
}

/** Upload a category tile image → returns the R2 object key. */
export async function uploadCategoryAsset(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/categories/asset', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to a category's tile image (presigned). */
export function categoryImageUrl(id: string): string {
  return `${api.defaults.baseURL ?? ''}/categories/${id}/image`;
}

export async function createProduct(
  input: Partial<Product> & { sku: string; name: string; price: string; pv: string },
): Promise<Product> {
  const res = await api.post<Product>('/products', input);
  return res.data;
}

/**
 * How widely a product is already referenced, returned alongside the 409
 * `sku_in_use` when renaming a SKU that's out in the world.
 */
export type SkuUsage = {
  orderLines: number;
  inventoryMovements: number;
  purchaseOrderLines: number;
  variants: number;
};

/** Courses a product unlocks when bought (a membership kit sold as a "course"). */
export async function getProductCourseGrants(
  id: string,
): Promise<Array<{ courseId: string; title: string; active: boolean }>> {
  const res = await api.get<{
    courses: Array<{ courseId: string; title: string; active: boolean }>;
  }>(`/products/${id}/course-grants`);
  return res.data.courses;
}
export async function putProductCourseGrants(id: string, courseIds: string[]): Promise<void> {
  await api.put(`/products/${id}/course-grants`, { courseIds });
}

export async function updateProduct(
  id: string,
  patch: Partial<Product> & { confirmSkuRename?: boolean },
): Promise<Product> {
  const res = await api.patch<Product>(`/products/${id}`, patch);
  return res.data;
}

/** Accounting Phase 1: suggested unit cost for the editor's cost field —
 *  latest approved PO line cost, else the preferred sourcing row. */
export type CostSuggestion = {
  unitCost: string;
  source: 'purchase_order' | 'supplier';
  /** PO number or supplier name, for the chip label. */
  reference: string;
};
export async function getProductCostSuggestion(
  productId: string,
  variantId?: string,
): Promise<CostSuggestion | null> {
  const res = await api.get<{ suggestion: CostSuggestion | null }>(
    `/products/${productId}/cost-suggestion`,
    { params: variantId ? { variantId } : undefined },
  );
  return res.data.suggestion;
}

/** Per-variant override fields (null = inherit the product). */
export type VariantOverrides = {
  /** Accounting Phase 1: unit-cost override (null = inherit product's cost). */
  cost: string | null;
  binaryPoints: string | null;
  vortexToken: string | null;
  /** Coins awarded per unit on a cash purchase (null = inherit product's coin_grant). */
  coinGrant: string | null;
  aiCreditReward?: number | null;
  weightKg: string | null;
  lengthCm: string | null;
  widthCm: string | null;
  heightCm: string | null;
};
export type HouseVariant = {
  id: string;
  productId: string;
  sku: string;
  name: string;
  attributes: Record<string, string>;
  price: string;
  srp: string | null;
  /** BF4: flat per-currency prices (null = not sold in that currency). */
  priceGc: string | null;
  priceCoin: string | null;
  pv: string;
  active: boolean;
  sortOrder: number;
} & VariantOverrides;
export async function listHouseVariants(productId: string): Promise<HouseVariant[]> {
  const res = await api.get<{ variants: HouseVariant[] }>(`/products/${productId}/variants`);
  return res.data.variants;
}
export async function addHouseVariant(
  productId: string,
  body: {
    name: string;
    /** Blank → server derives `<PARENT>-<OPTION>` from the parent product's sku. */
    sku?: string;
    price: string;
    pv?: string;
    srp?: string | null;
    priceGc?: string | null;
    priceCoin?: string | null;
    attributes?: Record<string, string>;
  } & Partial<VariantOverrides>,
): Promise<HouseVariant> {
  const res = await api.post<HouseVariant>(`/products/${productId}/variants`, body);
  return res.data;
}
export async function updateHouseVariant(
  productId: string,
  variantId: string,
  body: Partial<
    {
      name: string;
      sku: string;
      price: string;
      pv: string;
      srp: string | null;
      priceGc: string | null;
      priceCoin: string | null;
      active: boolean;
    } & VariantOverrides
  >,
): Promise<HouseVariant> {
  const res = await api.patch<HouseVariant>(`/products/${productId}/variants/${variantId}`, body);
  return res.data;
}
export async function setHouseVariantStock(
  productId: string,
  variantId: string,
  branchId: string,
  qty: number,
) {
  return (await api.post(`/products/${productId}/variants/${variantId}/stock`, { branchId, qty }))
    .data;
}

// ── Variation types (admin-managed: Size, Color, …) ───────────────────────
export type VariationOption = {
  id: string;
  variationTypeId: string;
  code: string;
  label: string;
  sortOrder: number;
  active: boolean;
};
export type VariationType = {
  id: string;
  code: string;
  name: string;
  ownerMerchantId: string | null;
  active: boolean;
  sortOrder: number;
  options: VariationOption[];
};
export type ProductVariationSelection = { typeId: string; optionIds: string[] };

export async function listVariationTypes(includeInactive = false): Promise<VariationType[]> {
  const res = await api.get<{ types: VariationType[] }>('/variation-types', {
    params: includeInactive ? { includeInactive: true } : {},
  });
  return res.data.types;
}
export async function createVariationType(input: {
  code: string;
  name: string;
  sortOrder?: number;
}): Promise<VariationType> {
  return (await api.post<VariationType>('/variation-types', input)).data;
}
export async function updateVariationType(
  id: string,
  patch: Partial<{ name: string; sortOrder: number; active: boolean }>,
): Promise<VariationType> {
  return (await api.patch<VariationType>(`/variation-types/${id}`, patch)).data;
}
export async function deleteVariationType(id: string): Promise<void> {
  await api.delete(`/variation-types/${id}`);
}
export async function addVariationOption(
  typeId: string,
  input: { code: string; label: string; sortOrder?: number },
): Promise<VariationOption> {
  return (await api.post<VariationOption>(`/variation-types/${typeId}/options`, input)).data;
}
export async function updateVariationOption(
  optionId: string,
  patch: Partial<{ label: string; sortOrder: number; active: boolean }>,
): Promise<VariationOption> {
  return (await api.patch<VariationOption>(`/variation-options/${optionId}`, patch)).data;
}
export async function deleteVariationOption(optionId: string): Promise<void> {
  await api.delete(`/variation-options/${optionId}`);
}

export async function getProductVariations(
  productId: string,
): Promise<{ selections: ProductVariationSelection[]; variants: HouseVariant[] }> {
  return (await api.get(`/products/${productId}/variations`)).data;
}
export async function setProductVariations(
  productId: string,
  selections: ProductVariationSelection[],
): Promise<{ variants: HouseVariant[] }> {
  return (await api.put(`/products/${productId}/variations`, { selections })).data;
}

// ── Suppliers + purchase orders ───────────────────────────────────────────
export type Supplier = {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, string>;
  paymentTerms: string | null;
  /** Accounting v1: days from bill date to due date (AP aging). null = due on receipt. */
  paymentTermsDays: number | null;
  active: boolean;
};
export type SupplierInput = {
  code: string;
  name: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  paymentTerms?: string | null;
  paymentTermsDays?: number | null;
  active?: boolean;
};
export async function listSuppliers(activeOnly = false): Promise<Supplier[]> {
  const res = await api.get<{ suppliers: Supplier[] }>('/suppliers', {
    params: activeOnly ? { activeOnly: true } : {},
  });
  return res.data.suppliers;
}
export async function createSupplier(input: SupplierInput): Promise<Supplier> {
  return (await api.post<Supplier>('/suppliers', input)).data;
}
export async function updateSupplier(id: string, patch: Partial<SupplierInput>): Promise<Supplier> {
  return (await api.patch<Supplier>(`/suppliers/${id}`, patch)).data;
}

// ── R2: customer master + payment terms ─────────────────────────────────────

export type CustomerKind = 'member' | 'individual' | 'business';

export type CustomerRow = {
  id: string;
  code: string;
  kind: CustomerKind;
  userId: string | null;
  name: string;
  tradeName: string | null;
  tin: string | null;
  vatRegistered: boolean;
  email: string | null;
  phone: string | null;
  termsCode: string | null;
  creditLimit: string;
  active: boolean;
  createdAt: string;
};

export type CustomerDetail = CustomerRow & {
  businessStyle: string | null;
  notes: string | null;
  billingAddress: Record<string, string>;
  shippingAddress: Record<string, string>;
  openInvoiceCount: number;
  /** Unapplied receipts + posted credit memos: what the customer can still spend. */
  credit: string;
  outstanding: string;
};

export type CustomerInput = {
  kind: CustomerKind;
  userId?: string | null;
  name: string;
  tradeName?: string | null;
  tin?: string | null;
  vatRegistered?: boolean;
  businessStyle?: string | null;
  email?: string | null;
  phone?: string | null;
  termsCode?: string | null;
  creditLimit?: string | null;
  notes?: string | null;
};

export type CustomerDocumentRow = {
  kind: string;
  docNo: string;
  docDate: string;
  status: string;
  amount: string | null;
};

export async function listCustomers(
  params: {
    q?: string;
    kind?: CustomerKind;
    active?: boolean;
    limit?: number;
    offset?: number;
  } = {},
): Promise<{ rows: CustomerRow[]; total: number; limit: number; offset: number }> {
  const res = await api.get('/admin/customers', {
    params: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.kind ? { kind: params.kind } : {}),
      ...(params.active === undefined ? {} : { active: String(params.active) }),
      ...(params.limit ? { limit: params.limit } : {}),
      ...(params.offset ? { offset: params.offset } : {}),
    },
  });
  return res.data as { rows: CustomerRow[]; total: number; limit: number; offset: number };
}
export async function getCustomer(id: string): Promise<CustomerDetail> {
  return (await api.get<CustomerDetail>(`/admin/customers/${id}`)).data;
}
export async function createCustomer(input: CustomerInput): Promise<CustomerRow> {
  return (await api.post<CustomerRow>('/admin/customers', input)).data;
}
export async function updateCustomer(
  id: string,
  patch: Partial<Omit<CustomerInput, 'kind' | 'userId'>> & { active?: boolean },
): Promise<CustomerRow> {
  return (await api.patch<CustomerRow>(`/admin/customers/${id}`, patch)).data;
}
export async function setCustomerActive(id: string, active: boolean): Promise<CustomerRow> {
  return (await api.post<CustomerRow>(`/admin/customers/${id}/deactivate`, { active })).data;
}
export async function listCustomerDocuments(id: string): Promise<CustomerDocumentRow[]> {
  const res = await api.get<{ rows: CustomerDocumentRow[] }>(`/admin/customers/${id}/documents`);
  return res.data.rows;
}

// ── R2 Phase C: general AR billings ────────────────────────────────────────

export type ArInvoiceStatus = 'draft' | 'open' | 'partial' | 'paid' | 'void';

export type ArInvoiceRow = {
  id: string;
  docNo: string;
  status: ArInvoiceStatus;
  customerId: string;
  customerName: string;
  invoiceDate: string;
  dueDate: string | null;
  termsCode: string | null;
  reference: string | null;
  subtotal: string;
  discount: string;
  vatAmount: string;
  total: string;
  amountPaid: string;
  deductsInventory: boolean;
  branchId: string | null;
  postedAt: string | null;
  /** Derived server-side, never stored: due date past on a still-owing billing. */
  overdue: boolean;
};

export type ArInvoiceLineRow = {
  id: string;
  productId: string | null;
  variantId: string | null;
  description: string | null;
  qty: number;
  unitPrice: string;
  lineTotal: string;
  vatAmount: string;
  taxCode: string | null;
  incomeAccountCode: string | null;
  unitCost: string | null;
  sortOrder: number;
};

export type ArInvoiceDetail = {
  invoice: ArInvoiceRow & { memo: string | null; payload: ArInvoicePayload | null };
  customer: { id: string; code: string; name: string } | null;
  lines: ArInvoiceLineRow[];
};

/** The frozen print snapshot. Rendered verbatim — never recomputed. */
export type ArInvoicePayload = {
  version: 1;
  title: string;
  docNo: string;
  invoiceDate: string;
  dueDate: string | null;
  termsCode: string | null;
  reference: string | null;
  memo: string | null;
  issuedAt: string;
  company: {
    name: string | null;
    address: string | null;
    tin: string | null;
    phone: string | null;
    branchName: string | null;
  };
  customer: {
    name: string | null;
    tradeName: string | null;
    tin: string | null;
    vatRegistered: boolean;
    businessStyle: string | null;
    email: string | null;
    phone: string | null;
    addressLines: string[];
  };
  lines: Array<{
    name: string;
    sku: string | null;
    qty: number;
    unitPrice: string;
    lineTotal: string;
    taxCode: string | null;
    vat: string;
  }>;
  totals: {
    subtotal: string;
    discount: string;
    vat: string;
    vatable: string;
    exempt: string;
    zeroRated: string;
    untagged: string;
    total: string;
  };
  footer: string;
};

export type ArInvoiceLineInput = {
  productId?: string | null;
  variantId?: string | null;
  description?: string | null;
  qty: number;
  unitPrice: string;
  taxCode?: string | null;
  incomeAccountCode?: string | null;
};

export type ArInvoiceInput = {
  customerId: string;
  invoiceDate?: string;
  dueDate?: string | null;
  termsCode?: string | null;
  reference?: string | null;
  memo?: string | null;
  discount?: string | null;
  deductsInventory?: boolean;
  branchId?: string | null;
  lines: ArInvoiceLineInput[];
  clientRequestId?: string;
};

export async function listArInvoices(
  params: {
    status?: string;
    customerId?: string;
    q?: string;
  } = {},
): Promise<ArInvoiceRow[]> {
  const res = await api.get<{ rows: ArInvoiceRow[] }>('/admin/ar/invoices', { params });
  return res.data.rows;
}

export async function getArInvoice(id: string): Promise<ArInvoiceDetail> {
  return (await api.get<ArInvoiceDetail>(`/admin/ar/invoices/${id}`)).data;
}

export async function createArInvoice(input: ArInvoiceInput): Promise<ArInvoiceRow> {
  return (await api.post<{ invoice: ArInvoiceRow }>('/admin/ar/invoices', input)).data.invoice;
}

export async function updateArInvoice(
  id: string,
  input: Omit<ArInvoiceInput, 'customerId' | 'clientRequestId'>,
): Promise<ArInvoiceRow> {
  return (await api.patch<{ invoice: ArInvoiceRow }>(`/admin/ar/invoices/${id}`, input)).data
    .invoice;
}

export async function deleteArInvoiceDraft(id: string): Promise<void> {
  await api.delete(`/admin/ar/invoices/${id}`);
}

/** Posting can succeed WITH warnings (a credit limit in `warn` mode). */
export async function postArInvoice(
  id: string,
): Promise<{ invoice: ArInvoiceRow; warnings: string[] }> {
  return (
    await api.post<{ invoice: ArInvoiceRow; warnings: string[] }>(`/admin/ar/invoices/${id}/post`)
  ).data;
}

export async function voidArInvoice(id: string, reason: string): Promise<ArInvoiceRow> {
  return (await api.post<{ invoice: ArInvoiceRow }>(`/admin/ar/invoices/${id}/void`, { reason }))
    .data.invoice;
}

export async function getArInvoicePrint(id: string): Promise<
  {
    draft: boolean;
    payload?: ArInvoicePayload;
    /** Posted billings only: the live status, so a voided one reprints marked VOID. */
    status?: string;
    voidedAt?: string | null;
    voidReason?: string | null;
  } & Partial<ArInvoiceDetail>
> {
  return (await api.get(`/admin/ar/invoices/${id}/print`)).data;
}

// ── R2 Phase D: receipts, customer credit, refunds ──────────────────────────

export type ArReceiptRow = {
  id: string;
  docNo: string;
  customerId: string;
  amount: string;
  /** Money on this receipt not against any billing — the customer's credit. */
  amountUnapplied: string;
  kind: 'payment' | 'prepayment';
  method: string;
  status: 'posted' | 'void';
  receivedAt: string;
};

export type ArApplicationRow = {
  id: string;
  receiptId: string | null;
  /** R2 Phase E: set when the credit came from a credit memo instead. */
  creditMemoId: string | null;
  /** The PAY- or CM- document the money came from. */
  sourceDocNo: string | null;
  invoiceId: string;
  invoiceDocNo: string;
  amount: string;
  status: 'posted' | 'void';
  /** Written in the receipt's own transaction, or applied later. */
  inSourceTx: boolean;
  appliedAt: string;
};

export type ArCreditRefundRow = {
  id: string;
  docNo: string;
  customerId: string;
  receiptId: string | null;
  creditMemoId: string | null;
  sourceDocNo: string | null;
  amount: string;
  method: string;
  status: 'posted' | 'void';
  refundedAt: string;
};

export type ArReceiptDetail = {
  receipt: ArReceiptRow & { memo: string | null; voidReason: string | null };
  applications: Array<{
    id: string;
    invoiceId: string;
    docNo: string;
    kind: string;
    amount: string;
    status: 'posted' | 'void';
    inSourceTx: boolean;
    appliedAt: string;
  }>;
  refunds: Array<{
    id: string;
    docNo: string;
    amount: string;
    status: 'posted' | 'void';
    refundedAt: string;
  }>;
};

export async function listArReceipts(
  params: { customerId?: string; limit?: number } = {},
): Promise<ArReceiptRow[]> {
  return (await api.get<{ rows: ArReceiptRow[] }>('/admin/ar/receipts', { params })).data.rows;
}

export async function getArReceipt(id: string): Promise<ArReceiptDetail> {
  return (await api.get<ArReceiptDetail>(`/admin/ar/receipts/${id}`)).data;
}

export async function getCustomerCredit(customerId: string): Promise<string> {
  return (await api.get<{ credit: string }>(`/admin/ar/customers/${customerId}/credit`)).data
    .credit;
}

export type ArReceiptInput = {
  customerId: string;
  amount: string;
  method: string;
  reference?: string | null;
  /** Omit to spread the money oldest-first; a short list leaves credit. */
  allocations?: Array<{ invoiceId: string; amount: string }>;
  autoApply?: 'oldest_first' | 'none';
  memo?: string | null;
  branchId?: string | null;
  clientRequestId?: string | null;
  fundsAccountId?: string | null;
};

export async function createArReceipt(
  input: ArReceiptInput,
): Promise<{ receipt: ArReceiptRow; replayed: boolean }> {
  return (await api.post<{ receipt: ArReceiptRow; replayed: boolean }>('/admin/ar/receipts', input))
    .data;
}

export async function reallocateArReceipt(
  id: string,
  input: { allocations: Array<{ invoiceId: string; amount: string }>; reason: string },
): Promise<void> {
  await api.post(`/admin/ar/receipts/${id}/reallocate`, input);
}

export async function voidArReceipt(id: string, reason: string): Promise<void> {
  await api.post(`/admin/ar/receipts/${id}/void`, { reason });
}

export async function listArApplications(
  params: { receiptId?: string; creditMemoId?: string; invoiceId?: string; limit?: number } = {},
): Promise<ArApplicationRow[]> {
  return (await api.get<{ rows: ArApplicationRow[] }>('/admin/ar/applications', { params })).data
    .rows;
}

export async function applyArCredit(input: {
  receiptId: string;
  allocations: Array<{ invoiceId: string; amount: string }>;
}): Promise<{ applied: string; creditLeft: string }> {
  return (await api.post<{ applied: string; creditLeft: string }>('/admin/ar/applications', input))
    .data;
}

export async function unapplyArApplication(id: string, reason: string): Promise<void> {
  await api.post(`/admin/ar/applications/${id}/void`, { reason });
}

export async function listArCreditRefunds(
  params: { customerId?: string; creditMemoId?: string; limit?: number } = {},
): Promise<ArCreditRefundRow[]> {
  return (await api.get<{ rows: ArCreditRefundRow[] }>('/admin/ar/credit-refunds', { params })).data
    .rows;
}

export async function createArCreditRefund(input: {
  /** Exactly one source: a receipt's remainder or a posted credit memo's. */
  receiptId?: string | null;
  creditMemoId?: string | null;
  amount: string;
  method: string;
  fundsAccountId: string;
  reference?: string | null;
  memo?: string | null;
  branchId?: string | null;
  clientRequestId?: string | null;
}): Promise<{ refund: ArCreditRefundRow; replayed: boolean }> {
  return (
    await api.post<{ refund: ArCreditRefundRow; replayed: boolean }>(
      '/admin/ar/credit-refunds',
      input,
    )
  ).data;
}

export async function voidArCreditRefund(id: string, reason: string): Promise<void> {
  await api.post(`/admin/ar/credit-refunds/${id}/void`, { reason });
}

// ── R2 Phase E: standalone credit memos (CM-) ───────────────────────────────

export type CreditMemoStatus = 'draft' | 'open' | 'applied' | 'refunded' | 'void';

export type CreditMemoRow = {
  id: string;
  docNo: string;
  customerId: string;
  customerName: string;
  invoiceId: string | null;
  /** The billing it was raised against. */
  againstDocNo: string | null;
  status: CreditMemoStatus;
  memoDate: string;
  reason: string;
  reference: string | null;
  total: string;
  /** Credit still on the memo — spendable on billings or refundable. */
  amountUnapplied: string;
  restock: boolean;
};

export type CreditMemoLineRow = {
  id: string;
  productId: string | null;
  variantId: string | null;
  description: string | null;
  qty: number;
  unitPrice: string;
  lineTotal: string;
  vatAmount: string;
  taxCode: string | null;
  incomeAccountCode: string | null;
  unitCost: string | null;
};

export type CreditMemoPayload = {
  version: 1;
  title: 'Credit Memo';
  docNo: string;
  memoDate: string;
  reason: string;
  reference: string | null;
  memo: string | null;
  againstDocNo: string | null;
  issuedAt: string;
  company: ArInvoicePayload['company'];
  customer: ArInvoicePayload['customer'];
  lines: ArInvoicePayload['lines'];
  totals: {
    subtotal: string;
    vat: string;
    vatable: string;
    exempt: string;
    zeroRated: string;
    untagged: string;
    total: string;
  };
  footer: string;
};

export type CreditMemoDetail = {
  creditMemo: Omit<CreditMemoRow, 'customerName' | 'againstDocNo'> & {
    memo: string | null;
    subtotal: string;
    vatAmount: string;
    branchId: string | null;
    postedAt: string | null;
    voidedAt: string | null;
    voidReason: string | null;
    payload: CreditMemoPayload | null;
  };
  customer: { id: string; code: string; name: string } | null;
  againstDocNo: string | null;
  lines: CreditMemoLineRow[];
  applications: ArApplicationRow[];
  refunds: ArCreditRefundRow[];
};

export type CreditMemoInput = {
  customerId: string;
  invoiceId?: string | null;
  memoDate?: string;
  reason: string;
  reference?: string | null;
  memo?: string | null;
  restock?: boolean;
  branchId?: string | null;
  lines: ArInvoiceLineInput[];
  clientRequestId?: string;
};

export async function listCreditMemos(
  params: { status?: CreditMemoStatus; customerId?: string; q?: string } = {},
): Promise<CreditMemoRow[]> {
  return (await api.get<{ rows: CreditMemoRow[] }>('/admin/ar/credit-memos', { params })).data.rows;
}

export async function getCreditMemo(id: string): Promise<CreditMemoDetail> {
  return (await api.get<CreditMemoDetail>(`/admin/ar/credit-memos/${id}`)).data;
}

export async function createCreditMemo(input: CreditMemoInput): Promise<{ id: string }> {
  return (await api.post<{ creditMemo: { id: string } }>('/admin/ar/credit-memos', input)).data
    .creditMemo;
}

export async function updateCreditMemo(
  id: string,
  input: Omit<CreditMemoInput, 'customerId' | 'clientRequestId'>,
): Promise<void> {
  await api.patch(`/admin/ar/credit-memos/${id}`, input);
}

export async function deleteCreditMemoDraft(id: string): Promise<void> {
  await api.delete(`/admin/ar/credit-memos/${id}`);
}

export async function postCreditMemo(id: string): Promise<{ applied: string }> {
  return (await api.post<{ applied: string }>(`/admin/ar/credit-memos/${id}/post`)).data;
}

export async function voidCreditMemo(id: string, reason: string): Promise<void> {
  await api.post(`/admin/ar/credit-memos/${id}/void`, { reason });
}

export async function applyCreditMemo(
  id: string,
  allocations: Array<{ invoiceId: string; amount: string }>,
): Promise<{ applied: string; creditLeft: string }> {
  return (
    await api.post<{ applied: string; creditLeft: string }>(`/admin/ar/credit-memos/${id}/apply`, {
      allocations,
    })
  ).data;
}

export async function getCreditMemoPrint(id: string): Promise<{
  draft: boolean;
  status: CreditMemoStatus;
  voidedAt?: string | null;
  voidReason?: string | null;
  payload: CreditMemoPayload | null;
}> {
  return (await api.get(`/admin/ar/credit-memos/${id}/print`)).data;
}

export type PaymentTermRow = {
  code: string;
  name: string;
  days: number;
  kind: 'net' | 'eom';
  active: boolean;
  sortOrder: number;
};
export async function listPaymentTerms(includeInactive = false): Promise<PaymentTermRow[]> {
  const res = await api.get<{ rows: PaymentTermRow[] }>('/admin/payment-terms', {
    params: includeInactive ? { includeInactive: 'true' } : {},
  });
  return res.data.rows;
}
export async function upsertPaymentTerm(
  input: Omit<PaymentTermRow, 'sortOrder'> & { sortOrder?: number },
): Promise<PaymentTermRow> {
  return (await api.patch<PaymentTermRow>('/admin/payment-terms', input)).data;
}

export type ProductSupplierRow = {
  id?: string;
  variantId: string | null;
  supplierId: string;
  supplierName?: string;
  supplierCode?: string;
  unitCost: string;
  supplierSku: string | null;
  isPreferred: boolean;
  active: boolean;
};
export async function listProductSuppliers(productId: string): Promise<ProductSupplierRow[]> {
  return (await api.get<{ rows: ProductSupplierRow[] }>(`/products/${productId}/suppliers`)).data
    .rows;
}
export async function setProductSuppliers(
  productId: string,
  rows: Array<{
    variantId?: string | null;
    supplierId: string;
    unitCost: string;
    supplierSku?: string | null;
    isPreferred?: boolean;
    active?: boolean;
  }>,
): Promise<{ rows: ProductSupplierRow[] }> {
  return (await api.put(`/products/${productId}/suppliers`, { rows })).data;
}

export type SupplierCatalogItem = {
  productSupplierId: string;
  productId: string;
  productName: string;
  sku: string;
  variantId: string | null;
  variantName: string | null;
  unitCost: string;
};
export async function listSupplierCatalog(supplierId: string): Promise<SupplierCatalogItem[]> {
  return (await api.get<{ items: SupplierCatalogItem[] }>(`/suppliers/${supplierId}/catalog`)).data
    .items;
}

export type PurchaseOrderStatus = 'pending' | 'approved' | 'incomplete' | 'completed' | 'cancelled';
export type PurchaseOrderListRow = {
  id: string;
  poNumber: string;
  status: PurchaseOrderStatus;
  supplierId: string;
  supplierName: string;
  total: string;
  createdByName: string | null;
  createdAt: string;
  itemCount: number;
};
export type PurchaseOrderLine = {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  variantId: string | null;
  variantName: string | null;
  qtyOrdered: number;
  qtyFulfilled: number;
  /** Units sent back via Return-to-Supplier (RTS-); returnable = fulfilled − returned. */
  qtyReturned: number;
  unitCost: string;
  note: string | null;
};
export type PurchaseOrderFulfillment = {
  id: string;
  branchId: string;
  branchName: string;
  inventoryBatchId: string | null;
  fulfilledByName: string | null;
  note: string | null;
  createdAt: string;
  lines: Array<{ fulfillmentId: string; poLineId: string; qty: number }>;
};
export type PurchaseOrderDetail = {
  id: string;
  poNumber: string;
  status: PurchaseOrderStatus;
  supplierId: string;
  supplierName: string;
  memo: string | null;
  subtotal: string;
  total: string;
  createdByName: string | null;
  approvedAt: string | null;
  cancelledReason: string | null;
  createdAt: string;
  lines: PurchaseOrderLine[];
  fulfillments: PurchaseOrderFulfillment[];
};

export async function listPurchaseOrders(
  filter: {
    status?: PurchaseOrderStatus;
    supplierId?: string;
  } = {},
): Promise<PurchaseOrderListRow[]> {
  return (await api.get<{ rows: PurchaseOrderListRow[] }>('/purchase-orders', { params: filter }))
    .data.rows;
}
export async function getPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
  return (await api.get<PurchaseOrderDetail>(`/purchase-orders/${id}`)).data;
}
export async function createPurchaseOrder(input: {
  supplierId: string;
  memo?: string | null;
  lines: Array<{
    productId: string;
    variantId?: string | null;
    qtyOrdered: number;
    unitCost?: string | null;
    note?: string | null;
  }>;
}): Promise<{ id: string; poNumber: string }> {
  return (await api.post('/purchase-orders', input)).data;
}
export async function approvePurchaseOrder(id: string): Promise<PurchaseOrderListRow> {
  return (await api.post(`/purchase-orders/${id}/approve`, {})).data;
}
export async function cancelPurchaseOrder(
  id: string,
  reason: string,
): Promise<PurchaseOrderListRow> {
  return (await api.post(`/purchase-orders/${id}/cancel`, { reason })).data;
}
export async function refillPurchaseOrder(
  id: string,
  body: {
    branchId: string;
    note?: string | null;
    /** `expiresOn` (YYYY-MM-DD) and `lotNo` only apply while stock lots are on. */
    lines: Array<{ poLineId: string; qty: number; expiresOn?: string; lotNo?: string }>;
  },
): Promise<{ status: PurchaseOrderStatus; refNumber: string }> {
  return (await api.post(`/purchase-orders/${id}/refill`, body)).data;
}

export type KitComponentRow = {
  componentProductId: string;
  /** Names one VARIATION of the component; null = the whole product. */
  variantId: string | null;
  qtyPerKit: number;
  sku: string;
  name: string;
  /** Display only — the picker searches ACTIVE products, so this is the only
   *  signal that an already-saved component has since been archived. */
  active: boolean;
  /** Display only — present when `variantId` is set. */
  variantSku: string | null;
  variantName: string | null;
};

export async function getKitComponents(productId: string): Promise<KitComponentRow[]> {
  const res = await api.get<{ components: KitComponentRow[] }>(`/products/${productId}/components`);
  return res.data.components;
}

export async function setKitComponents(
  productId: string,
  components: Array<{ componentProductId: string; variantId?: string | null; qtyPerKit: number }>,
): Promise<void> {
  await api.put(`/products/${productId}/components`, { components });
}

/**
 * One row of a product's RECIPE: a material one sold unit uses up. `variantId`
 * scopes the row to one variation of the product (null = every size without
 * rows of its own). The joined columns are display-only.
 */
export type RecipeRow = {
  materialProductId: string;
  variantId: string | null;
  variantName: string | null;
  variantSku: string | null;
  qtyPerUnit: number;
  sku: string;
  name: string;
  unitLabel: string | null;
  active: boolean;
};

export async function getProductRecipe(productId: string): Promise<RecipeRow[]> {
  const res = await api.get<{ materials: RecipeRow[] }>(`/products/${productId}/materials`);
  return res.data.materials;
}

export async function setProductRecipe(
  productId: string,
  materials: Array<{ materialProductId: string; variantId: string | null; qtyPerUnit: number }>,
): Promise<void> {
  await api.put(`/products/${productId}/materials`, { materials });
}

/**
 * Per-product absolute segment pricing (membership tier / rank / stockist).
 * Each entry is the final cash price for that segment; a missing entry = base
 * price. Replaces the global Discount tab.
 */
export type ProductPricing = {
  membership: Array<{ membershipId: string; price: string }>;
  rank: Array<{ rankCode: string; price: string }>;
  stockist: Array<{ branchId: string; price: string }>;
  /** Single absolute price for non-activated buyers (null = no override). */
  nonActivated: string | null;
};

export async function getProductPricing(productId: string): Promise<ProductPricing> {
  const res = await api.get<ProductPricing>(`/products/${productId}/pricing`);
  return res.data;
}

/**
 * Package prerequisite: the membership tier(s) a buyer's active slot must hold
 * for this product to be visible/sellable. Empty = available to all.
 */
export async function getProductMembershipRequirements(productId: string): Promise<string[]> {
  const res = await api.get<{ membershipIds: string[] }>(
    `/products/${productId}/membership-requirements`,
  );
  return res.data.membershipIds;
}

export async function saveProductMembershipRequirements(
  productId: string,
  membershipIds: string[],
): Promise<void> {
  await api.put(`/products/${productId}/membership-requirements`, { membershipIds });
}

export async function saveProductPricing(productId: string, body: ProductPricing): Promise<void> {
  await api.put(`/products/${productId}/pricing`, body);
}

/**
 * Per-VARIANT absolute segment pricing — same shape as ProductPricing but scoped
 * to one variant. Overrides the product cascade discount (lowest applicable wins).
 */
export type VariantPricing = ProductPricing;

export async function getVariantPricing(
  productId: string,
  variantId: string,
): Promise<VariantPricing> {
  const res = await api.get<VariantPricing>(`/products/${productId}/variants/${variantId}/pricing`);
  return res.data;
}

export async function saveVariantPricing(
  productId: string,
  variantId: string,
  body: VariantPricing,
): Promise<void> {
  await api.put(`/products/${productId}/variants/${variantId}/pricing`, body);
}

/** Public catalog image URL — the API 302-redirects to a presigned object URL. */
export function productImageUrl(productId: string, idx: number): string {
  return `${BASE_URL}/products/${productId}/image/${idx}`;
}

export async function uploadProductImage(id: string, file: File): Promise<{ images: string[] }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ images: string[] }>(`/products/${id}/images`, form);
  return res.data;
}

export async function deleteProductImage(id: string, key: string): Promise<{ images: string[] }> {
  const res = await api.delete<{ images: string[] }>(`/products/${id}/images`, { data: { key } });
  return res.data;
}

/**
 * Active membership tiers only — for product/pricing/comp-plan/slot pickers, so
 * archived tiers never appear. The Memberships management page uses
 * `listAllMemberships()` instead to manage the archive.
 */
export async function listMemberships(): Promise<Membership[]> {
  const res = await api.get<Membership[]>('/memberships');
  return res.data;
}

/** Full membership list INCLUDING archived — for the Memberships CRUD page only. */
export async function listAllMemberships(): Promise<Membership[]> {
  const res = await api.get<Membership[]>('/admin/memberships');
  return res.data;
}

export async function createMembership(input: {
  code: string;
  name: string;
  config?: Record<string, unknown>;
}): Promise<Membership> {
  const res = await api.post<Membership>('/memberships', input);
  return res.data;
}

export async function updateMembership(
  id: string,
  patch: Partial<{
    name: string;
    discountPct: string;
    active: number;
    config: Record<string, unknown>;
  }>,
): Promise<Membership> {
  const res = await api.patch<Membership>(`/memberships/${id}`, patch);
  return res.data;
}

// Shared with the cashier tape and the receipt renderer in @mlm/ui — one
// definition, so a branch's header can't mean two different things.
export type { ReceiptHeader };

export type Branch = {
  id: string;
  code: string;
  name: string;
  type: string;
  address: Record<string, string | undefined>;
  defaultDiscountPct: string;
  active: string;
  ecommerceReady: boolean;
  shippingOrigin: string | null;
  // Order-by-Area: courier zone ids this branch/stockist fulfills + the single
  // designated Main (fallback) branch.
  serviceAreas: string[];
  isMain: boolean;
  completionMode: 'auto' | 'manual';
  receiptHeader: ReceiptHeader;
  /** Which optional blocks the tape prints. Sparse — merge via `resolveReceiptFields`. */
  receiptFields: Partial<ReceiptFieldFlags>;
  /** Thermal roll width in mm (58 or 80). */
  receiptPaperWidth: number;
  ownerUserId: string | null;
  // The SLOT that owns this stockist branch + its resolved code (for the picker).
  ownerSlotId: string | null;
  ownerSlotCode: string | null;
  // Per-branch cashier-discount policy: enable + caps. Above either cap
  // requires a manager PIN at checkout. Branch-scoped so different stores
  // can give different managers different override authority.
  // `cashierDiscountMode` is the policy (off / free / presets_only);
  // `cashierDiscountEnabled` is the boolean it replaced, kept in step by the
  // server (true only for `free`).
  cashierDiscountMode?: CashierDiscountMode;
  cashierDiscountEnabled: boolean;
  cashierDiscountMaxPct: string;
  cashierDiscountMaxAmount: string;
  // Per-branch order-number series (e.g. prefix "MB" → MB0001).
  orderPrefix: string | null;
  orderSeqStart: number;
  orderNextSeq: number;
  orderSeqPad: number;
  createdAt: string;
};

/** Active-member autocomplete result; shared by admin pickers. */
export type AdminUserSearchResult = {
  id: string;
  fullName: string | null;
  email: string;
  referralCode: string | null;
};

/** Backwards-compatible alias for callers that imported the old name. */
export type AdminUserSearchRow = AdminUserSearchResult;

/**
 * Server-side ILIKE on name/email/referral_code, ACTIVE members only.
 * Used by MemberPicker (Default Sponsor in Settings) and the Branch Owner
 * picker. Empty query short-circuits to no results — keeps the dropdown
 * empty until the user types.
 */
export async function searchAdminUsers(q: string, limit = 10): Promise<AdminUserSearchResult[]> {
  if (q.trim().length < 1) return [];
  const res = await api.get<AdminUserSearchResult[]>('/users/search', {
    params: { q: q.trim(), limit },
  });
  return res.data;
}

export async function listBranches(): Promise<Branch[]> {
  const res = await api.get<Branch[]>('/branches');
  return res.data;
}

/** Order-by-Area: per-stockist receivable balances (Main owes each stockist). */
export type StockistReceivable = {
  branchId: string;
  branchCode: string;
  branchName: string;
  receivable: string | null;
  lifetime: string | null;
  updatedAt: string | null;
};
export async function listStockistReceivables(): Promise<StockistReceivable[]> {
  const res = await api.get<{ stockists: StockistReceivable[] }>('/stockist-receivables');
  return res.data.stockists;
}

export type StockistLedgerEntry = {
  id: string;
  kind: string;
  amount: string;
  currency: string;
  direction: 'C' | 'D';
  metadata: Record<string, unknown>;
  createdAt: string;
  orderCode: string | null;
};
export async function getStockistLedger(branchId: string): Promise<StockistLedgerEntry[]> {
  const res = await api.get<{ entries: StockistLedgerEntry[] }>(
    `/stockist-receivables/${branchId}/ledger`,
  );
  return res.data.entries;
}

export async function createBranch(input: {
  code: string;
  name: string;
  type?: 'branch' | 'stockist';
  defaultDiscountPct?: string;
  address?: Record<string, string>;
}): Promise<Branch> {
  const res = await api.post<Branch>('/branches', input);
  return res.data;
}

export async function updateBranch(
  id: string,
  input: {
    ecommerceReady?: boolean;
    shippingOrigin?: string | null;
    active?: string;
    defaultDiscountPct?: string;
    completionMode?: 'auto' | 'manual';
    receiptHeader?: ReceiptHeader;
    receiptFields?: Partial<ReceiptFieldFlags>;
    receiptPaperWidth?: 58 | 80;
    ownerUserId?: string | null;
    /** Tag the stockist owner by slot_code (null clears). Resolved server-side. */
    ownerSlotCode?: string | null;
    cashierDiscountMode?: CashierDiscountMode;
    cashierDiscountEnabled?: boolean;
    cashierDiscountMaxPct?: string;
    cashierDiscountMaxAmount?: string;
    orderPrefix?: string | null;
    orderSeqStart?: number;
    orderSeqPad?: number;
    allowRewind?: boolean;
    serviceAreas?: string[];
    isMain?: boolean;
  },
): Promise<Branch> {
  const res = await api.patch<Branch>(`/branches/${id}`, input);
  return res.data;
}

export type InventoryVariantRow = {
  variantId: string;
  name: string;
  sku: string;
  qty: number;
  /** The variation's own static cost, when it has one. */
  cost: string | null;
};

export type InventoryRow = {
  productId: string;
  sku: string;
  name: string;
  active: boolean;
  qty: number;
  /** Per-branch on-hand for each of the product's variations (empty = no variants). */
  variants: InventoryVariantRow[];
  isMaterial: boolean;
  /** Display unit of a stock quantity ("pcs", "g", "ml"). Null = pieces. */
  unitLabel: string | null;
  /** Units per purchase pack, for receiving by the pack. Null = none. */
  packSize: number | null;
  shelfLifeDays: number | null;
  /** Static per-unit cost (the default cost of a new stock lot). */
  cost: string | null;
  /** Item locator at this branch. */
  locationAisle: string | null;
  locationRack: string | null;
  /** Stock lots: soonest expiry among the open lots, and how many are open. */
  nearestExpiresOn: string | null;
  openLots: number;
};

export async function listBranchInventory(
  branchId: string,
  search?: string,
  status?: 'active' | 'archived' | 'all',
): Promise<InventoryRow[]> {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (status && status !== 'active') params.status = status;
  const res = await api.get<InventoryRow[]>(`/branches/${branchId}/inventory`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return res.data;
}

export async function receiveBranchInventory(
  branchId: string,
  args: { productId: string; qty: number; note?: string },
): Promise<{ qty: number }> {
  const res = await api.post<{ qty: number }>(`/branches/${branchId}/inventory/receive`, args);
  return res.data;
}

export async function writeOffBranchInventory(
  branchId: string,
  args: { productId: string; qty: number; reason: string },
): Promise<{ qty: number }> {
  const res = await api.post<{ qty: number }>(`/branches/${branchId}/inventory/write-off`, args);
  return res.data;
}

export type InventoryMovement = {
  id: string;
  productId: string;
  productSku: string;
  productName: string;
  variantId: string | null;
  variantName: string | null;
  variantSku: string | null;
  delta: number;
  reason: string;
  balanceBefore: number;
  balanceAfter: number;
  refType: string | null;
  refId: string | null;
  actorUserId: string | null;
  /** Resolved from users at read time; null for system movements. */
  actorName: string | null;
  actorEmail: string | null;
  note: string | null;
  createdAt: string;
};

export async function listInventoryMovements(
  branchId: string,
  params: { productId?: string; reason?: string } = {},
): Promise<InventoryMovement[]> {
  const res = await api.get<InventoryMovement[]>(`/branches/${branchId}/inventory/movements`, {
    params,
  });
  return res.data;
}

// ── Inventory batches (document model: receive shipment / write-off report) ──

export type InventoryBatchKind = 'receive' | 'write_off';

export type InventoryBatchLineInput = {
  productId: string;
  /** Target a specific variation's stock (omit for a product-level line). */
  variantId?: string;
  qty: number;
  note?: string;
  /** Stock lots, receive lines: per-unit cost, expiry (YYYY-MM-DD), lot number. */
  unitCost?: string;
  expiresOn?: string;
  lotNo?: string;
  /** Stock lots, write-off lines: take the units from this one lot. */
  lotId?: string;
};

export type ExpiryBucket = 'expired' | 'within_7' | 'within_30' | 'later' | 'none';

export type StockLot = {
  id: string;
  branchId: string;
  branchName: string;
  productId: string;
  sku: string;
  productName: string;
  unitLabel: string | null;
  variantId: string | null;
  variantName: string | null;
  lotNo: string | null;
  receivedAt: string;
  expiresOn: string | null;
  /** Whole days until expiry in tenant time; negative once past. */
  daysLeft: number | null;
  bucket: ExpiryBucket;
  unitCost: string | null;
  qtyReceived: number;
  qtyRemaining: number;
  locationAisle: string | null;
  locationRack: string | null;
};

export type StockLotList = { today: string; lots: StockLot[] };

export async function listBranchLots(
  branchId: string,
  params: { productId?: string; variantId?: string; includeEmpty?: boolean } = {},
): Promise<StockLotList> {
  const res = await api.get<StockLotList>(`/branches/${branchId}/inventory/lots`, {
    params: {
      productId: params.productId,
      variantId: params.variantId,
      includeEmpty: params.includeEmpty ? 'true' : undefined,
    },
  });
  return res.data;
}

export async function listExpiringLots(
  params: { branchId?: string; withinDays?: number } = {},
): Promise<StockLotList> {
  const res = await api.get<StockLotList>('/inventory/lots/expiring', { params });
  return res.data;
}

export async function setInventoryLocation(
  branchId: string,
  productId: string,
  args: { aisle: string | null; rack: string | null },
): Promise<{ aisle: string | null; rack: string | null }> {
  const res = await api.put<{ aisle: string | null; rack: string | null }>(
    `/branches/${branchId}/inventory/${productId}/location`,
    args,
  );
  return res.data;
}

export type OrderStockUsage = {
  movementId: string;
  productId: string;
  sku: string;
  productName: string;
  unitLabel: string | null;
  variantName: string | null;
  isMaterial: boolean;
  reason: string;
  delta: number;
  note: string | null;
  createdAt: string;
  cost: string | null;
  lots: Array<{
    lotId: string | null;
    lotNo: string | null;
    expiresOn: string | null;
    qty: number;
    restoredQty: number;
    unitCost: string | null;
  }>;
};

export async function getOrderStockUsage(orderId: string): Promise<OrderStockUsage[]> {
  const res = await api.get<OrderStockUsage[]>(`/admin/orders/${orderId}/stock-usage`);
  return res.data;
}

export type InventoryBatchSummary = {
  id: string;
  refNumber: string;
  kind: InventoryBatchKind;
  memo: string | null;
  reason: string | null;
  lineCount: number;
  unitCount: number;
  createdAt: string;
};

export type InventoryBatchListRow = InventoryBatchSummary & {
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
};

export type InventoryBatchLineRow = {
  movementId: string;
  productId: string;
  productSku: string;
  productName: string;
  variantId: string | null;
  variantName: string | null;
  variantSku: string | null;
  delta: number;
  balanceBefore: number;
  balanceAfter: number;
  note: string | null;
};

export type InventoryBatchDetail = InventoryBatchListRow & {
  branchId: string;
  branchCode: string;
  branchName: string;
  lines: InventoryBatchLineRow[];
};

export const WRITE_OFF_REASONS = [
  { code: 'damaged', label: 'Damaged' },
  { code: 'expired', label: 'Expired' },
  { code: 'audit_shrinkage', label: 'Audit shrinkage' },
  { code: 'theft', label: 'Theft' },
  { code: 'other', label: 'Other' },
] as const;

export type WriteOffReason = (typeof WRITE_OFF_REASONS)[number]['code'];

export async function receiveInventoryBatch(
  branchId: string,
  args: { lines: InventoryBatchLineInput[]; memo?: string },
): Promise<InventoryBatchSummary> {
  const res = await api.post<InventoryBatchSummary>(
    `/branches/${branchId}/inventory/receive-batch`,
    args,
  );
  return res.data;
}

export async function writeOffInventoryBatch(
  branchId: string,
  args: { lines: InventoryBatchLineInput[]; reason: WriteOffReason; memo?: string },
): Promise<InventoryBatchSummary> {
  const res = await api.post<InventoryBatchSummary>(
    `/branches/${branchId}/inventory/writeoff-batch`,
    args,
  );
  return res.data;
}

export async function listInventoryBatches(
  branchId: string,
  params: { kind?: InventoryBatchKind; limit?: number } = {},
): Promise<InventoryBatchListRow[]> {
  const res = await api.get<InventoryBatchListRow[]>(`/branches/${branchId}/inventory/batches`, {
    params,
  });
  return res.data;
}

export async function getInventoryBatch(
  branchId: string,
  batchId: string,
): Promise<InventoryBatchDetail> {
  const res = await api.get<InventoryBatchDetail>(
    `/branches/${branchId}/inventory/batches/${batchId}`,
  );
  return res.data;
}

export type BranchSellable = { mode: 'all' | 'subset'; productIds: string[]; variantIds: string[] };

export async function getBranchSellable(branchId: string): Promise<BranchSellable> {
  const res = await api.get<BranchSellable>(`/branches/${branchId}/sellable`);
  return res.data;
}

export async function putBranchSellable(
  branchId: string,
  // Each list is rewritten only when present; a bare `{ mode }` preserves both.
  body: { mode: 'all' | 'subset'; productIds?: string[]; variantIds?: string[] },
): Promise<BranchSellable> {
  const res = await api.put<BranchSellable>(`/branches/${branchId}/sellable`, body);
  return res.data;
}

export async function patchBranchSellableProduct(
  branchId: string,
  productId: string,
  sellable: boolean,
): Promise<{ branchId: string; productId: string; sellable: boolean }> {
  const res = await api.patch<{ branchId: string; productId: string; sellable: boolean }>(
    `/branches/${branchId}/sellable/${productId}`,
    { sellable },
  );
  return res.data;
}

export async function patchBranchSellableVariant(
  branchId: string,
  variantId: string,
  sellable: boolean,
): Promise<{ branchId: string; variantId: string; sellable: boolean }> {
  const res = await api.patch<{ branchId: string; variantId: string; sellable: boolean }>(
    `/branches/${branchId}/sellable/variants/${variantId}`,
    { sellable },
  );
  return res.data;
}

export type BranchCashier = {
  id: string;
  email: string;
  fullName: string | null;
  // Name parts + phone prefill the Edit dialog (server returns them since the
  // POS-staff edit feature; older API builds omit them).
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  status: 'active' | 'pending' | 'suspended';
  totpEnabled: boolean;
  createdAt: string;
  // QoL Slice 5b — either 'cashier' or 'manager'. The branch staff panel
  // renders a chip + lets the create form pick one or the other.
  role: 'cashier' | 'manager';
  // True when this user has a POS manager PIN configured. Drives the
  // inline Set/Replace/Clear PIN action on manager rows in the Branch
  // staff list.
  posManagerPinSet: boolean;
};

export async function listBranchCashiers(branchId: string): Promise<BranchCashier[]> {
  const res = await api.get<BranchCashier[]>(`/branches/${branchId}/cashiers`);
  return res.data;
}

export async function createBranchCashier(
  branchId: string,
  body: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    // QoL Slice 5b — pick which staff role to provision under this branch.
    // Defaults to 'cashier' for backward compatibility.
    role?: 'cashier' | 'manager';
  },
): Promise<BranchCashier & { branchId: string }> {
  const res = await api.post<BranchCashier & { branchId: string }>(
    `/branches/${branchId}/cashiers`,
    body,
  );
  return res.data;
}

export async function patchBranchCashierStatus(
  branchId: string,
  userId: string,
  status: 'active' | 'suspended',
): Promise<{ id: string; status: string }> {
  const res = await api.patch<{ id: string; status: string }>(
    `/branches/${branchId}/cashiers/${userId}`,
    { status },
  );
  return res.data;
}

/** POS-staff Edit dialog: name/phone/email/role through the same branch-scoped PATCH. */
export async function patchBranchCashier(
  branchId: string,
  userId: string,
  patch: {
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    email?: string;
    role?: 'cashier' | 'manager';
  },
): Promise<{ id: string; email: string; fullName: string | null; role: 'cashier' | 'manager' }> {
  const res = await api.patch<{
    id: string;
    email: string;
    fullName: string | null;
    role: 'cashier' | 'manager';
  }>(`/branches/${branchId}/cashiers/${userId}`, patch);
  return res.data;
}

/** Admin sets an exact password for a POS staffer (no forced change on login). */
export async function setBranchCashierPassword(
  branchId: string,
  userId: string,
  password: string,
): Promise<void> {
  await api.post(`/branches/${branchId}/cashiers/${userId}/password`, { password });
}

export type AdminSlotRow = {
  id: string;
  slotCode: string;
  userId: string;
  userEmail: string;
  userFullName: string | null;
  sponsorSlotId: string | null;
  membershipCode: string;
  membershipName: string;
  status: string;
  isCompany: boolean;
  rankShowcaseHidden: boolean;
  activatedAt: string | null;
  createdAt: string;
  walletAvailable: string | null;
  lifetimeEarned: string | null;
  sponsorSlotCode: string | null;
  sponsorFullName: string | null;
  sponsorEmail: string | null;
  // The REPLICATED-LINK sponsor: the slot the owner's signup `?ref=` resolved
  // to. Different question from the genealogy sponsor above — the two diverge
  // on an admin override, a typed sponsorCode at redeem, or a 2nd+ slot.
  linkSponsorSlotCode: string | null;
  linkSponsorFullName: string | null;
  linkSponsorEmail: string | null;
  rankCode: string | null;
};

export async function adminListSlots(params: {
  page?: number;
  pageSize?: number;
  status?: 'pending_payment' | 'active' | 'suspended' | 'cancelled';
  membershipCode?: string;
  /** Fuzzy substring search over email/name/slot code. For "the slots THIS
   * member owns", pass `userId` instead. */
  ownerSearch?: string;
  /** Exact owner filter (user uuid). */
  userId?: string;
}): Promise<{
  rows: AdminSlotRow[];
  total: number;
  page: number;
  pageSize: number;
  stairstepActive: boolean;
}> {
  const res = await api.get('/admin/slots', { params });
  return res.data;
}

export type AdminCreateSlotInput = {
  ownerEmail?: string;
  ownerId?: string;
  sponsorCode: string;
  membershipCode: string;
  preferredBinaryPosition?: 'L' | 'R';
  placement?: { parentSlotId: string; position: 'L' | 'R' };
  activateNow?: boolean;
};

export type AdminCreateSlotResult = {
  slotId: string;
  slotCode: string;
  userId: string;
  sponsorSlotId: string | null;
  status: string;
};

export async function adminCreateSlot(input: AdminCreateSlotInput): Promise<AdminCreateSlotResult> {
  const res = await api.post<AdminCreateSlotResult>('/admin/slots', input);
  return res.data;
}

// ── Genealogy ──────────────────────────────────────────────────────────
export type AdminGenealogyNode = {
  slotId: string;
  slotCode: string;
  userFullName: string | null;
  userEmail: string | null;
  depth: number;
  parentSlotId: string | null;
  position?: 'L' | 'R' | null;
  membershipCode: string | null;
  membershipName: string | null;
  packageAmount: string | null;
  descendantCount: number;
  childCount: number;
  rankCode: string | null;
  // Binary-only node fields for the configurable genealogy card.
  leftPv?: string | null;
  rightPv?: string | null;
  sponsorSlotCode?: string | null;
  sponsorName?: string | null;
};

export type AdminGenealogyResponse = {
  view: 'unilevel' | 'binary';
  root: string;
  depth: number;
  currency: string;
  nodes: AdminGenealogyNode[];
};

export async function adminGetGenealogy(args: {
  slotId: string;
  view: 'unilevel' | 'binary';
  depth?: number;
}): Promise<AdminGenealogyResponse> {
  const res = await api.get<AdminGenealogyResponse>(`/slots/${args.slotId}/genealogy`, {
    params: { view: args.view, depth: args.depth ?? 5 },
  });
  return res.data;
}

export type AdminGenealogySearchMatch = {
  slotId: string;
  slotCode: string;
  userId: string;
  userFullName: string | null;
  userEmail: string | null;
  depth: number;
  pathSlotIds: string[];
};

export async function adminSearchGenealogy(args: {
  slotId: string;
  view: 'unilevel' | 'binary';
  q: string;
}): Promise<{ view: 'unilevel' | 'binary'; root: string; matches: AdminGenealogySearchMatch[] }> {
  const res = await api.get<{
    view: 'unilevel' | 'binary';
    root: string;
    matches: AdminGenealogySearchMatch[];
  }>(`/slots/${args.slotId}/genealogy/search`, {
    params: { view: args.view, q: args.q },
  });
  return res.data;
}

// ── Slot placement ────────────────────────────────────────────────────
export type UnplacedSlot = {
  slotId: string;
  slotCode: string;
  userId: string;
  userEmail: string | null;
  userFullName: string | null;
  status: string;
  membershipCode: string | null;
  membershipName: string | null;
  packageAmount: string | null;
};

export type UnplacedDownlineResponse = {
  parentSlotId: string;
  nodes: UnplacedSlot[];
};

/**
 * Unplaced DIRECT downlines of a parent slot — the only slots placeable into
 * that parent's empty L/R seat.
 */
export async function getUnplacedDownline(parentSlotId: string): Promise<UnplacedDownlineResponse> {
  const res = await api.get<UnplacedDownlineResponse>(`/slots/${parentSlotId}/unplaced-downline`);
  return res.data;
}

export type PlaceSlotInput = { parentSlotId: string; position: 'L' | 'R' };
export type PlaceSlotResult = {
  slotId: string;
  parentSlotId: string;
  position: 'L' | 'R';
};

export async function placeSlot(slotId: string, input: PlaceSlotInput): Promise<PlaceSlotResult> {
  const res = await api.post<PlaceSlotResult>(`/slots/${slotId}/place`, input);
  return res.data;
}

// ── Phase 13.5: Distribute wizard state machine ─────────────────────────
// `computing` / `distributing` are TRANSIENT: a background job owns the row
// and flips it to the terminal state — poll `getDistState` while in-flight.
export type DistWizardStatus =
  | 'pending'
  | 'activated'
  | 'loaded'
  | 'computing'
  | 'computed'
  | 'distributing'
  | 'distributed';

export type DistWizardState = {
  periodId: string;
  planCode: string;
  status: DistWizardStatus;
  activatedAt: string | null;
  loadedAt: string | null;
  computedAt: string | null;
  distributedAt: string | null;
  snapshot: Record<string, unknown>;
  updatedAt: string | null;
};

export async function getDistState(periodId: string, plan: string): Promise<DistWizardState> {
  const res = await api.get<DistWizardState>(`/admin/periods/${periodId}/distribute/${plan}/state`);
  return res.data;
}

export async function distActivate(periodId: string, plan: string) {
  const res = await api.post<DistWizardState>(
    `/admin/periods/${periodId}/distribute/${plan}/activate`,
  );
  return res.data;
}
export async function distLoad(periodId: string, plan: string) {
  const res = await api.post<DistWizardState>(`/admin/periods/${periodId}/distribute/${plan}/load`);
  return res.data;
}
export async function distCompute(periodId: string, plan: string) {
  const res = await api.post<DistWizardState>(
    `/admin/periods/${periodId}/distribute/${plan}/compute`,
  );
  return res.data;
}

export async function createCustomPeriod(args: { startAt: string; endAt: string; code?: string }) {
  const res = await api.post('/admin/periods', args);
  return res.data as {
    id: string;
    code: string;
    startAt: string;
    endAt: string;
    status: string;
  };
}

// ── Per-plan ad-hoc distribution runs (Phase 1: G15) ────────────────────────

export type DistributionRun = {
  id: string;
  planCode: string;
  startAt: string;
  endAt: string;
  // `computing` / `distributing` are transient background-job states — poll
  // `getDistributionRun` until terminal (`computed` / `distributed` / `failed`).
  status: 'pending' | 'computing' | 'computed' | 'distributing' | 'distributed' | 'failed';
  eventId: string;
  label: string | null;
  /** Admin-entered runtime knobs (Binary/Teslab: `pairing_pct`, `mentor_pct`). */
  params: Record<string, number>;
  snapshot: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  distributedAt: string | null;
};

/** Per-recipient computation breakdown carried on a distribution-run entry's
 *  metadata. Fields are plan-specific; the UI renders by the row's `kind`. */
export type DistributionBreakdown = {
  // G15 (monthly_direct_bonus)
  breakdown?: Record<string, { count: number; points: number; bonus: string }>;
  total_directs?: number;
  // Mentor rider rows
  from_sponsors?: string[];
  mentor_bonus_percentage_source?: string;
  // Binary (Teslab) pairing
  pairing_pct?: string;
  paired_points?: string;
  pv_consumed?: string;
  cap_flushed?: string;
  flushed_pv?: string;
  // Stairstep
  rank?: string;
  rank_pct?: number;
  personal_pv?: string;
  group_pv?: string;
  // Stairstep per-descendant breakdown (one row per contributing leg, plus a
  // `self` row). `path_max_pct` is the highest downline rank % that "passed
  // through" on the path to this node — the amount subtracted from the payer's
  // rate (compression). `slot_code` is resolved server-side for display.
  legs?: Array<{
    slot_id: string;
    slot_code?: string | null;
    self?: boolean;
    rank: string | null;
    pct: number;
    path_max_pct: number;
    ppv: string;
    amount: string;
    // Same-rank royalty leg: pays `royalty_pct × group PV` on an equal-rank
    // breakaway instead of the (zero) differential. `group_ppv` is the leg's
    // whole-subtree PV used as the royalty base.
    royalty?: true;
    royalty_pct?: number;
    group_ppv?: string;
  }>;
  // Maintenance cashback
  ppv?: string;
  percentage?: number;
  min_ppv?: number | string;
  // Unilevel consecutive
  streak_months?: number;
  threshold?: number | string;
  [k: string]: unknown;
};

/** A distributed run whose window overlaps another for the same plan. */
export type OverlappingRun = {
  id: string;
  label: string | null;
  startAt: string;
  endAt: string;
  distributedAt: string | null;
};

/** An older run for the same plan that hasn't paid yet (blocks a binary run). */
export type OlderOpenRun = {
  id: string;
  label: string | null;
  startAt: string;
  endAt: string;
  status: DistributionRun['status'];
};

export type DistributionRunPreview = {
  recipients: Array<{
    slotId: string;
    slotCode: string | null;
    ownerName: string | null;
    kind: string;
    totalAmount: string;
    metadata: DistributionBreakdown | null;
  }>;
  totalCash: string;
  /** Non-blocking: already-distributed runs covering this window (live preview only). */
  overlapWarning?: OverlappingRun[];
  /** True when this is a distributed run's immutable, as-paid result. */
  frozen?: boolean;
  distributedAt?: string | null;
  /** True when the figures came from the run's stored snapshot (computed state). */
  computed?: boolean;
};

export type DistributionRunResultRow = {
  eventId: string;
  slotId: string;
  slotCode: string | null;
  userId: string;
  ownerName: string | null;
  kind: string;
  level: number | null;
  amount: string;
  currencyKind: string;
  createdAt: string;
  metadata?: DistributionBreakdown | null;
};

export async function createDistributionRun(args: {
  planCode: string;
  startAt: string;
  endAt: string;
  label?: string;
  /** Per-run runtime knobs (Binary/Teslab: `pairing_pct`, `mentor_pct`). */
  params?: Record<string, number>;
}): Promise<DistributionRun & { overlapWarning?: OverlappingRun[] }> {
  const res = await api.post<DistributionRun & { overlapWarning?: OverlappingRun[] }>(
    '/admin/distribution-runs',
    args,
  );
  return res.data;
}

export async function listDistributionRuns(planCode?: string): Promise<DistributionRun[]> {
  const res = await api.get<{ rows: DistributionRun[] }>('/admin/distribution-runs', {
    params: planCode ? { planCode } : undefined,
  });
  return res.data.rows;
}

/** Single run — the poll target while a background preview/distribute runs.
 *  `snapshot` carries `progress` / `preview` / `error`. */
export async function getDistributionRun(id: string): Promise<DistributionRun> {
  const res = await api.get<DistributionRun>(`/admin/distribution-runs/${id}`);
  return res.data;
}

/** 202 shape when the preview compute was queued in the background. */
export type DistributionRunPreviewPending = {
  status: 'computing';
  overlapWarning?: OverlappingRun[];
};

export function isPreviewPending(
  p: DistributionRunPreview | DistributionRunPreviewPending,
): p is DistributionRunPreviewPending {
  return (p as DistributionRunPreviewPending).status === 'computing';
}

export async function previewDistributionRun(
  id: string,
  recompute = false,
): Promise<DistributionRunPreview | DistributionRunPreviewPending> {
  const res = await api.post<DistributionRunPreview | DistributionRunPreviewPending>(
    `/admin/distribution-runs/${id}/preview`,
    undefined,
    { params: recompute ? { recompute: 'true' } : undefined },
  );
  return res.data;
}

/** 202: the distribute now runs in the background — poll `getDistributionRun`. */
export async function distributeRun(
  id: string,
): Promise<{ run: DistributionRun; status: 'distributing' }> {
  const res = await api.post(`/admin/distribution-runs/${id}/distribute`);
  return res.data;
}

/** Discard a run that never paid (pending / computed / failed). */
export async function discardDistributionRun(id: string): Promise<void> {
  await api.delete(`/admin/distribution-runs/${id}`);
}

export async function distributionRunResults(id: string): Promise<DistributionRunResultRow[]> {
  const res = await api.get<{ rows: DistributionRunResultRow[] }>(
    `/admin/distribution-runs/${id}/results`,
  );
  return res.data.rows;
}

export type AdminSlotDetail = {
  id: string;
  slotCode: string;
  userId: string;
  sponsorSlotId: string | null;
  membershipId: string;
  slotType: 'paid' | 'free' | 'cd' | 'half_cd';
  status: string;
  isCompany: boolean;
  rankShowcaseHidden: boolean;
  activatedAt: string | null;
  createdAt: string;
  ownerEmail: string;
  ownerFullName: string | null;
  ownerUsername: string | null;
  ownerPhone: string | null;
  ownerAddress: Record<string, unknown> | null;
  membershipCode: string;
  membershipName: string;
  sponsor: {
    slotCode: string;
    ownerEmail: string;
    ownerFullName: string | null;
  } | null;
  /** Replicated-link sponsor (signup `?ref=`), distinct from `sponsor` above. */
  linkSponsor: {
    slotCode: string;
    status: string;
    ownerEmail: string;
    ownerFullName: string | null;
  } | null;
  binary: {
    parentSlotId: string | null;
    position: 'L' | 'R' | null;
    leftCount: number;
    rightCount: number;
    leftPv: string;
    rightPv: string;
  } | null;
  wallet: {
    available: string;
    pending: string;
    /** Ledger-backed lifetime credits only. */
    lifetimeEarned: string;
    /** What the MEMBER sees: `lifetimeEarned` + any imported legacy offset. */
    totalEarned: string;
    /** Imported tenants only; null for natively-created slots. */
    legacyTotalEarned: string | null;
    legacyEarnedOffset: string | null;
    availableGc: string;
    lifetimeGcEarned: string;
    availableCoin: string;
    lifetimeCoinEarned: string;
    availableToken: string;
    lifetimeTokenEarned: string;
    updatedAt: string | null;
  } | null;
  breakdown: Array<{
    kind: string;
    currencyKind: 'cash' | 'gc' | 'coin' | 'token';
    totalCredit: string;
    totalDebit: string;
    entryCount: number;
    lastAt: string | null;
  }>;
  pvByPeriod: Array<{
    periodId: string;
    periodCode: string;
    periodStartAt: string;
    periodEndAt: string;
    personalPv: string;
    groupPv: string;
    qualifyingLegs: number;
    updatedAt: string;
  }>;
  binaryDays: Array<{
    date: string;
    earned: string;
    pairsCount: number;
    flushed: string;
    cappedAt: string | null;
  }>;
  binaryHistory: Array<{
    sourceEventId: string;
    createdAt: string;
    kinds: string[];
    causeSlotId: string | null;
    causeSlotCode: string | null;
    causeOwnerName: string | null;
    beforeLeft: string | null;
    beforeRight: string | null;
    addLeft: string | null;
    addRight: string | null;
    afterLeft: string | null;
    afterRight: string | null;
    amount: string;
    gc: string;
    flushout: boolean;
    flushoutIncome: string;
  }>;
  currentRank: string | null;
  stairstepActive: boolean;
  binaryActive: boolean;
  vortexActive: boolean;
  vortexTokenBalance: string;
  pointsHistory: Array<{
    occurredAt: string;
    type: 'ppv' | 'gpv' | 'binary_l' | 'binary_r' | 'vortex_token';
    direction: 'in' | 'out';
    amount: string;
    balanceBefore: string | null;
    balanceAfter: string | null;
    kind: string;
    sourceEventId: string;
    fromSlotId: string | null;
    fromSlotCode: string | null;
    orderCode: string | null;
    isDropship: boolean;
  }>;
};

export async function adminGetSlot(id: string): Promise<AdminSlotDetail> {
  const res = await api.get<AdminSlotDetail>(`/admin/slots/${id}`);
  return res.data;
}

export async function adminChangeSlotOwner(
  slotId: string,
  newOwnerUserId: string,
): Promise<{ slotId: string; beforeUserId: string; afterUserId: string }> {
  const res = await api.post(`/admin/slots/${slotId}/change-owner`, { newOwnerUserId });
  return res.data;
}

export type AdminUpgradeSlotResult = {
  slotId: string;
  beforeMembershipId: string;
  afterMembershipId: string;
  afterMembershipCode: string;
  beforeSlotType: 'paid' | 'free';
  afterSlotType: 'paid' | 'free';
  becamePaid: boolean;
  /** Whether activation/placement comp fired (free->paid always; paid->paid per tenant flag). */
  fireActivation: boolean;
  upgradeHistoryId: string;
  isPlaced: boolean;
  codeId: string | null;
};

/**
 * Admin override: upgrade a slot to a strictly-higher membership tier WITHOUT a
 * kit code. Ignores kit usage-mode entirely (no charge). The target tier must
 * have a strictly higher `membership_hierarchy` than the slot's current tier.
 */
export async function adminUpgradeSlot(
  slotId: string,
  body: { targetMembershipCode: string; targetSlotType: 'paid' | 'free' },
): Promise<AdminUpgradeSlotResult> {
  const res = await api.post<AdminUpgradeSlotResult>(`/admin/slots/${slotId}/upgrade`, body);
  return res.data;
}

/** compStatus of an upgrade's activation bonus, derived from comp_plan_runs. */
export type UpgradeCompStatus = 'never' | 'fired' | 'partial' | 'failed' | 'pending';

export type AdminUpgradeHistoryRow = {
  id: string;
  createdAt: string;
  codeUsed: string | null;
  beforeSlotType: 'paid' | 'free' | 'cd' | 'half_cd';
  afterSlotType: 'paid' | 'free' | 'cd' | 'half_cd';
  beforeMembershipCode: string;
  beforeMembershipName: string;
  afterMembershipCode: string;
  afterMembershipName: string;
  compStatus: UpgradeCompStatus;
};

export async function adminGetUpgradeHistory(slotId: string): Promise<AdminUpgradeHistoryRow[]> {
  const res = await api.get<{ rows: AdminUpgradeHistoryRow[] }>(
    `/admin/slots/${slotId}/upgrade-history`,
  );
  return res.data.rows;
}

export type RefireUpgradeBonusOutcome =
  | 'enqueued'
  | 'already_fired'
  | 'nothing_to_fire'
  | 'blocked_superseded'
  | 'blocked_cd_settlement'
  | 'blocked_in_flight'
  | 'blocked_failed';

export type RefireUpgradeBonusResult = {
  slotId: string;
  upgradeHistoryId: string;
  afterMembershipCode: string;
  afterSlotType: 'paid' | 'free' | 'cd' | 'half_cd';
  outcome: RefireUpgradeBonusOutcome;
  enqueuedActivation: boolean;
  enqueuedPlacement: boolean;
  claimStatuses: Record<string, number>;
};

/**
 * SUPER_ADMIN: re-fire the activation/placement commissions for an already-done
 * upgrade whose bonus never paid. Idempotent (reuses the upgrade's deterministic
 * comp event id) — it only fires a genuinely-virgin event; anything already run,
 * failed, superseded, or a CD settlement is surfaced as a `blocked_*` outcome.
 */
export async function adminRefireUpgradeBonus(
  slotId: string,
  body: { upgradeHistoryId?: string },
): Promise<RefireUpgradeBonusResult> {
  const res = await api.post<RefireUpgradeBonusResult>(
    `/admin/slots/${slotId}/refire-upgrade-bonus`,
    body,
  );
  return res.data;
}

/** One already-paid credit a sponsor transfer will reverse and re-issue. */
export type SponsorTransferCredit = {
  ledgerId: string;
  sourceEventId: string;
  sourcePlan: string;
  kind: string;
  level: number | null;
  amount: string;
  currencyKind: string;
  recipientSlotId: string;
  recipientSlotCode: string;
  recipientName: string | null;
  triggerSlotCode: string | null;
};

export type SponsorTransferPreview = {
  slotId: string;
  slotCode: string;
  ownerName: string | null;
  currentSponsorSlotId: string | null;
  currentSponsorSlotCode: string | null;
  currentSponsorName: string | null;
  newSponsorSlotId: string;
  newSponsorSlotCode: string;
  newSponsorName: string | null;
  subtreeSize: number;
  alreadyTransferred: boolean;
  affectedCredits: SponsorTransferCredit[];
  affectedEvents: string[];
  /** Non-empty means the transfer will be refused; render these, don't submit. */
  blockers: string[];
};

export type SponsorTransferResult = {
  slotId: string;
  slotCode: string;
  beforeSponsorSlotId: string | null;
  afterSponsorSlotId: string;
  newPath: string;
  pathsRewritten: number;
  reversedCredits: number;
  replayedEvents: string[];
  treeAlreadyMoved: boolean;
};

/**
 * Dry-run a sponsor transfer: who it moves, how much of the subtree moves with
 * it, exactly which credits get reversed and re-issued, and why it would be
 * refused. Always call this before {@link adminTransferSponsor}.
 */
export async function adminPreviewSponsorTransfer(
  slotId: string,
  newSponsorCode: string,
): Promise<SponsorTransferPreview> {
  const res = await api.post<SponsorTransferPreview>(
    `/admin/slots/${slotId}/transfer-sponsor/preview`,
    { newSponsorCode },
  );
  return res.data;
}

/**
 * Move a slot (and its sponsor-tree descendants) under a new sponsor. With
 * `moveComp` the comp already paid up the old chain is reversed and replayed
 * against the corrected tree. Safe to re-send: a completed transfer is a no-op
 * and an interrupted one resumes.
 */
export async function adminTransferSponsor(
  slotId: string,
  body: { newSponsorCode: string; moveComp: boolean; reason: string },
): Promise<SponsorTransferResult> {
  const res = await api.post<SponsorTransferResult>(
    `/admin/slots/${slotId}/transfer-sponsor`,
    body,
  );
  return res.data;
}

export type CompPlanConfigRow = {
  id: string;
  planCode: string;
  version: number;
  config: Record<string, unknown>;
  effectiveFrom: string;
  createdBy: string | null;
  createdAt: string;
};

export async function listCompPlanConfigs(): Promise<CompPlanConfigRow[]> {
  const res = await api.get<CompPlanConfigRow[]>('/admin/compplan/configs');
  return res.data;
}

/**
 * A Stairstep publish rechecks every slot's rank right away when live rank-up
 * is on: `raised` slots moved up, or the recheck failed (the next order
 * rechecks anyway). Absent for other plans or with live rank-up off.
 */
export type RankRecheckResult = { raised: number } | { error: 'rank_recheck_failed' };

export async function publishCompPlanConfig(input: {
  planCode: string;
  config: Record<string, unknown>;
}): Promise<CompPlanConfigRow & { rankRecheck?: RankRecheckResult }> {
  const res = await api.post<CompPlanConfigRow & { rankRecheck?: RankRecheckResult }>(
    '/admin/compplan/configs',
    input,
  );
  return res.data;
}

export type RoleRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  builtin: boolean;
  permissionsLocked: boolean;
  permissions: string[];
  userCount: number;
};

export type PermissionRow = {
  id: string;
  code: string;
  description: string | null;
  // When false, a tenant admin (non-super-admin) may not grant this perm to a
  // custom role. super_admin can toggle it via setPermissionGrantable.
  grantable: boolean;
};

export async function listRoles(): Promise<RoleRow[]> {
  const res = await api.get<RoleRow[]>('/admin/roles');
  return res.data;
}

/**
 * Slim {code,name} role list for pickers (POS method role gating). Unlike
 * `listRoles` it doesn't require the non-grantable `user.roles.assign` perm,
 * so a custom role granted only the payment-methods perms can still load it.
 */
export async function getRoleOptions(): Promise<Array<{ code: string; name: string }>> {
  const res = await api.get<Array<{ code: string; name: string }>>('/admin/roles/options');
  return res.data;
}

export async function listPermissions(): Promise<PermissionRow[]> {
  const res = await api.get<PermissionRow[]>('/admin/permissions');
  return res.data;
}

export async function createRole(input: {
  code: string;
  name: string;
  description?: string;
}): Promise<RoleRow> {
  const res = await api.post<RoleRow>('/admin/roles', input);
  return res.data;
}

export async function updateRole(
  id: string,
  patch: { name?: string; description?: string | null },
): Promise<RoleRow> {
  const res = await api.patch<RoleRow>(`/admin/roles/${id}`, patch);
  return res.data;
}

export async function deleteRole(id: string): Promise<void> {
  await api.delete(`/admin/roles/${id}`);
}

export async function setRolePermissions(
  id: string,
  permissionCodes: string[],
): Promise<{ ok: boolean; permissionCodes: string[] }> {
  const res = await api.put<{ ok: boolean; permissionCodes: string[] }>(
    `/admin/roles/${id}/permissions`,
    { permissionCodes },
  );
  return res.data;
}

// ─── Role membership (role-centric assignment, replaces the per-user Access tab) ───

export type RoleMember = {
  id: string;
  email: string;
  fullName: string | null;
  status: string;
};

export async function listRoleMembers(roleId: string, search?: string): Promise<RoleMember[]> {
  const res = await api.get<RoleMember[]>(`/admin/roles/${roleId}/members`, {
    params: { search: search || undefined, page: 1, pageSize: 50 },
  });
  return res.data;
}

export async function addRoleMember(roleId: string, userId: string): Promise<void> {
  await api.post(`/admin/roles/${roleId}/members`, { userId });
}

export async function removeRoleMember(roleId: string, userId: string): Promise<void> {
  await api.delete(`/admin/roles/${roleId}/members/${userId}`);
}

/** User search for the role-member picker — reuses GET /users. */
export async function searchUsersForRole(search: string): Promise<RoleMember[]> {
  const res = await api.get<{ rows: RoleMember[] }>('/users', {
    params: { search: search || undefined, page: 1, pageSize: 20 },
  });
  return res.data.rows;
}

/** super_admin-only: flip whether tenant admins may grant a permission. */
export async function setPermissionGrantable(
  code: string,
  grantable: boolean,
): Promise<{ code: string; grantable: boolean }> {
  const res = await api.patch<{ code: string; grantable: boolean }>(
    `/admin/permissions/${encodeURIComponent(code)}/grantable`,
    { grantable },
  );
  return res.data;
}

// ─── Phase 12C distribution ──────────────────────────────────────────────

export type UnilevelQualification = {
  slotId: string;
  slotCode: string;
  userId: string;
  email: string;
  fullName: string | null;
  personalPv: string;
  qualifies: boolean;
};

export async function listUnilevelQualifications(
  periodId: string,
): Promise<{ threshold: string; qualifiers: UnilevelQualification[] }> {
  const res = await api.get<{ threshold: string; qualifiers: UnilevelQualification[] }>(
    `/admin/periods/${periodId}/distribute/unilevel/qualifications`,
  );
  return res.data;
}

/** 202: the period release now runs in the background — poll `getDistState`
 *  until the wizard state reaches `distributed` (snapshot carries progress). */
export async function distributeUnilevel(
  periodId: string,
): Promise<{ ok: boolean; periodId: string; status: 'distributing' }> {
  const res = await api.post<{ ok: boolean; periodId: string; status: 'distributing' }>(
    `/admin/periods/${periodId}/distribute/unilevel`,
  );
  return res.data;
}

export type DistributionResult = {
  eventId: string;
  slotId: string;
  userId: string;
  kind: string;
  level: number | null;
  amount: string;
  currencyKind: string;
  createdAt: string;
};

export async function getUnilevelDistributionResults(
  periodId: string,
): Promise<DistributionResult[]> {
  const res = await api.get<{ rows: DistributionResult[] }>(
    `/admin/periods/${periodId}/distribute/unilevel/results`,
  );
  return res.data.rows;
}

export type DistributedIncomeRow = {
  eventId: string;
  eventType: string;
  planCode: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  entries: Array<{
    slotId: string;
    slotCode: string | null;
    userId: string;
    ownerName: string | null;
    kind: string;
    level: number | null;
    amount: string;
    currencyKind: string;
  }>;
};

export async function listDistributedIncome(params: {
  from?: string;
  to?: string;
  userId?: string;
  /** Filter to entries this slot RECEIVED (recipient). */
  slotId?: string;
  /** Filter to events this slot TRIGGERED (e.g. the activating buyer slot). */
  triggerSlotId?: string;
  planCode?: string;
  limit?: number;
}): Promise<DistributedIncomeRow[]> {
  const res = await api.get<{ rows: DistributedIncomeRow[] }>('/admin/distributions', {
    params,
  });
  return res.data.rows;
}

export type PeriodRow = {
  id: string;
  code: string;
  startAt: string;
  endAt: string;
  status: string;
  closedAt: string | null;
};

export async function listPeriods(): Promise<PeriodRow[]> {
  const res = await api.get<{ periods: PeriodRow[] }>('/periods');
  return res.data.periods;
}

// ─── Phase 12D DLQ admin ────────────────────────────────────────────────

export type DlqJob = {
  jobId: string;
  queue: string;
  name: string;
  data: Record<string, unknown>;
  failedReason: string;
  attemptsMade: number;
  timestamp: string;
};

export async function listDlqJobs(): Promise<DlqJob[]> {
  const res = await api.get<{ jobs: DlqJob[] }>('/admin/dlq');
  return res.data.jobs;
}

export async function retryDlqJob(queue: string, jobId: string): Promise<void> {
  await api.post(`/admin/dlq/${encodeURIComponent(queue)}/${encodeURIComponent(jobId)}/retry`);
}

export async function dropDlqJob(queue: string, jobId: string): Promise<void> {
  await api.post(`/admin/dlq/${encodeURIComponent(queue)}/${encodeURIComponent(jobId)}/drop`);
}

// ─── Phase 12E reports ──────────────────────────────────────────────────

export type SalesByBranchRow = {
  branchId: string | null;
  branchCode: string | null;
  branchName: string | null;
  ordersCount: number;
  grossTotal: string;
  pvTotal: string;
};

export type PayoutsByPlanRow = {
  sourcePlan: string;
  currencyKind: string;
  entryCount: number;
  totalAmount: string;
  uniqueRecipients: number;
};

export type TopSponsorRow = {
  userId: string;
  email: string;
  fullName: string | null;
  directs: number;
  directVolume: string;
};

export type ReportWindow = { from?: string; to?: string };

// ── Phase 3: generic report fetch + CSV export ────────────────────────────
/** Mirrors `ReportColumn` in apps/api/src/services/reports-extended.ts.
 *  `type` is the OPTIONAL cell-type hint the Excel/PDF writers consume — see
 *  `reportExportColumns` in lib/report-export.ts for the `align`-based fallback
 *  applied to the reports that don't declare one. */
export type ReportColumn = {
  key: string;
  label: string;
  align?: 'right';
  type?: 'string' | 'number' | 'money' | 'date' | 'datetime';
};
export type ReportResult = {
  columns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  summary?: { label: string; value: string }[];
};
export type BranchTypeFilter = 'branch' | 'stockist';
export type ReportParams = {
  from?: string;
  to?: string;
  /** Due Payments only: overdue bills plus those due within this many days. */
  withinDays?: string;
  slotCode?: string;
  /** Member Total Earned only: reports across every slot this member owns. */
  userId?: string;
  /** Sales reports only: a registry CODE, never a display label. */
  paymentMethod?: string;
  /** Sales reports only: include the inert imported legacy history. */
  includeImports?: 'true';
  reason?: string;
  /** One branch id, or several comma-joined (`<uuid>,<uuid>`). Honoured by the
   *  branch-capable reports only (see REPORTS' `branch` flag); a branch-scoped
   *  admin is clamped server-side, so a foreign id here is ignored.
   *
   *  Stays a STRING even though the server takes a list: axios is created here
   *  without a `paramsSerializer`, so a real array would go out as `branchId[]=`
   *  and never reach the filter. Typing it `string | string[]` would invite
   *  exactly that at every call site. */
  branchId?: string;
  /** Stock on Hand only. A stockist IS a branch (`branches.type`), so this
   *  narrows the SAME dimension as `branchId` and ANDs with it — and with the
   *  server's scope clamp, so it can only ever shrink the result set. */
  branchType?: BranchTypeFilter;
  /** Group Purchases only: which downline tree to walk. `binary` includes
   *  spillover placed by uplines — slots the member never recruited. */
  tree?: 'unilevel' | 'binary';
  /** Group Purchases only: count the entered slot's own orders too. */
  includeSelf?: 'true' | 'false';
  /**
   * As-of snapshot reports (ap-aging, trial-balance, balance-sheet, …).
   * Prefer a tenant-local `YYYY-MM-DD`: the books cut on a calendar date, and
   * an instant built in the browser's timezone moves that cut by a day for any
   * admin outside the tenant's timezone (audit F23). ISO instants are still
   * accepted server-side so bookmarked URLs keep working.
   */
  asOf?: string;
  /** gl-account drill-down only: the `gl_accounts.code` to walk. */
  accountCode?: string;
};

/**
 * The tape behind a POS sales-report row. Server withholds kit PINs on this
 * route — see `getOrderReceipt`.
 */
export async function fetchOrderReceipt(orderId: string): Promise<ReceiptData> {
  const res = await api.get(`/admin/reports/orders/${orderId}/receipt`);
  return orderToReceipt(res.data);
}

export async function fetchReport(key: string, params: ReportParams): Promise<ReportResult> {
  const res = await api.get<ReportResult>(`/admin/reports/${key}`, { params });
  return res.data;
}

/**
 * Fetch a report as CSV (auth header preserved). TRANSPORT ONLY — the download
 * itself is `downloadReport()` in lib/report-export.ts, which is the one path
 * every admin export surface uses for all three formats.
 */
export async function fetchReportCsvBlob(key: string, params: ReportParams): Promise<Blob> {
  const res = await api.get(`/admin/reports/${key}`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });
  return res.data as Blob;
}

export async function reportSalesByBranch(
  w: ReportWindow & { branchId?: string },
): Promise<SalesByBranchRow[]> {
  const res = await api.get<{ rows: SalesByBranchRow[] }>('/admin/reports/sales-by-branch', {
    params: w,
  });
  return res.data.rows;
}

export async function reportPayoutsByPlan(w: ReportWindow): Promise<PayoutsByPlanRow[]> {
  const res = await api.get<{ rows: PayoutsByPlanRow[] }>('/admin/reports/payouts-by-plan', {
    params: w,
  });
  return res.data.rows;
}

export async function reportTopSponsors(
  w: ReportWindow & { limit?: number },
): Promise<TopSponsorRow[]> {
  const res = await api.get<{ rows: TopSponsorRow[] }>('/admin/reports/top-sponsors', {
    params: w,
  });
  return res.data.rows;
}

export type VortexQueueRow = {
  vortexId: string;
  slotCode: string;
  email: string;
  fullName: string | null;
  membershipName: string;
  graduated: boolean;
  afterGraduate: boolean;
  createdAt: string;
  graduatesAt: number;
  entriesRemaining: number;
};

export type VortexWatchRow = {
  slotId: string;
  slotCode: string;
  email: string;
  fullName: string | null;
  membershipName: string;
  availableToken: string;
  lifetimeTokenEarned: string;
  progressPct: number;
};

export type VortexStatusResponse = {
  tokenRequirement: string;
  graduationRequirement: number;
  totalMinted: number;
  queue: VortexQueueRow[];
  watchlist: VortexWatchRow[];
};

export async function reportVortexStatus(): Promise<VortexStatusResponse> {
  const res = await api.get<VortexStatusResponse>('/admin/reports/vortex-status');
  return res.data;
}

export type AdjustTarget = 'cash' | 'gc' | 'token' | 'coin' | 'ai_credit' | 'pv';

export type AdjustSlotBody = {
  direction: 'C' | 'D';
  target: AdjustTarget;
  amount: string;
  sourcePlan: string;
  note: string;
  sendNotification: boolean;
  notificationTitle?: string;
  notificationBody?: string;
};

export async function adjustSlot(
  slotId: string,
  body: AdjustSlotBody,
): Promise<{ ok: boolean; ledgerId: string | null; eventId: string }> {
  const res = await api.post<{ ok: boolean; ledgerId: string | null; eventId: string }>(
    `/admin/slots/${slotId}/adjust`,
    body,
  );
  return res.data;
}

// ─── CD / Half-CD debt ──────────────────────────────────────────────────

export type SlotDebt = {
  slotId: string;
  debtTotal: string;
  debtPaid: string;
  debtRemaining: string;
  settledAt: string | null;
  settledByEventId: string | null;
  entryCompFired: boolean;
  entrySlotType: 'cd' | 'half_cd';
  createdAt: string;
};

/** 404 (no debt row) resolves to null — most slots never carried a debt. */
export async function getSlotDebt(slotId: string): Promise<SlotDebt | null> {
  try {
    const res = await api.get<SlotDebt>(`/admin/slots/${slotId}/debt`);
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

export type AdjustSlotDebtBody = {
  op: 'increase' | 'reduce' | 'forgive';
  amount?: string;
  note: string;
};

export async function adjustSlotDebt(
  slotId: string,
  body: AdjustSlotDebtBody,
): Promise<{ ok: boolean; settled: boolean; debtTotal: string; debtPaid: string }> {
  const res = await api.post<{
    ok: boolean;
    settled: boolean;
    debtTotal: string;
    debtPaid: string;
  }>(`/admin/slots/${slotId}/debt/adjust`, body);
  return res.data;
}

// ─── Phase 13 dashboard ─────────────────────────────────────────────────

/**
 * Whether a figure responds to the dashboard's branch filter. Declared by the
 * server per metric, so the UI's badge rule is one line and no card id is
 * hardcoded here: badge it when a branch is applied and the metric is
 * 'tenant'. See services/dashboard.ts for why the tenant-wide ones can't
 * narrow (no branch column on slots / ledger_entries / slot_wallets / …).
 */
export type MetricScope = 'branch' | 'tenant';

export type KpiCardData = {
  id:
    | 'total_members'
    | 'active_members'
    | 'mtd_sales'
    | 'mtd_commissions'
    | 'all_time_sales'
    | 'all_time_earnings'
    | 'circulating_wallet'
    | 'pending_payouts'
    | 'pending_kyc'
    | 'open_orders';
  label: string;
  value: string;
  deltaPct: string | null;
  sparkline: number[];
  format: 'number' | 'currency';
  scope: MetricScope;
};

/**
 * `appliedBranchId` is the branch the SERVER actually filtered on, after
 * clamping — not the one we asked for. A branch-scoped operator sends nothing
 * and still gets their branch back; a forged id gets replaced by theirs. It is
 * the only honest source for "is this page filtered".
 */
export type DashboardKpisResult = {
  cards: KpiCardData[];
  appliedBranchId: string | null;
};

export async function getDashboardKpis(branchId?: string): Promise<DashboardKpisResult> {
  const res = await api.get<{
    cards: KpiCardData[];
    generatedAt: string;
    appliedBranchId: string | null;
  }>('/admin/dashboard/kpis', { params: branchId ? { branchId } : {} });
  return { cards: res.data.cards, appliedBranchId: res.data.appliedBranchId };
}

export type InactivityBlockSummary = {
  enabled: boolean;
  days: number;
  since: string | null;
  /** Accounts currently suspended by the inactivity sweep (not manual blocks). */
  blockedCount: number;
};

export async function getInactivityBlockSummary(): Promise<InactivityBlockSummary> {
  const res = await api.get<InactivityBlockSummary>('/admin/inactivity-blocks/summary');
  return res.data;
}

export type EnrollmentTrendPoint = { date: string; count: number };

export async function getEnrollmentTrend(days = 90): Promise<EnrollmentTrendPoint[]> {
  const res = await api.get<{ series: EnrollmentTrendPoint[] }>(
    '/admin/dashboard/enrollment-trend',
    { params: { days } },
  );
  return res.data.series;
}

export type SalesCommissionPoint = {
  weekStart: string;
  sales: string;
  commission: string;
};

/**
 * Only the SALES series narrows to a branch — commissions are paid to uplines
 * who may sit in another branch, and `ledger_entries` has no branch column at
 * all. The chart stops stacking the two while filtered rather than implying a
 * whole/part relationship between figures at different scopes.
 */
export async function getSalesVsCommission(
  weeks = 12,
  branchId?: string,
): Promise<SalesCommissionPoint[]> {
  const res = await api.get<{ series: SalesCommissionPoint[] }>(
    '/admin/dashboard/sales-vs-commission',
    { params: { weeks, ...(branchId ? { branchId } : {}) } },
  );
  return res.data.series;
}

export type ActivityRow = {
  ts: string;
  kind: 'enrolled' | 'order_placed' | 'order_completed' | 'payout_paid';
  actorName: string;
  actorEmail: string;
  summary: string;
  /** Order rows narrow to the filtered branch; enrolments/payouts never do. */
  scope: MetricScope;
};

export async function getActivityFeed(limit = 20, branchId?: string): Promise<ActivityRow[]> {
  const res = await api.get<{ rows: ActivityRow[] }>('/admin/dashboard/activity', {
    params: { limit, ...(branchId ? { branchId } : {}) },
  });
  return res.data.rows;
}

export type TopEarnerRow = {
  userId: string;
  email: string;
  fullName: string | null;
  lifetimeCashEarned: string;
};

export async function getTopEarners(limit = 10): Promise<TopEarnerRow[]> {
  const res = await api.get<{ rows: TopEarnerRow[] }>('/admin/dashboard/top-earners', {
    params: { limit },
  });
  return res.data.rows;
}

export type TopRecruiterRow = {
  userId: string;
  email: string;
  fullName: string | null;
  directs: number;
};

export async function getTopRecruiters(limit = 10): Promise<TopRecruiterRow[]> {
  const res = await api.get<{ rows: TopRecruiterRow[] }>('/admin/dashboard/top-recruiters', {
    params: { limit },
  });
  return res.data.rows;
}

// ── Shipping config (admin) ─────────────────────────────────────────────
export async function getShippingConfig(): Promise<ShippingConfig | null> {
  const res = await api.get<{ config: ShippingConfig | null }>('/admin/shipping/config');
  return res.data.config;
}

export async function publishShippingConfig(config: ShippingConfig): Promise<{ version: number }> {
  const res = await api.post<{ version: number }>('/admin/shipping/config', config);
  return res.data;
}

export async function uploadCourierLogo(
  file: File,
  courierId?: string,
): Promise<{ key: string; version?: number }> {
  const form = new FormData();
  // courierId must precede the file part so the server has it parsed by the
  // time it persists the merged config.
  if (courierId) form.append('courierId', courierId);
  form.append('file', file);
  const res = await api.post<{ key: string; version?: number }>(
    '/admin/shipping/courier-logo',
    form,
  );
  return res.data;
}

/** Public read endpoint that 302-redirects to the courier logo (presigned). */
export function courierLogoUrl(courierId: string): string {
  return `${api.defaults.baseURL ?? ''}/shipping/courier-logo/${encodeURIComponent(courierId)}`;
}

export async function uploadEcomPaymentMethodLogo(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/ecom-payment-methods/logo', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to a payment method logo (presigned). */
export function ecomPaymentMethodLogoUrl(code: string): string {
  return `${api.defaults.baseURL ?? ''}/storefront/payment-method-logo/${encodeURIComponent(code)}`;
}

// ─── Legacy data import (super-admin) ──────────────────────────────────────

export type ImportMembershipMapEntry = {
  code: string;
  name: string;
  hierarchy: number;
  create: boolean;
};

export type ImportAnalyzeReport = {
  tenant: string;
  counts: {
    users: number;
    slots: number;
    codes: number;
    usersWithSlots: number;
    usersWithoutSlots: number;
    /** Users carrying a legacy per-user sponsor (the "lead" connection); the
     * slotless subset are leads. Optional: older API builds don't send them. */
    usersWithSponsor?: number;
    leadsWithSponsor?: number;
    slotsPlaced: number;
    slotsUnplaced: number;
    sponsorRoots: number;
  };
  walletTotals: { cash: string; gc: string; token: string; coin: string };
  detection: {
    currencies: string[];
    legPointsSource: 'binary' | 'binary_three';
    hasPersonalPv: boolean;
    hasGroupPv: boolean;
    memberships: Array<{
      legacyId: string;
      count: number;
      suggestedCode: string;
      suggestedName: string;
      suggestedHierarchy: number;
    }>;
    ranks: Array<{ legacyRankId: number; count: number }>;
  };
  issues: Array<{ level: 'error' | 'warning' | 'info'; code: string; message: string }>;
  unusedCodes: Array<{
    code: string;
    pin: string | null;
    membership: string | null;
    recipientUid: string | null;
    createdDate: string | null;
  }>;
  // Read-only sales overview — present only when the workbook has a db_sales sheet.
  sales?: ImportSalesAnalysis;
  progress?: { phase: string; done: number; total: number };
  reconciliation?: Record<string, string | number>;
};

export type ImportSalesAnalysis = {
  count: number;
  minSaleId: number | null;
  maxSaleId: number | null;
  fingerprint: string;
  sellers: Array<{ code: string; branchName: string | null; count: number }>;
  statuses: Record<string, number>;
  skus: Array<{ sku: string; name: string | null; qty: number; type: string | null }>;
  unresolvedBuyers: number;
  paymentVariance: number;
  missingSlotRef: number;
  noLines: number;
};

export type ImportSalesBranchMapping = { branchId: string; series: 'preserve' | 'renumber' };
export type ImportSalesProductTarget =
  | { kind: 'product'; productId: string }
  | { kind: 'variant'; productId: string; variantId: string }
  | { kind: 'placeholder' }
  | { kind: 'skip' };
export type ImportSalesConfig = {
  branchMap: Record<string, ImportSalesBranchMapping>;
  productMap: Record<string, ImportSalesProductTarget>;
  unmatchedSku: 'block' | 'placeholder' | 'skip';
  importPayments: boolean;
};

export type ImportBatch = {
  id: string;
  tenant: string;
  fileName: string | null;
  status: 'uploaded' | 'analyzing' | 'ready' | 'importing' | 'completed' | 'failed';
  fileKey?: string | null;
  config?: Record<string, unknown>;
  report: ImportAnalyzeReport;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ImportBatchSummary = Pick<
  ImportBatch,
  'id' | 'tenant' | 'fileName' | 'status' | 'createdAt' | 'updatedAt'
>;

export async function analyzeImportFile(
  file: File,
  tenant: string,
): Promise<{ importId: string; report: ImportAnalyzeReport }> {
  const form = new FormData();
  form.append('file', file);
  form.append('tenant', tenant);
  const res = await api.post<{ importId: string; report: ImportAnalyzeReport }>(
    '/admin/data-import/analyze',
    form,
  );
  return res.data;
}

export async function listImportBatches(): Promise<{ batches: ImportBatchSummary[] }> {
  const res = await api.get<{ batches: ImportBatchSummary[] }>('/admin/data-import');
  return res.data;
}

export async function getImportBatch(id: string): Promise<ImportBatch> {
  const res = await api.get<ImportBatch>(`/admin/data-import/${id}`);
  return res.data;
}

export async function executeImportBatch(
  id: string,
  body: {
    defaultPassword: string;
    forceChangePassword?: boolean;
    currency?: string;
    legPointsSource?: 'binary' | 'binary_three';
    membershipMap?: Record<string, ImportMembershipMapEntry>;
    sales?: ImportSalesConfig;
    confirmSales?: string;
  },
): Promise<{ ok: boolean; importId: string; status: string }> {
  const res = await api.post<{ ok: boolean; importId: string; status: string }>(
    `/admin/data-import/${id}/execute`,
    { confirm: 'IMPORT', ...body },
  );
  return res.data;
}

export async function uploadEcomPaymentMethodQr(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/ecom-payment-methods/qr', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to a payment method QR (presigned). */
export function ecomPaymentMethodQrUrl(code: string): string {
  return `${api.defaults.baseURL ?? ''}/storefront/payment-method-qr/${encodeURIComponent(code)}`;
}

/** Upload a storefront banner/slide image → returns the R2 object key. */
export async function uploadStorefrontAsset(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/storefront/asset', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to a storefront asset (presigned). */
export function storefrontAssetUrl(key: string): string {
  return `${api.defaults.baseURL ?? ''}/storefront/asset?key=${encodeURIComponent(key)}`;
}

/** Upload a stairstep rank icon → returns the R2 key, stored on the next config publish. */
export async function uploadRankIcon(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/compplan/rank-icon', form);
  return res.data;
}

/**
 * Public icon of a PUBLISHED rank (reads the latest config's `icon_key`). 404s
 * while the ranks showcase is off, so callers need an onError fallback.
 */
export function rankIconUrl(code: string): string {
  return `${api.defaults.baseURL ?? ''}/ranks/${encodeURIComponent(code)}/icon`;
}

/**
 * Upload a tenant brand asset (light/dark logo or favicon). The key is persisted
 * onto `tenant_config.theme` server-side immediately; returns the key + merged
 * theme so the caller can refresh its draft without a separate save.
 */
export async function uploadTenantLogo(
  file: File,
  which: 'light' | 'dark' | 'favicon',
): Promise<{ key: string; theme: ThemeTokens }> {
  const form = new FormData();
  form.append('which', which);
  form.append('file', file);
  const res = await api.post<{ key: string; theme: ThemeTokens }>('/admin/tenant/logo', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to an uploaded tenant logo (presigned). */
export function tenantLogoUrl(which: 'light' | 'dark' | 'favicon'): string {
  return `${api.defaults.baseURL ?? ''}/tenant/logo/${which}`;
}

// ── Training Videos CMS ────────────────────────────────────────────────────
export type TrainingVideoCategory = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type TrainingVideo = {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  videoUrl: string;
  thumbnailKey: string | null;
  sortOrder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export async function listTrainingVideoCategories(): Promise<TrainingVideoCategory[]> {
  const res = await api.get<TrainingVideoCategory[]>('/admin/training-video-categories');
  return res.data;
}

export async function createTrainingVideoCategory(input: {
  name: string;
  sortOrder?: number;
  active?: boolean;
}): Promise<TrainingVideoCategory> {
  const res = await api.post<TrainingVideoCategory>('/admin/training-video-categories', input);
  return res.data;
}

export async function updateTrainingVideoCategory(
  id: string,
  patch: Partial<{ name: string; sortOrder: number; active: boolean }>,
): Promise<TrainingVideoCategory> {
  const res = await api.patch<TrainingVideoCategory>(
    `/admin/training-video-categories/${id}`,
    patch,
  );
  return res.data;
}

export async function deleteTrainingVideoCategory(id: string): Promise<void> {
  await api.delete(`/admin/training-video-categories/${id}`);
}

export async function listTrainingVideos(categoryId?: string): Promise<TrainingVideo[]> {
  const res = await api.get<TrainingVideo[]>('/admin/training-videos', {
    params: categoryId ? { category: categoryId } : undefined,
  });
  return res.data;
}

export async function createTrainingVideo(input: {
  title: string;
  description?: string | null;
  categoryId?: string | null;
  videoUrl: string;
  thumbnailKey?: string | null;
  sortOrder?: number;
  active?: boolean;
}): Promise<TrainingVideo> {
  const res = await api.post<TrainingVideo>('/admin/training-videos', input);
  return res.data;
}

export async function updateTrainingVideo(
  id: string,
  patch: Partial<{
    title: string;
    description: string | null;
    categoryId: string | null;
    videoUrl: string;
    thumbnailKey: string | null;
    sortOrder: number;
    active: boolean;
  }>,
): Promise<TrainingVideo> {
  const res = await api.patch<TrainingVideo>(`/admin/training-videos/${id}`, patch);
  return res.data;
}

export async function deleteTrainingVideo(id: string): Promise<void> {
  await api.delete(`/admin/training-videos/${id}`);
}

export async function uploadTrainingVideoThumbnail(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/training-videos/thumbnail', form);
  return res.data;
}

// ── Resources CMS ──────────────────────────────────────────────────────────
export type ResourceCategory = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type AdminResource = {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  kind: 'file' | 'link';
  fileKey: string | null;
  originalFilename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  linkUrl: string | null;
  sortOrder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ResourceInput = {
  title: string;
  description?: string | null;
  categoryId?: string | null;
  kind: 'file' | 'link';
  fileKey?: string | null;
  originalFilename?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  linkUrl?: string | null;
  sortOrder?: number;
  active?: boolean;
};

export async function listResourceCategories(): Promise<ResourceCategory[]> {
  const res = await api.get<ResourceCategory[]>('/admin/resource-categories');
  return res.data;
}

export async function createResourceCategory(input: {
  name: string;
  sortOrder?: number;
  active?: boolean;
}): Promise<ResourceCategory> {
  const res = await api.post<ResourceCategory>('/admin/resource-categories', input);
  return res.data;
}

export async function updateResourceCategory(
  id: string,
  patch: Partial<{ name: string; sortOrder: number; active: boolean }>,
): Promise<ResourceCategory> {
  const res = await api.patch<ResourceCategory>(`/admin/resource-categories/${id}`, patch);
  return res.data;
}

export async function deleteResourceCategory(id: string): Promise<void> {
  await api.delete(`/admin/resource-categories/${id}`);
}

export async function listAdminResources(categoryId?: string): Promise<AdminResource[]> {
  const res = await api.get<AdminResource[]>('/admin/resources', {
    params: categoryId ? { category: categoryId } : undefined,
  });
  return res.data;
}

export async function createResource(input: ResourceInput): Promise<AdminResource> {
  const res = await api.post<AdminResource>('/admin/resources', input);
  return res.data;
}

export async function updateResource(
  id: string,
  patch: Partial<ResourceInput>,
): Promise<AdminResource> {
  const res = await api.patch<AdminResource>(`/admin/resources/${id}`, patch);
  return res.data;
}

export async function deleteResource(id: string): Promise<void> {
  await api.delete(`/admin/resources/${id}`);
}

/** Uploads the document and returns the metadata to store on the resource row. */
export async function uploadResourceFile(file: File): Promise<{
  key: string;
  originalFilename: string | null;
  mimeType: string;
  sizeBytes: number;
}> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{
    key: string;
    originalFilename: string | null;
    mimeType: string;
    sizeBytes: number;
  }>('/admin/resources/file', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to a video thumbnail (presigned). */
export function trainingVideoThumbnailUrl(id: string): string {
  return `${api.defaults.baseURL ?? ''}/training-videos/${id}/thumbnail`;
}

// ── Announcements CMS ──────────────────────────────────────────────────────
export type Announcement = {
  id: string;
  title: string;
  body: string;
  imageKey: string | null;
  imageUrl: string | null;
  /** Delivery channels: dashboard card and/or notification bell. */
  deliverDashboard: boolean;
  deliverBell: boolean;
  /** When the one-time bell fan-out ran (null = not yet broadcast). Read-only. */
  fannedOutAt: string | null;
  publishedAt: string | null;
  expiresAt: string | null;
  createdAt?: string;
};

export async function listAnnouncements(): Promise<Announcement[]> {
  const res = await api.get<Announcement[]>('/admin/announcements');
  return res.data;
}

export async function createAnnouncement(input: {
  title: string;
  body: string;
  imageKey?: string | null;
  deliverDashboard?: boolean;
  deliverBell?: boolean;
  publishedAt?: string | null;
  expiresAt?: string | null;
}): Promise<Announcement> {
  const res = await api.post<Announcement>('/admin/announcements', input);
  return res.data;
}

export async function updateAnnouncement(
  id: string,
  patch: Partial<{
    title: string;
    body: string;
    imageKey: string | null;
    deliverDashboard: boolean;
    deliverBell: boolean;
    publishedAt: string | null;
    expiresAt: string | null;
  }>,
): Promise<Announcement> {
  const res = await api.patch<Announcement>(`/admin/announcements/${id}`, patch);
  return res.data;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await api.delete(`/admin/announcements/${id}`);
}

export async function uploadAnnouncementImage(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/announcements/image', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to an announcement banner (presigned).
 * Uses the publish-state-agnostic route so draft banners preview in the admin CMS
 * (the /me/ route only serves published rows). */
export function announcementImageUrl(id: string): string {
  return `${api.defaults.baseURL ?? ''}/announcements/${id}/image`;
}

// ── Testimonials CMS (per-product, admin-authored) ──────────────────────────
export type Testimonial = {
  id: string;
  productId: string;
  title: string;
  youtubeUrl: string | null;
  bodyHtml: string | null;
  authorName: string | null;
  sortOrder: number;
  active: boolean;
  createdAt: string;
};

export type TestimonialInput = {
  productId: string;
  title: string;
  youtubeUrl?: string | null;
  bodyHtml?: string | null;
  authorName?: string | null;
  sortOrder?: number;
  active?: boolean;
};

export async function listTestimonials(productId?: string): Promise<Testimonial[]> {
  const res = await api.get<Testimonial[]>('/admin/testimonials', {
    params: productId ? { productId } : undefined,
  });
  return res.data;
}

export async function createTestimonial(input: TestimonialInput): Promise<Testimonial> {
  const res = await api.post<Testimonial>('/admin/testimonials', input);
  return res.data;
}

export async function updateTestimonial(
  id: string,
  patch: Partial<TestimonialInput>,
): Promise<Testimonial> {
  const res = await api.patch<Testimonial>(`/admin/testimonials/${id}`, patch);
  return res.data;
}

export async function deleteTestimonial(id: string): Promise<void> {
  await api.delete(`/admin/testimonials/${id}`);
}

// ── Coupons ────────────────────────────────────────────────────────────────
export type Coupon = {
  id: string;
  code: string;
  discountType: 'percent' | 'amount';
  discountValue: string;
  oneTime: boolean;
  maxUses: number | null;
  usedCount: number;
  perUserLimit: number | null;
  validFrom: string | null;
  validUntil: string | null;
  active: boolean;
  productIds: string[];
  /** Where the code is good: the online store, the till, or both. */
  channel: CouponChannel;
  /** A till button; `name` is what the button and the receipt call it. */
  posPreset: boolean;
  name: string | null;
  /** Discounts every line of the order instead of `productIds`. */
  appliesToAll: boolean;
  requiresManagerPin: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type CouponChannel = 'ecom' | 'pos' | 'both';

export type CouponInput = {
  /** Left out for a till preset: the server generates its internal code. */
  code?: string;
  discountType: 'percent' | 'amount';
  discountValue: string;
  oneTime?: boolean;
  maxUses?: number | null;
  perUserLimit?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  active?: boolean;
  productIds: string[];
  channel?: CouponChannel;
  posPreset?: boolean;
  name?: string | null;
  appliesToAll?: boolean;
  requiresManagerPin?: boolean;
};

// ── Gift checks (cashier phase 4; internally "vouchers") ─────────────────

export type VoucherProgram = {
  id: string;
  name: string;
  description: string | null;
  discountType: 'percent' | 'amount';
  discountValue: string;
  appliesToAll: boolean;
  /** One item (the dearest) or every item it touches. */
  discountScope: 'one_unit' | 'every_unit';
  /** Units a gift check paid for count toward this program too. */
  rewardUnitsCount: boolean;
  earnKind: 'purchase_count' | 'manual' | 'birthday' | 'topup';
  earnCount: number | null;
  validDays: number | null;
  active: boolean;
  /** Products the gift check takes money off (empty when `appliesToAll`). */
  productIds: string[];
  /** Products whose units count toward earning one (empty = the eligible set). */
  earnProductIds: string[];
  issuedCount: number;
  redeemedCount: number;
  createdAt: string;
  updatedAt: string;
};

export type VoucherProgramInput = {
  name: string;
  description?: string | null;
  discountType: 'percent' | 'amount';
  discountValue: string;
  appliesToAll: boolean;
  discountScope?: 'one_unit' | 'every_unit';
  rewardUnitsCount?: boolean;
  productIds: string[];
  earnKind: 'purchase_count' | 'manual' | 'birthday' | 'topup';
  earnCount?: number | null;
  earnProductIds?: string[];
  validDays?: number | null;
  active?: boolean;
};

export type IssuedVoucher = {
  id: string;
  code: string;
  status: 'issued' | 'redeemed' | 'expired' | 'revoked';
  programId: string;
  programName: string;
  discountType: 'percent' | 'amount';
  discountValue: string;
  issuedReason: string;
  issuedAt: string;
  expiresAt: string | null;
  redeemedAt: string | null;
  userId: string;
  memberName: string | null;
  memberEmail: string;
  redeemedOrderId: string | null;
  redeemedOrderCode: string | null;
};

export async function listVoucherPrograms(): Promise<VoucherProgram[]> {
  const res = await api.get<VoucherProgram[]>('/admin/vouchers/programs');
  return res.data;
}

export async function createVoucherProgram(input: VoucherProgramInput): Promise<VoucherProgram> {
  const res = await api.post<VoucherProgram>('/admin/vouchers/programs', input);
  return res.data;
}

export async function updateVoucherProgram(
  id: string,
  input: VoucherProgramInput,
): Promise<VoucherProgram> {
  const res = await api.patch<VoucherProgram>(`/admin/vouchers/programs/${id}`, input);
  return res.data;
}

export async function deleteVoucherProgram(id: string): Promise<void> {
  await api.delete(`/admin/vouchers/programs/${id}`);
}

export async function listIssuedVouchers(params: {
  status?: string;
  programId?: string;
  q?: string;
}): Promise<IssuedVoucher[]> {
  const res = await api.get<IssuedVoucher[]>('/admin/vouchers', { params });
  return res.data;
}

export async function issueVoucher(input: {
  programId: string;
  userId: string;
}): Promise<{ id: string; code: string }> {
  const res = await api.post<{ id: string; code: string }>('/admin/vouchers/issue', input);
  return res.data;
}

export async function revokeVoucher(id: string, reason: string): Promise<void> {
  await api.post(`/admin/vouchers/${id}/revoke`, { reason });
}

export async function listCoupons(): Promise<Coupon[]> {
  const res = await api.get<Coupon[]>('/admin/coupons');
  return res.data;
}

export async function createCoupon(input: CouponInput): Promise<Coupon> {
  const res = await api.post<Coupon>('/admin/coupons', input);
  return res.data;
}

export async function updateCoupon(
  id: string,
  patch: Partial<Omit<CouponInput, 'code'>>,
): Promise<Coupon> {
  const res = await api.patch<Coupon>(`/admin/coupons/${id}`, patch);
  return res.data;
}

export async function deleteCoupon(id: string): Promise<void> {
  await api.delete(`/admin/coupons/${id}`);
}

// ── Replicated-link landing page CMS ───────────────────────────────────────
export type ReplicatedBlockType = 'heading' | 'text' | 'image' | 'video' | 'button';
export type ReplicatedBlockAlign = 'left' | 'center' | 'right';

export type ReplicatedBlock = {
  id: string;
  type: ReplicatedBlockType;
  align: ReplicatedBlockAlign;
  content: Record<string, unknown>;
  sortOrder: number;
};

export type ReplicatedBlockInput = {
  type: ReplicatedBlockType;
  align: ReplicatedBlockAlign;
  content: Record<string, unknown>;
};

export type ReplicatedPage = {
  id: string;
  slug: string;
  title: string;
  enabled: boolean;
  /** Wording of the auto-appended signup CTA (label only, destination server-derived). */
  ctaLabel: string;
  /** Rolling per-visitor countdown banner config. */
  timerEnabled: boolean;
  timerHours: number;
  timerLabel: string;
  isDefault: boolean;
  sortOrder: number;
  /** Activation-kit product ids this campaign narrows to ([] = untagged). */
  kitProductIds: string[];
  /** Whether the automatic signup button is appended below the page content. */
  ctaAuto: boolean;
  /** The product the automatic CTA sells (`/buy/<id>` → register, then
   *  checkout). Null = it goes to the registration form. */
  ctaProductId: string | null;
  /** Link-share preview (Open Graph). Null = derive from the page. */
  seoTitle: string | null;
  seoDescription: string | null;
  ogImageKey: string | null;
  /** Virtual products whose card opens this page. Deleting the page NULLs every
   *  one of those pointers (ON DELETE SET NULL), and nothing afterwards shows
   *  that it happened — so the delete confirmation names the count. */
  linkedProductCount: number;
};

/** All pages (incl. disabled) + the tenant master enable flag. */
export async function listReplicatedPages(): Promise<{
  enabled: boolean;
  pages: ReplicatedPage[];
}> {
  const res = await api.get<{ enabled: boolean; pages: ReplicatedPage[] }>(
    '/admin/replicated-pages',
  );
  return res.data;
}

export async function createReplicatedPage(input: {
  slug: string;
  title: string;
}): Promise<{ page: ReplicatedPage }> {
  const res = await api.post<{ page: ReplicatedPage }>('/admin/replicated-pages', input);
  return res.data;
}

export async function updateReplicatedPage(
  id: string,
  patch: Partial<
    Pick<
      ReplicatedPage,
      | 'title'
      | 'slug'
      | 'enabled'
      | 'ctaLabel'
      | 'timerEnabled'
      | 'timerHours'
      | 'timerLabel'
      | 'isDefault'
      | 'sortOrder'
      | 'kitProductIds'
      | 'ctaAuto'
      | 'ctaProductId'
      | 'seoTitle'
      | 'seoDescription'
      | 'ogImageKey'
    >
  >,
): Promise<{ page: ReplicatedPage }> {
  const res = await api.patch<{ page: ReplicatedPage }>(`/admin/replicated-pages/${id}`, patch);
  return res.data;
}

export async function deleteReplicatedPage(id: string): Promise<void> {
  await api.delete(`/admin/replicated-pages/${id}`);
}

/** One page's content for the editor: the builder `layout` + the legacy blocks. */
export async function getReplicatedPageBlocks(
  id: string,
): Promise<{ page: ReplicatedPage; blocks: ReplicatedBlock[]; layout: RpLayout }> {
  const res = await api.get<{ page: ReplicatedPage; blocks: ReplicatedBlock[]; layout: RpLayout }>(
    `/admin/replicated-pages/${id}`,
  );
  return res.data;
}

/**
 * Save a page's layout (the visual builder's write path).
 *
 * The server re-validates the whole layout and sanitizes every HTML-bearing
 * field, so a 400 here means the draft is genuinely unsaveable — surface it
 * rather than retrying.
 */
export async function saveReplicatedPageLayout(
  id: string,
  layout: RpLayout,
): Promise<{ page: ReplicatedPage; layout: RpLayout }> {
  const res = await api.put<{ page: ReplicatedPage; layout: RpLayout }>(
    `/admin/replicated-pages/${id}/layout`,
    { layout },
  );
  return res.data;
}

/** Public read endpoint that 302-redirects to an image by R2 key (presigned). */
export function replicatedImageUrl(key: string): string {
  return `${api.defaults.baseURL ?? ''}${REPLICATED_IMAGE_PATH}?key=${encodeURIComponent(key)}`;
}

/** Replace one page's block list. */
export async function saveReplicatedPageBlocks(
  id: string,
  blocks: ReplicatedBlockInput[],
): Promise<{ blocks: ReplicatedBlock[] }> {
  const res = await api.put<{ blocks: ReplicatedBlock[] }>(`/admin/replicated-pages/${id}`, {
    blocks,
  });
  return res.data;
}

export async function uploadReplicatedImage(file: File): Promise<{ key: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ key: string }>('/admin/replicated-page/image', form);
  return res.data;
}

/** Public read endpoint that 302-redirects to a saved block image (presigned). */
export function replicatedBlockImageUrl(blockId: string): string {
  return `${api.defaults.baseURL ?? ''}/replicated-page/blocks/${blockId}/image`;
}

/**
 * The API origin, for callers that build absolute URLs into content (e.g. the
 * replicated-page rich-text editor absolutizing stored-relative image srcs).
 */
export const API_BASE_URL = BASE_URL;

// ── Special Links (subscription-gated member content: Zoom/Vimeo live) ──
export type SpecialLink = {
  id: string;
  title: string;
  type: 'zoom' | 'vimeo' | 'other';
  target: string;
  zoomMeetingId: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type SpecialLinkInput = {
  title: string;
  type: 'zoom' | 'vimeo' | 'other';
  target: string;
  zoomMeetingId?: string | null;
  active?: boolean;
  sortOrder?: number;
};

export async function listSpecialLinks(): Promise<SpecialLink[]> {
  const res = await api.get<SpecialLink[]>('/admin/special-links');
  return res.data;
}

export async function createSpecialLink(input: SpecialLinkInput): Promise<SpecialLink> {
  const res = await api.post<SpecialLink>('/admin/special-links', input);
  return res.data;
}

export async function updateSpecialLink(
  id: string,
  input: Partial<SpecialLinkInput>,
): Promise<SpecialLink> {
  const res = await api.patch<SpecialLink>(`/admin/special-links/${id}`, input);
  return res.data;
}

export async function deleteSpecialLink(id: string): Promise<void> {
  await api.delete(`/admin/special-links/${id}`);
}

// ── Branch access (which branches an admin user may SEE) ───────────────────
// Distinct from `user_roles.scope_branch_id`, the POS pin.

export async function getUserBranches(userId: string): Promise<string[]> {
  const res = await api.get<{ branchIds: string[] }>(`/users/${userId}/branches`);
  return res.data.branchIds;
}

export async function setUserBranches(userId: string, branchIds: string[]): Promise<string[]> {
  const res = await api.put<{ branchIds: string[] }>(`/users/${userId}/branches`, { branchIds });
  return res.data.branchIds;
}

// ── Accounting v1: supplier bills (AP), expenses, consignment reads ─────────

/**
 * `draft` is AP-only (Phase 3): a bill is born a draft (editable/deletable),
 * then POSTED to `open`, which computes the match. Consignment invoices are
 * still born `open`.
 */
export type InvoiceStatus = 'draft' | 'open' | 'partial' | 'paid' | 'void';
export type AcctDocStatus = 'posted' | 'void';

/** How the match was computed at post time. null = legacy (pre-Phase 3) or draft. */
export type SupplierInvoiceMatchBasis = 'two_way' | 'three_way';

export type SupplierInvoiceRow = {
  id: string;
  supplierId: string;
  supplierName: string;
  invoiceNo: string;
  poId: string | null;
  poNumber: string | null;
  invoiceDate: string;
  dueDate: string;
  subtotal: string;
  vatAmount: string;
  total: string;
  amountPaid: string;
  /** Σ Return-to-Supplier credits applied; outstanding = total − paid − credit. */
  creditApplied: string;
  status: InvoiceStatus;
  matchStatus: 'matched' | 'variance' | 'no_po' | null;
  matchVariance: string | null;
  matchBasis: SupplierInvoiceMatchBasis | null;
  postedAt: string | null;
  memo: string | null;
  createdAt: string;
};

/**
 * Bill line as SENT. Item lines bill a product/variant at a unit cost;
 * category lines bill an expense category for a flat amount (QBO-style).
 * Money is a decimal string (max 4dp, no thousands separators).
 */
export type SupplierInvoiceLineInput =
  | { productId: string; variantId?: string | null; qty: number; unitCost: string }
  | { categoryId: string; description?: string | null; amount: string };

/** Bill line as READ (names resolved server-side; `lineTotal` is authoritative). */
export type SupplierInvoiceLineRow = {
  id: string;
  sortOrder: number;
  kind: 'item' | 'category';
  productId: string | null;
  productName: string | null;
  variantId: string | null;
  variantName: string | null;
  qty: number | null;
  unitCost: string | null;
  categoryId: string | null;
  categoryName: string | null;
  description: string | null;
  lineTotal: string;
};

/** A PO receipt (purchase-order fulfillment) linked to a bill for the 3-way match. */
export type SupplierInvoiceReceiptRow = {
  fulfillmentId: string;
  poId: string;
  receivedAt: string;
  branchId: string;
  branchName: string;
  refNumber: string | null;
  note: string | null;
  /** Σ qty × PO unit_cost, server-computed. */
  value: string;
  lines: Array<{
    poLineId: string;
    productId: string;
    productName: string;
    variantId: string | null;
    variantName: string | null;
    qty: number;
    unitCost: string;
    lineValue: string;
  }>;
};

/**
 * A PO receipt as offered by the bill editor's picker. `claimedBy` is set when
 * a NON-void bill already links this receipt — a bill's own receipts come back
 * pointing at itself, which the editor treats as "mine".
 */
export type BillableReceipt = SupplierInvoiceReceiptRow & {
  claimedBy: { invoiceId: string; invoiceNo: string; status: InvoiceStatus } | null;
};

export type SupplierInvoiceDetail = SupplierInvoiceRow & {
  postedByName: string | null;
  lines: SupplierInvoiceLineRow[];
  receipts: SupplierInvoiceReceiptRow[];
};

/** Create/update body. Totals are computed server-side from `lines`. */
export type SupplierInvoiceBody = {
  invoiceNo: string;
  poId?: string | null;
  invoiceDate: string;
  dueDate?: string | null;
  vatAmount?: string | null;
  memo?: string | null;
  lines: SupplierInvoiceLineInput[];
  receiptFulfillmentIds?: string[];
};

export async function listSupplierInvoices(
  filter: { status?: InvoiceStatus; supplierId?: string; from?: string; to?: string } = {},
): Promise<SupplierInvoiceRow[]> {
  return (
    await api.get<{ rows: SupplierInvoiceRow[] }>('/admin/supplier-invoices', { params: filter })
  ).data.rows;
}

export async function getSupplierInvoice(id: string): Promise<SupplierInvoiceDetail> {
  return (await api.get<SupplierInvoiceDetail>(`/admin/supplier-invoices/${id}`)).data;
}

/** Every fulfillment of a PO, newest first, annotated with who already bills it. */
export async function listPoBillableReceipts(poId: string): Promise<BillableReceipt[]> {
  return (
    await api.get<{ rows: BillableReceipt[] }>(`/admin/supplier-invoices/po/${poId}/receipts`)
  ).data.rows;
}

/** Creates a DRAFT bill. */
export async function createSupplierInvoice(
  input: SupplierInvoiceBody & { supplierId: string },
): Promise<SupplierInvoiceRow> {
  return (await api.post('/admin/supplier-invoices', input)).data;
}

/** Draft only. Supplier is immutable, so it isn't part of the body. */
export async function updateSupplierInvoice(
  id: string,
  input: SupplierInvoiceBody,
): Promise<SupplierInvoiceRow> {
  return (await api.put(`/admin/supplier-invoices/${id}`, input)).data;
}

/** Draft only. */
export async function deleteSupplierInvoice(id: string): Promise<void> {
  await api.delete(`/admin/supplier-invoices/${id}`);
}

/** Draft → open; computes the match. */
export async function postSupplierInvoice(id: string): Promise<SupplierInvoiceRow> {
  return (await api.post(`/admin/supplier-invoices/${id}/post`)).data;
}

export async function voidSupplierInvoice(id: string, reason: string): Promise<void> {
  await api.post(`/admin/supplier-invoices/${id}/void`, { reason });
}

// ── Return to Supplier (RTS-) ───────────────────────────────────────────────

export type SupplierReturnCreditKind = 'bill_credit' | 'grni_relief' | 'supplier_credit';

export type SupplierReturnRow = {
  id: string;
  docNo: string;
  supplierId: string;
  supplierName: string;
  branchId: string;
  branchName: string;
  poId: string | null;
  poNumber: string | null;
  supplierInvoiceId: string | null;
  invoiceNo: string | null;
  returnedAt: string;
  creditKind: SupplierReturnCreditKind;
  subtotal: string;
  vatAmount: string;
  total: string;
  amountApplied: string;
  amountRefunded: string;
  /** total − applied − refunded (server-computed). */
  openCredit: string;
  reason: string;
  supplierRef: string | null;
  memo: string | null;
  status: 'posted' | 'void';
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
};

export type SupplierReturnLineRow = {
  id: string;
  poLineId: string | null;
  productId: string;
  productName: string;
  sku: string;
  variantId: string | null;
  variantName: string | null;
  qty: number;
  unitCost: string;
  lineTotal: string;
  costSource: 'po_line' | 'product' | 'manual';
};

export type SupplierReturnDetail = SupplierReturnRow & {
  createdByName: string | null;
  lines: SupplierReturnLineRow[];
};

/** What the "New return" dialog needs for one PO. */
export type ReturnableForPo = {
  po: {
    id: string;
    poNumber: string;
    supplierId: string;
    supplierName: string;
    status: PurchaseOrderStatus;
  };
  lines: Array<{
    poLineId: string;
    productId: string;
    productName: string;
    sku: string;
    variantId: string | null;
    variantName: string | null;
    qtyFulfilled: number;
    qtyReturned: number;
    returnable: number;
    unitCost: string;
  }>;
  /** Posted bills touching the PO — one MUST be linked when any exist. */
  bills: Array<{ id: string; invoiceNo: string; status: InvoiceStatus; outstanding: string }>;
  billLinkRequired: boolean;
  branches: Array<{ id: string; name: string }>;
};

export type SupplierReturnBody = {
  supplierId: string;
  branchId: string;
  poId?: string | null;
  supplierInvoiceId?: string | null;
  returnedAt?: string;
  reason: string;
  supplierRef?: string | null;
  memo?: string | null;
  lines: Array<{
    poLineId?: string | null;
    productId: string;
    variantId?: string | null;
    qty: number;
    unitCost?: string | null;
  }>;
  clientRequestId?: string;
};

export async function listSupplierReturns(
  filter: { supplierId?: string; status?: 'posted' | 'void'; poId?: string } = {},
): Promise<SupplierReturnRow[]> {
  return (
    await api.get<{ rows: SupplierReturnRow[] }>('/admin/supplier-returns', { params: filter })
  ).data.rows;
}
export async function getSupplierReturn(id: string): Promise<SupplierReturnDetail> {
  return (await api.get<SupplierReturnDetail>(`/admin/supplier-returns/${id}`)).data;
}
export async function getReturnableForPo(poId: string): Promise<ReturnableForPo> {
  return (
    await api.get<ReturnableForPo>('/admin/supplier-returns/returnable', { params: { poId } })
  ).data;
}
export async function createSupplierReturn(
  input: SupplierReturnBody,
): Promise<SupplierReturnRow & { replayed: boolean }> {
  return (await api.post('/admin/supplier-returns', input)).data;
}
export async function voidSupplierReturn(id: string, reason: string): Promise<void> {
  await api.post(`/admin/supplier-returns/${id}/void`, { reason });
}

// ── Supplier refunds (SREF-) — cash returned against an RTS credit ──────────

/** Local mirror of the server row (apps/api services/supplier-returns.ts). */
export type SupplierRefundRow = {
  id: string;
  docNo: string;
  supplierReturnId: string;
  returnDocNo: string;
  supplierId: string;
  supplierName: string;
  amount: string;
  method: string;
  reference: string | null;
  paidAt: string;
  memo: string | null;
  status: AcctDocStatus;
  voidedAt: string | null;
  voidReason: string | null;
  fundsAccountId: string | null;
  fundsAccountName: string | null;
  createdAt: string;
};

export async function listSupplierRefunds(returnId: string): Promise<SupplierRefundRow[]> {
  return (
    await api.get<{ rows: SupplierRefundRow[] }>(`/admin/supplier-returns/${returnId}/refunds`)
  ).data.rows;
}

export async function createSupplierRefund(
  returnId: string,
  input: {
    amount: string;
    method: string;
    reference?: string | null;
    paidAt?: string | null;
    memo?: string | null;
    clientRequestId?: string | null;
    fundsAccountId?: string | null;
  },
): Promise<{ refund: SupplierRefundRow; replayed: boolean }> {
  return (await api.post(`/admin/supplier-returns/${returnId}/refunds`, input)).data;
}

export async function voidSupplierRefund(id: string, reason: string): Promise<void> {
  await api.post(`/admin/supplier-refunds/${id}/void`, { reason });
}

export type SupplierPaymentRow = {
  id: string;
  docNo: string;
  supplierId: string;
  invoiceId: string;
  amount: string;
  method: string;
  reference: string | null;
  paidAt: string;
  memo: string | null;
  status: AcctDocStatus;
  fundsAccountId: string | null;
  fundsAccountName: string | null;
};

export async function listSupplierInvoicePayments(
  invoiceId: string,
): Promise<SupplierPaymentRow[]> {
  return (
    await api.get<{ rows: SupplierPaymentRow[] }>(`/admin/supplier-invoices/${invoiceId}/payments`)
  ).data.rows;
}

export async function createSupplierPayment(
  invoiceId: string,
  input: {
    amount: string;
    method: string;
    reference?: string | null;
    paidAt?: string | null;
    memo?: string | null;
    clientRequestId?: string | null;
    fundsAccountId?: string | null;
  },
): Promise<{ payment: SupplierPaymentRow }> {
  return (await api.post(`/admin/supplier-invoices/${invoiceId}/payments`, input)).data;
}

export async function voidSupplierPayment(id: string, reason: string): Promise<void> {
  await api.post(`/admin/supplier-payments/${id}/void`, { reason });
}

/** A supplier payment joined to its supplier + bill (the Expenses page list). */
export type SupplierPaymentListRow = SupplierPaymentRow & {
  supplierName: string;
  invoiceNo: string;
  voidReason: string | null;
};

export async function listSupplierPayments(
  filter: {
    supplierId?: string;
    status?: AcctDocStatus;
    from?: string;
    to?: string;
    limit?: number;
  } = {},
): Promise<SupplierPaymentListRow[]> {
  return (
    await api.get<{ rows: SupplierPaymentListRow[] }>('/admin/supplier-payments', {
      params: filter,
    })
  ).data.rows;
}

/** Per-PO received-vs-billed picture for the bill dialog's PO picker. */
export type PoBillingSummaryRow = {
  poId: string;
  poNumber: string;
  status: PurchaseOrderStatus;
  orderedTotal: string;
  receivedValue: string;
  billedTotal: string;
  unbilledValue: string;
  fullyBilled: boolean;
};

export async function listPoBillingSummary(supplierId: string): Promise<PoBillingSummaryRow[]> {
  return (
    await api.get<{ rows: PoBillingSummaryRow[] }>('/admin/supplier-invoices/po-summary', {
      params: { supplierId },
    })
  ).data.rows;
}

export type ExpenseCategory = {
  id: string;
  name: string;
  active: boolean;
  sortOrder: number;
  isSystem: boolean;
  /** Soft FK onto `gl_accounts.code`. NULL = expenses book to 6900. */
  glAccountCode: string | null;
};

export async function listExpenseCategories(activeOnly?: boolean): Promise<ExpenseCategory[]> {
  return (
    await api.get<{ rows: ExpenseCategory[] }>('/admin/expense-categories', {
      params: activeOnly ? { activeOnly: true } : {},
    })
  ).data.rows;
}

export async function createExpenseCategory(input: {
  name: string;
  sortOrder?: number;
  glAccountCode?: string | null;
}): Promise<ExpenseCategory> {
  return (await api.post('/admin/expense-categories', input)).data;
}

export async function updateExpenseCategory(
  id: string,
  patch: {
    name?: string;
    active?: boolean;
    sortOrder?: number;
    glAccountCode?: string | null;
  },
): Promise<ExpenseCategory> {
  return (await api.patch(`/admin/expense-categories/${id}`, patch)).data;
}

export type ExpenseRow = {
  id: string;
  docNo: string;
  expenseDate: string;
  payeeName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  categoryId: string;
  categoryName: string;
  amount: string;
  vatAmount: string;
  paymentSource: string;
  reference: string | null;
  memo: string | null;
  branchId: string | null;
  branchName: string | null;
  status: AcctDocStatus;
  voidReason: string | null;
  createdAt: string;
  fundsAccountId: string | null;
  fundsAccountName: string | null;
};

export async function listExpenses(
  filter: {
    from?: string;
    to?: string;
    categoryId?: string;
    supplierId?: string;
    paymentSource?: string;
    status?: AcctDocStatus;
    q?: string;
  } = {},
): Promise<ExpenseRow[]> {
  return (await api.get<{ rows: ExpenseRow[] }>('/admin/expenses', { params: filter })).data.rows;
}

export async function createExpense(input: {
  expenseDate: string;
  payeeName?: string | null;
  supplierId?: string | null;
  categoryId: string;
  amount: string;
  vatAmount?: string | null;
  paymentSource: string;
  reference?: string | null;
  memo?: string | null;
  branchId?: string | null;
  fundsAccountId?: string | null;
}): Promise<ExpenseRow> {
  return (await api.post('/admin/expenses', input)).data;
}

export async function voidExpense(id: string, reason: string): Promise<void> {
  await api.post(`/admin/expenses/${id}/void`, { reason });
}

export type ConsignmentCustomerRow = {
  customer_user_id: string;
  customer_name: string;
  email: string;
  units: number;
  open_billings: number;
  receivable: string;
  last_activity: string | null;
};

export async function listAdminConsignmentCustomers(q?: string): Promise<ConsignmentCustomerRow[]> {
  return (
    await api.get<{ rows: ConsignmentCustomerRow[] }>('/admin/consignment-customers', {
      params: q ? { q } : {},
    })
  ).data.rows;
}

export type SoaLine = {
  date: string;
  docType: string;
  docNo: string;
  ref: string | null;
  charges: string;
  payments: string;
  balance: string;
};
export type CustomerSoa = {
  customer: { id: string; name: string; email: string; phone: string | null };
  from: string | null;
  to: string | null;
  openingBalance: string;
  lines: SoaLine[];
  totalCharges: string;
  totalPayments: string;
  closingBalance: string;
};

export async function getAdminCustomerSoa(
  userId: string,
  params: { from?: string; to?: string } = {},
): Promise<CustomerSoa> {
  return (await api.get<CustomerSoa>(`/admin/consignment-customers/${userId}/soa`, { params }))
    .data;
}

// ── Track 2A: stockist consignment (read-only admin mounts) ────────────────
//
// Branch-keyed, unlike the customer module above: a stockist owes as a BRANCH.
// Everything here is read-only — documents are raised at the till, and the
// receivable itself is raised by the stockist's own POS sale.

export type StockistSummaryRow = {
  stockist_branch_id: string;
  name: string;
  code: string;
  units: number;
  open_billings: number;
  receivable: string;
  last_activity: string | null;
};

export async function listAdminStockistSummary(q?: string): Promise<StockistSummaryRow[]> {
  return (
    await api.get<{ rows: StockistSummaryRow[] }>('/admin/stockist-consignment-summary', {
      params: q ? { q } : {},
    })
  ).data.rows;
}

export type StockistSoa = {
  branch: { id: string; name: string; code: string };
  openingBalance: string;
  lines: SoaLine[];
  totalCharges: string;
  totalPayments: string;
  closingBalance: string;
};

export async function getAdminStockistSoa(
  stockistBranchId: string,
  params: { from?: string; to?: string } = {},
): Promise<StockistSoa> {
  return (
    await api.get<StockistSoa>(`/admin/stockist-consignment-branches/${stockistBranchId}/soa`, {
      params,
    })
  ).data;
}

export type StockistHoldingRow = {
  productId: string;
  variantId: string | null;
  productName: string;
  variantName?: string | null;
  qty: number;
};

export async function listAdminStockistHoldings(
  stockistBranchId: string,
): Promise<StockistHoldingRow[]> {
  return (
    await api.get<{ rows: StockistHoldingRow[] }>('/admin/stockist-consignment-holdings', {
      params: { stockistBranchId },
    })
  ).data.rows;
}

export type StockistInvoiceRow = {
  id: string;
  docNo: string;
  status: 'open' | 'partial' | 'paid' | 'void';
  invoiceDate: string;
  dueDate: string | null;
  total: string;
  amountPaid: string;
};

export async function listAdminStockistInvoices(
  stockistBranchId: string,
  status?: string,
): Promise<StockistInvoiceRow[]> {
  return (
    await api.get<{ rows: StockistInvoiceRow[] }>('/admin/stockist-consignment-invoices', {
      params: { stockistBranchId, ...(status ? { status } : {}) },
    })
  ).data.rows;
}

// ── Accounting Phase 2: order documents (Billing Statement / Credit Memo) ───
export type OrderDocumentRow = {
  id: string;
  orderId: string;
  docType: OrderDocType;
  docNo: string;
  branchId: string | null;
  issuedBy: string | null;
  payload: OrderDocumentPayload;
  issuedAt: string;
};
export type OrderDocumentsResponse = {
  documents: OrderDocumentRow[];
  eligibility: { billing: boolean; creditMemo: boolean };
};

export async function getOrderDocuments(orderId: string): Promise<OrderDocumentsResponse> {
  return (await api.get<OrderDocumentsResponse>(`/admin/orders/${orderId}/documents`)).data;
}

export async function issueOrderDocument(
  orderId: string,
  docType: OrderDocType,
): Promise<{ document: OrderDocumentRow; created: boolean }> {
  return (
    await api.post<{ document: OrderDocumentRow; created: boolean }>(
      `/admin/orders/${orderId}/documents`,
      { docType },
    )
  ).data;
}

// ── Cash/Bank register (funds subledger) ────────────────────────────────────

export type FundsAccountKindValue = 'cash' | 'bank' | 'cheque' | 'ewallet';

export type FundsAccount = {
  id: string;
  code: string | null;
  name: string;
  kind: FundsAccountKindValue;
  bankName: string | null;
  accountNumber: string | null;
  branchId: string | null;
  branchName: string | null;
  currentBalance: string;
  openingBalance: string;
  openingBalanceDate: string;
  active: boolean;
  sortOrder: number;
  memo: string | null;
};

export type FundsAccountsResponse = {
  /** Whether a money document must name an account (`funds_account_required`). */
  required: boolean;
  rows: FundsAccount[];
  totalsByKind: Record<string, string>;
};

export type FundsDocumentRow = {
  id: string;
  docNo: string;
  kind: 'deposit' | 'withdrawal' | 'transfer';
  amount: string;
  fromAccountId: string | null;
  toAccountId: string | null;
  occurredAt: string;
  reference: string | null;
  memo: string | null;
  status: AcctDocStatus;
  voidReason: string | null;
  createdAt: string;
  createdByName: string | null;
};

export async function listFundsAccounts(activeOnly?: boolean): Promise<FundsAccountsResponse> {
  const res = await api.get<FundsAccountsResponse>('/admin/funds-accounts', {
    params: activeOnly ? { activeOnly: true } : {},
  });
  return res.data;
}

export async function createFundsAccount(input: {
  name: string;
  code?: string | null;
  kind: FundsAccountKindValue;
  bankName?: string | null;
  accountNumber?: string | null;
  branchId?: string | null;
  openingBalance?: string | null;
  openingBalanceDate?: string | null;
  sortOrder?: number;
  memo?: string | null;
}): Promise<FundsAccount> {
  const res = await api.post<FundsAccount>('/admin/funds-accounts', input);
  return res.data;
}

export async function updateFundsAccount(
  id: string,
  patch: Partial<{
    name: string;
    code: string | null;
    kind: FundsAccountKindValue;
    bankName: string | null;
    accountNumber: string | null;
    branchId: string | null;
    active: boolean;
    sortOrder: number;
    memo: string | null;
  }>,
): Promise<FundsAccount> {
  const res = await api.patch<FundsAccount>(`/admin/funds-accounts/${id}`, patch);
  return res.data;
}

/** The account register — the same ReportResult the CSV download serves. */
export async function fetchFundsRegister(
  id: string,
  params: { from?: string; to?: string },
): Promise<ReportResult> {
  const res = await api.get<ReportResult>(`/admin/funds-accounts/${id}/register`, { params });
  return res.data;
}

/** Transport only — see `downloadFundsRegister()` in lib/report-export.ts. */
export async function fetchFundsRegisterCsvBlob(
  id: string,
  params: { from?: string; to?: string },
): Promise<Blob> {
  const res = await api.get(`/admin/funds-accounts/${id}/register`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });
  return res.data as Blob;
}

export async function listFundsDocuments(filter: {
  accountId?: string;
  status?: AcctDocStatus;
  from?: string;
  to?: string;
  limit?: number;
}): Promise<FundsDocumentRow[]> {
  const res = await api.get<FundsDocumentRow[]>('/admin/funds-documents', { params: filter });
  return res.data;
}

export async function createFundsDocument(input: {
  kind: 'deposit' | 'withdrawal' | 'transfer';
  amount: string;
  fromAccountId?: string | null;
  toAccountId?: string | null;
  occurredAt?: string | null;
  reference?: string | null;
  memo?: string | null;
  branchId?: string | null;
  /** Phase 4: GL account code for the deposit/withdrawal counter-leg (null → 6900). */
  counterGlAccountCode?: string | null;
  clientRequestId?: string | null;
}): Promise<{ document: FundsDocumentRow; replayed: boolean }> {
  const res = await api.post<{ document: FundsDocumentRow; replayed: boolean }>(
    '/admin/funds-documents',
    input,
  );
  return res.data;
}

export async function voidFundsDocument(id: string, reason: string): Promise<FundsDocumentRow> {
  const res = await api.post<FundsDocumentRow>(`/admin/funds-documents/${id}/void`, { reason });
  return res.data;
}

// Auto-posting rules: (source, code, branch) → funds account. NULL code / NULL
// branch = wildcard. The key is immutable server-side — a key change is a
// delete + re-add; only the target account, `active` and `memo` patch.

export type FundsMappingRow = {
  id: string;
  source: string;
  code: string | null;
  branchId: string | null;
  branchName: string | null;
  branchCode: string | null;
  fundsAccountId: string;
  accountName: string;
  accountKind: FundsAccountKindValue;
  accountBranchId: string | null;
  accountActive: boolean;
  active: boolean;
  memo: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FundsMappingSource = {
  source: string;
  label: string;
  direction: 'in' | 'out';
  /** The source's documents carry no branch — only a company-wide account can serve it. */
  branchless: boolean;
};

export type FundsMappingsResponse = {
  rows: FundsMappingRow[];
  sources: FundsMappingSource[];
  /** Registry codes each source can carry (free text is still allowed). */
  codeSuggestions: Record<string, Array<{ code: string; label: string }>>;
};

export async function listFundsMappings(): Promise<FundsMappingsResponse> {
  const res = await api.get<FundsMappingsResponse>('/admin/funds-mappings');
  return res.data;
}

export async function createFundsMapping(input: {
  source: string;
  code?: string | null;
  branchId?: string | null;
  fundsAccountId: string;
  memo?: string | null;
}): Promise<FundsMappingRow> {
  const res = await api.post<FundsMappingRow>('/admin/funds-mappings', input);
  return res.data;
}

export async function updateFundsMapping(
  id: string,
  patch: Partial<{ fundsAccountId: string; active: boolean; memo: string | null }>,
): Promise<FundsMappingRow> {
  const res = await api.patch<FundsMappingRow>(`/admin/funds-mappings/${id}`, patch);
  return res.data;
}

export async function deleteFundsMapping(id: string): Promise<void> {
  await api.delete(`/admin/funds-mappings/${id}`);
}

// ── Lean GL (Phase 4): Chart of Accounts + General Journal ──────────────────

export type GlAccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export type GlAccount = {
  id: string;
  code: string;
  name: string;
  type: GlAccountType;
  isSystem: boolean;
  active: boolean;
  sortOrder: number;
};

export async function listGlAccounts(): Promise<{ rows: GlAccount[] }> {
  const res = await api.get<{ rows: GlAccount[] }>('/admin/accounting/accounts');
  return res.data;
}

export async function createGlAccount(input: {
  code: string;
  name: string;
  type: GlAccountType;
  sortOrder?: number;
}): Promise<GlAccount> {
  const res = await api.post<{ account: GlAccount }>('/admin/accounting/accounts', input);
  return res.data.account;
}

export async function patchGlAccount(
  id: string,
  patch: Partial<{ name: string; active: boolean; sortOrder: number }>,
): Promise<GlAccount> {
  const res = await api.patch<{ account: GlAccount }>(`/admin/accounting/accounts/${id}`, patch);
  return res.data.account;
}

export type GlJournalLine = {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  branchId: string | null;
  direction: 'D' | 'C';
  amount: string;
  memo: string | null;
  metadata: Record<string, unknown>;
};

export type GlJournalEntry = {
  id: string;
  entryNo: string;
  postingDate: string;
  memo: string | null;
  sourceType: string;
  sourceRef: string;
  postedByUserId: string | null;
  reversesEntryId: string | null;
  reversedByEntryId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  lines: GlJournalLine[];
};

export type JournalListParams = {
  from?: string;
  to?: string;
  sourceType?: string;
  q?: string;
  unmappedOnly?: boolean;
  limit?: number;
  offset?: number;
};

export async function fetchJournal(
  params: JournalListParams,
): Promise<{ rows: GlJournalEntry[]; total: number }> {
  const res = await api.get<{ rows: GlJournalEntry[]; total: number }>(
    '/admin/accounting/journal',
    { params },
  );
  return res.data;
}

export async function getGlJournalEntry(id: string): Promise<GlJournalEntry> {
  const res = await api.get<{ entry: GlJournalEntry }>(`/admin/accounting/journal/${id}`);
  return res.data.entry;
}

export type ManualJournalLineInput = {
  accountCode: string;
  direction: 'D' | 'C';
  amount: string;
  branchId?: string | null;
  memo?: string | null;
};

export async function postManualJournalEntry(input: {
  postingDate: string;
  memo?: string | null;
  lines: ManualJournalLineInput[];
  clientRequestId?: string;
}): Promise<{ entry?: GlJournalEntry; replayed?: boolean }> {
  const res = await api.post<{ entry?: GlJournalEntry; replayed?: boolean }>(
    '/admin/accounting/journal',
    input,
  );
  return res.data;
}

export async function reverseGlJournalEntry(
  id: string,
  memo?: string | null,
): Promise<GlJournalEntry> {
  const res = await api.post<{ entry: GlJournalEntry }>(`/admin/accounting/journal/${id}/reverse`, {
    memo: memo ?? null,
  });
  return res.data.entry;
}

// ── Lean GL: overview aggregates ────────────────────────────────────────────

export type AccountingOverview = {
  cashTotal: string;
  entriesPosted: number;
  lastPostingDate: string | null;
  needsAttention: number;
  tbInBalance: boolean;
  mtd: { netSales: string; netIncome: string };
  /** What the POSTER uses — pinned on the first tick, never the flag after. */
  postingStart: { date: string | null; pinned: boolean };
  /** Documents/ledger rows the poster still cannot book. */
  posterFailures: number;
  /**
   * Documents the poster is WAITING on because nothing is costed yet. They post
   * themselves once a cost is set — a to-do list, not a failure list.
   */
  uncosted: { batches: number; consignmentBillings: number; completions: number };
};

export async function getAccountingOverview(): Promise<AccountingOverview> {
  const res = await api.get<AccountingOverview>('/admin/accounting/overview');
  return res.data;
}

export type AccountingHealth = {
  ok: boolean;
  checks: Array<{ id: string; label: string; ok: boolean; detail: string }>;
};

/** The R1 invariants, re-derived live. Scans the journal — fetch on demand. */
export async function getAccountingHealth(): Promise<AccountingHealth> {
  const res = await api.get<AccountingHealth>('/admin/accounting/health');
  return res.data;
}

// ── Notifications (LMS Phase 2) ─────────────────────────────────────────────
// `/me/notifications*` is `requireAuth` + `req.user.sub` only — an admin's
// access token reads that admin's own rows with no API change at all.
export type NotificationRow = {
  id: string;
  kind: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
};

export type NotificationsPage = {
  items: NotificationRow[];
  nextCursor: string | null;
};

export async function listNotifications(args: {
  limit?: number;
  cursor?: string;
}): Promise<NotificationsPage> {
  const res = await api.get<NotificationsPage>('/me/notifications', { params: args });
  return res.data;
}

export async function getUnreadCount(): Promise<{ count: number }> {
  const res = await api.get<{ count: number }>('/me/notifications/unread-count');
  return res.data;
}

export async function markNotificationRead(id: string): Promise<{ id: string; readAt: string }> {
  const res = await api.post<{ id: string; readAt: string }>(`/me/notifications/${id}/read`);
  return res.data;
}

export async function markAllNotificationsRead(): Promise<{ marked: number }> {
  const res = await api.post<{ marked: number }>('/me/notifications/read-all');
  return res.data;
}

// ── Account archival (LMS Phase 8) ──────────────────────────────────────────
// Deliberately NOT in creators-api.ts: a member asking to be erased has
// nothing to do with whether the tenant sells courses, and the API registers
// these routes outside the `lms_enabled` gate for exactly that reason.
export type ArchiveRequestRow = {
  id: string;
  userId: string;
  memberName: string | null;
  memberEmail: string;
  memberStatus: string;
  reason: string | null;
  status: ArchiveRequestStatus;
  requestedAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  /** What execution DID: null until executed. Only 'deactivated' can come back. */
  executedMode: 'deactivated' | 'erased' | null;
  reactivatedAt: string | null;
};

/**
 * What the admin must read before pressing Execute. Only `blockers` stops the
 * action; the rest is judgement the operator has to exercise, which is why the
 * numbers are surfaced individually instead of rolled into one verdict.
 */
export type ArchivePreflight = {
  userId: string;
  walletBalance: string;
  openPayoutRequests: number;
  activeSlots: number;
  activeDownline: number;
  openOrders: number;
  liveEnrollments: number;
  isCreator: boolean;
  isMerchant: boolean;
  blockers: string[];
};

export async function listArchiveRequests(
  params: { status?: ArchiveRequestStatus; page?: number; pageSize?: number } = {},
): Promise<{ items: ArchiveRequestRow[]; total: number; page: number; pageSize: number }> {
  const res = await api.get<{
    items: ArchiveRequestRow[];
    total: number;
    page: number;
    pageSize: number;
  }>('/admin/archive-requests', { params });
  return res.data;
}

/** Bring a deactivated account back. Refused for an erased one. */
export async function unarchiveUser(userId: string): Promise<void> {
  await api.post(`/admin/users/${userId}/unarchive`);
}

export async function getArchiveRequestPreflight(
  id: string,
): Promise<{ request: ArchiveRequestRow; preflight: ArchivePreflight }> {
  const res = await api.get<{ request: ArchiveRequestRow; preflight: ArchivePreflight }>(
    `/admin/archive-requests/${id}/preflight`,
  );
  return res.data;
}

export async function executeArchiveRequest(id: string): Promise<{ ok: true; userId: string }> {
  const res = await api.post<{ ok: true; userId: string }>(`/admin/archive-requests/${id}/execute`);
  return res.data;
}

export async function rejectArchiveRequest(id: string, note?: string): Promise<{ ok: true }> {
  const res = await api.post<{ ok: true }>(`/admin/archive-requests/${id}/reject`, { note });
  return res.data;
}

/**
 * Queue depths for the sidebar badges.
 *
 * An area the signed-in admin cannot read is ABSENT rather than zero — zero
 * would claim "nothing waiting", which is a different thing from "you can't
 * see this queue". Callers must treat `undefined` as "render no badge".
 */
export type NavCounts = {
  orders?: number;
  payments?: number;
  topups?: number;
  payouts?: number;
  messages?: number;
  kyc?: number;
  archiveRequests?: number;
};

export async function getNavCounts(): Promise<NavCounts> {
  const res = await api.get<{ counts: NavCounts }>('/admin/nav-counts');
  return res.data.counts;
}

// ── Member subscription (admin-granted) ───────────────────────────────────
// Which store a grant writes is decided SERVER-side from
// `lms_subscription_model`; the client sends a creator only because the
// creator model needs to know whose library is being opened.

export type AdminSubscriptionView = {
  model: 'tenant' | 'creator';
  tenant: {
    status: string;
    provider: string;
    currentPeriodEnd: string | null;
    slotId: string | null;
    currency: string;
    isActive: boolean;
  } | null;
  creator: Array<{
    creatorUserId: string;
    creatorName: string;
    expiresAt: string;
    active: boolean;
    periodLabel?: string;
    /** The member's standing consent to renew, from a wallet or a card (null = never set). */
    autoRenew?: {
      status: 'active' | 'past_due' | 'stopped' | 'canceled';
      consentedPrice: string;
      stoppedReason: string | null;
      failedAttempts: number;
      /** 'stripe' = a card subscription that bills itself. Absent on an older API build. */
      funding?: 'wallet' | 'stripe';
    } | null;
    /** The card subscription behind a 'stripe' consent (Stripe's status and next charge). */
    card?: {
      status: string | null;
      renewsAt: string | null;
      cancelScheduled: boolean;
    } | null;
  }>;
};

export async function getMemberSubscription(userId: string): Promise<AdminSubscriptionView> {
  const res = await api.get<AdminSubscriptionView>(`/admin/users/${userId}/subscription`);
  return res.data;
}

export async function grantMemberSubscription(
  userId: string,
  body: { creatorUserId?: string; endsAt?: string; addDays?: number },
): Promise<{ model: string; endsAt: string; previousEndsAt: string | null }> {
  const res = await api.post(`/admin/users/${userId}/subscription/grant`, body);
  return res.data as { model: string; endsAt: string; previousEndsAt: string | null };
}

/** Stop a member's wallet auto-renewal for one creator; paid time is untouched. */
export async function stopMemberAutoRenew(
  userId: string,
  creatorUserId: string,
): Promise<{ enabled: boolean; status: string | null }> {
  const res = await api.post(`/admin/users/${userId}/subscription/auto-renew/stop`, {
    creatorUserId,
  });
  return res.data as { enabled: boolean; status: string | null };
}

export async function revokeMemberSubscription(
  userId: string,
  body: { creatorUserId?: string } = {},
): Promise<{ model: string; revoked: number }> {
  const res = await api.post(`/admin/users/${userId}/subscription/revoke`, body);
  return res.data as { model: string; revoked: number };
}

// ── Coaching (Miracles Protocol) ────────────────────────────────────────────

export type CoachingPlanTier = 'pro' | 'elite';
export type CoachingPlanPeriod = 'monthly' | 'annual';
export type CoachingTier = 'free' | CoachingPlanTier;

export type CoachingPlanRow = {
  id: string;
  tier: CoachingPlanTier;
  period: CoachingPlanPeriod;
  label: string;
  productId: string;
  sku: string;
  productName: string;
  price: string;
  productActive: boolean;
  approvalStatus: string;
  active: boolean;
  /** AI Credits each paid window mints into the member's included bucket. */
  includedCredits: number;
};

export type CoachingPlanEdit = {
  tier: CoachingPlanTier;
  period: CoachingPlanPeriod;
  /** Bind an existing approved virtual product. */
  productId?: string;
  /** Set the price; required when minting a new hidden product. */
  price?: string;
  active: boolean;
  /** AI Credits each paid window mints; omit = unchanged. */
  includedCredits?: number;
};

export async function getCoachingPlans(): Promise<CoachingPlanRow[]> {
  const res = await api.get<{ plans: CoachingPlanRow[] }>('/admin/coaching/plans');
  return res.data.plans;
}

export async function putCoachingPlans(plans: CoachingPlanEdit[]): Promise<CoachingPlanRow[]> {
  const res = await api.put<{ plans: CoachingPlanRow[] }>('/admin/coaching/plans', { plans });
  return res.data.plans;
}

export type CoachingContentKind =
  | 'affirmation'
  | 'prompt'
  | 'reading_copy'
  | 'breathwork'
  | 'audio'
  | 'coach_prompt';

export type CoachingContentRow = {
  id: string;
  kind: CoachingContentKind;
  key: string | null;
  category: string | null;
  /** Breathwork, audio and coach-prompt rows only. */
  title: string | null;
  body: string;
  /** Breathwork: the timing. Audio: contentType, sizeBytes, frequencyLabel, durationSeconds. */
  meta: Record<string, unknown>;
  premiumLevel: CoachingTier;
  sortOrder: number;
  active: boolean;
  updatedAt: string;
  /** Audio rows in a list: a short-lived URL for the preview player. */
  audioUrl?: string | null;
  /** A linked track's own address (Vimeo, a direct file); null for an upload. */
  sourceUrl?: string | null;
};

export type BreathworkTiming = {
  inhale: number;
  holdIn: number;
  exhale: number;
  holdOut: number;
  cycles: number;
};

export async function listCoachingContent(
  kind?: CoachingContentKind,
): Promise<CoachingContentRow[]> {
  const res = await api.get<{ items: CoachingContentRow[] }>('/admin/coaching/content', {
    params: kind ? { kind } : {},
  });
  return res.data.items;
}

/** Typed per kind, exactly like the server's discriminated body. */
export type CoachingContentCreate =
  | {
      kind: 'affirmation';
      body: string;
      category: string | null;
      premiumLevel: CoachingTier;
      sortOrder?: number;
    }
  | { kind: 'prompt'; body: string; premiumLevel: CoachingTier; sortOrder?: number }
  | { kind: 'reading_copy'; readingKind: string; name: string; body: string }
  | ({
      kind: 'breathwork';
      /** A built-in pattern's key overrides it; absent adds a pattern of the tenant's own. */
      key?: string;
      title: string;
      body: string;
      outcome: string;
      premiumLevel: CoachingTier;
      active?: boolean;
    } & BreathworkTiming)
  | {
      kind: 'coach_prompt';
      /** A built-in prompt's key overrides it; absent adds a prompt of the tenant's own. */
      key?: string;
      title: string;
      body: string;
      category: string;
      premiumLevel: CoachingTier;
      active?: boolean;
      sortOrder?: number;
    };

export async function createCoachingContent(
  input: CoachingContentCreate,
): Promise<CoachingContentRow> {
  const res = await api.post<{ item: CoachingContentRow }>('/admin/coaching/content', input);
  return res.data.item;
}

export async function patchCoachingContent(
  id: string,
  patch: Partial<{
    body: string;
    category: string | null;
    premiumLevel: CoachingTier;
    sortOrder: number;
    active: boolean;
    title: string;
    breathwork: Partial<BreathworkTiming & { outcome: string }>;
    coachPrompt: Partial<{ category: string }>;
    audio: Partial<{
      category: string;
      frequencyLabel: string | null;
      durationSeconds: number | null;
    }>;
  }>,
): Promise<CoachingContentRow> {
  const res = await api.patch<{ item: CoachingContentRow }>(`/admin/coaching/content/${id}`, patch);
  return res.data.item;
}

/** Upload a frequency track: `file` plus title, body, category, frequencyLabel, durationSeconds, premiumLevel. */
export async function uploadCoachingAudio(form: FormData): Promise<CoachingContentRow> {
  const res = await api.post<{ item: CoachingContentRow }>('/admin/coaching/audio', form);
  return res.data.item;
}

export type LibraryItemRow = {
  id: string;
  kind: 'video' | 'audio' | 'image' | 'document';
  title: string;
  description: string;
  category: string | null;
  source: 'upload' | 'link';
  url: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  durationSeconds: number | null;
  premiumLevel: CoachingTier;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  previewUrl: string | null;
};

export async function listLibraryItems(): Promise<LibraryItemRow[]> {
  const res = await api.get<{ items: LibraryItemRow[] }>('/admin/coaching/library');
  return res.data.items;
}
export async function createLibraryLink(body: {
  kind: LibraryItemRow['kind'];
  title: string;
  description: string;
  category?: string;
  url: string;
  durationSeconds?: number;
  premiumLevel: CoachingTier;
}): Promise<LibraryItemRow> {
  const res = await api.post<{ item: LibraryItemRow }>('/admin/coaching/library', body);
  return res.data.item;
}
export async function uploadLibraryItem(form: FormData): Promise<LibraryItemRow> {
  const res = await api.post<{ item: LibraryItemRow }>('/admin/coaching/library/upload', form);
  return res.data.item;
}
export async function patchLibraryItem(
  id: string,
  patch: Partial<{
    title: string;
    description: string;
    category: string | null;
    durationSeconds: number | null;
    premiumLevel: CoachingTier;
    sortOrder: number;
    active: boolean;
  }>,
): Promise<LibraryItemRow> {
  const res = await api.patch<{ item: LibraryItemRow }>(`/admin/coaching/library/${id}`, patch);
  return res.data.item;
}
export async function deleteLibraryItem(id: string): Promise<void> {
  await api.delete(`/admin/coaching/library/${id}`);
}

/** A frequency track that lives at a link (Vimeo, another allowed host, a direct file). */
export async function createCoachingAudioLink(body: {
  title: string;
  body: string;
  category: string;
  url: string;
  frequencyLabel?: string;
  durationSeconds?: number;
  premiumLevel: CoachingTier;
}): Promise<CoachingContentRow> {
  const res = await api.post<{ item: CoachingContentRow }>('/admin/coaching/audio/link', body);
  return res.data.item;
}

export type BreathworkCatalogEntry = {
  key: string;
  title: string;
  description: string;
  outcome: string;
  pattern: Omit<BreathworkTiming, 'cycles'>;
  cycles: number;
  /** The tenant's override; inactive means the pattern is hidden from members. */
  override: CoachingContentRow | null;
};

export async function getCoachingBreathworkCatalog(): Promise<{
  builtIns: BreathworkCatalogEntry[];
  custom: CoachingContentRow[];
}> {
  const res = await api.get<{ builtIns: BreathworkCatalogEntry[]; custom: CoachingContentRow[] }>(
    '/admin/coaching/breathwork',
  );
  return res.data;
}

export type CoachPromptCatalogEntry = {
  key: string;
  category: string;
  title: string;
  body: string;
  /** The tenant's override; inactive means the prompt is hidden from members. */
  override: CoachingContentRow | null;
};

export async function getCoachingCoachPromptCatalog(): Promise<{
  builtIns: CoachPromptCatalogEntry[];
  custom: CoachingContentRow[];
}> {
  const res = await api.get<{ builtIns: CoachPromptCatalogEntry[]; custom: CoachingContentRow[] }>(
    '/admin/coaching/coach-prompts',
  );
  return res.data;
}

// ── guided programs ────────────────────────────────────────────────────────

export type ProgramAdminRow = {
  id: string;
  /** A built-in program's key (seeded from code); null for the tenant's own. */
  key: string | null;
  title: string;
  tagline: string;
  description: string;
  daysCount: number;
  premiumLevel: CoachingTier;
  active: boolean;
  sortOrder: number;
  activeEnrollments: number;
  completedEnrollments: number;
  updatedAt: string;
};

export type ProgramAdminDay = {
  dayNo: number;
  title: string;
  lesson: string;
  mediaKind: 'none' | 'audio' | 'video';
  /** An uploaded track's content id (audio) or an https url (video). */
  mediaRef: string | null;
  kalaPrompt: string;
  actionTitle: string;
  actionMinutes: number | null;
};

export async function listCoachingPrograms(): Promise<ProgramAdminRow[]> {
  const res = await api.get<{ programs: ProgramAdminRow[] }>('/admin/coaching/programs');
  return res.data.programs;
}

export async function getCoachingProgram(
  id: string,
): Promise<{ program: ProgramAdminRow; days: ProgramAdminDay[] }> {
  const res = await api.get<{ program: ProgramAdminRow; days: ProgramAdminDay[] }>(
    `/admin/coaching/programs/${id}`,
  );
  return res.data;
}

export async function createCoachingProgram(input: {
  title: string;
  tagline?: string;
  description?: string;
  premiumLevel: CoachingTier;
  active?: boolean;
  sortOrder?: number;
}): Promise<ProgramAdminRow> {
  const res = await api.post<{ program: ProgramAdminRow }>('/admin/coaching/programs', input);
  return res.data.program;
}

export async function patchCoachingProgram(
  id: string,
  patch: Partial<{
    title: string;
    tagline: string;
    description: string;
    premiumLevel: CoachingTier;
    active: boolean;
    sortOrder: number;
  }>,
): Promise<ProgramAdminRow> {
  const res = await api.patch<{ program: ProgramAdminRow }>(
    `/admin/coaching/programs/${id}`,
    patch,
  );
  return res.data.program;
}

export async function deleteCoachingProgram(id: string): Promise<void> {
  await api.delete(`/admin/coaching/programs/${id}`);
}

export async function putCoachingProgramDays(
  id: string,
  days: ProgramAdminDay[],
): Promise<ProgramAdminDay[]> {
  const res = await api.put<{ days: ProgramAdminDay[] }>(`/admin/coaching/programs/${id}/days`, {
    days,
  });
  return res.data.days;
}

export async function deleteCoachingContent(id: string): Promise<void> {
  await api.delete(`/admin/coaching/content/${id}`);
}

export type ReadingCopyEntry = {
  name: string;
  builtIn: string;
  override: { id: string; body: string; active: boolean } | null;
};

export async function getCoachingReadingCopy(): Promise<Record<string, ReadingCopyEntry[]>> {
  const res = await api.get<{ kinds: Record<string, ReadingCopyEntry[]> }>(
    '/admin/coaching/reading-copy',
  );
  return res.data.kinds;
}

export type MemberCoachingEntitlement = {
  tier: CoachingTier;
  endsAt: string | null;
  source: string | null;
  rows: Array<{
    id: string;
    tier: CoachingPlanTier;
    source: string;
    startsAt: string;
    endsAt: string;
    revokedAt: string | null;
    note: string | null;
    orderCode: string | null;
    grantedByName: string | null;
  }>;
};

export async function getMemberCoachingEntitlement(
  userId: string,
): Promise<MemberCoachingEntitlement> {
  const res = await api.get<MemberCoachingEntitlement>(
    `/admin/coaching/members/${userId}/entitlement`,
  );
  return res.data;
}

export async function grantMemberCoachingEntitlement(
  userId: string,
  body: { tier: CoachingPlanTier; addDays?: number; endsAt?: string; note?: string },
): Promise<{ tier: string; endsAt: string; previousEndsAt: string | null }> {
  const res = await api.post(`/admin/coaching/members/${userId}/entitlement/grant`, body);
  return res.data;
}

export async function revokeMemberCoachingEntitlement(
  userId: string,
  body: { note?: string } = {},
): Promise<{ revoked: number }> {
  const res = await api.post(`/admin/coaching/members/${userId}/entitlement/revoke`, body);
  return res.data;
}

export type CoachingUsageSummary = {
  astro: {
    provider: string;
    calls: number;
    okCalls: number;
    errorCalls: number;
    creditsUsed: number;
    monthlyCredits: number;
    usedPct: number | null;
    alertPct: number;
    alert: boolean;
  };
  claude: {
    calls: number;
    costUsd: string;
    kalaExchanges: number;
    kalaCostUsd: string;
    /** The coaching monthly budget in USD; null = unlimited. */
    budgetUsd: string | null;
  };
  engine: { callsToday: number; costTodayUsd: string };
  members: { onboarded: number; pro: number; elite: number };
};

export async function getCoachingUsage(): Promise<CoachingUsageSummary> {
  const res = await api.get<CoachingUsageSummary>('/admin/coaching/usage');
  return res.data;
}

// ── AI Credits (Coaching -> Credits) ─────────────────────────────────────────
export type CoachingCreditsSummary = {
  month: {
    minted: number;
    purchased: number;
    purchasedPesos: string;
    earned: number;
    adjustedNet: number;
    spent: number;
    spentByAction: Array<{ action: string; credits: number }>;
    refunded: number;
    expired: number;
  };
  position: {
    outstandingIncluded: number;
    outstandingPurchased: number;
    subscribers: number;
    subscribersAtZero: number;
    subscribersBelowTen: number;
    toppedUpThisMonth: number;
  };
  economics: {
    coachingSpendUsd: string;
    creditsSpent: number;
    costPerCreditUsd: string | null;
    costPerCreditPhp: string | null;
    packPricePerCreditPhp: string | null;
    includedBundle: number;
    usdPhpRate: number;
  };
  topConsumers: Array<{
    userId: string;
    name: string | null;
    email: string;
    credits: number;
    costUsd: string;
  }>;
  costs: Array<{ key: string; label: string; what: string; cost: number; defaultCost: number }>;
};

export async function getCoachingCredits(): Promise<CoachingCreditsSummary> {
  const res = await api.get<CoachingCreditsSummary>('/admin/coaching/credits');
  return res.data;
}

export type AiCreditPackRow = {
  productId: string;
  sku: string;
  name: string;
  credits: number;
  price: string;
  active: boolean;
  sold: number;
};

export async function listAiCreditPacks(): Promise<AiCreditPackRow[]> {
  const res = await api.get<{ packs: AiCreditPackRow[] }>('/admin/coaching/credit-packs');
  return res.data.packs;
}

export async function createAiCreditPack(input: {
  name: string;
  credits: number;
  price: string;
}): Promise<AiCreditPackRow> {
  const res = await api.post<{ pack: AiCreditPackRow }>('/admin/coaching/credit-packs', input);
  return res.data.pack;
}

export async function patchAiCreditPack(
  productId: string,
  patch: { name?: string; credits?: number; price?: string; active?: boolean },
): Promise<AiCreditPackRow> {
  const res = await api.patch<{ pack: AiCreditPackRow }>(
    `/admin/coaching/credit-packs/${productId}`,
    patch,
  );
  return res.data.pack;
}

export type MemberCredits = {
  purchased: number;
  included: number;
  includedUntil: string | null;
  lifetimeEarned: number;
  lifetimeSpent: number;
  entries: Array<{
    id: string;
    direction: 'C' | 'D';
    amount: number;
    kind: string;
    bucket: string;
    action: string | null;
    createdAt: string;
  }>;
};

export async function getMemberCredits(userId: string): Promise<MemberCredits> {
  const res = await api.get<MemberCredits>(`/admin/coaching/members/${userId}/credits`);
  return res.data;
}
